import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import "./styles/design-system-v2.css";
import "./styles/phase6-utility.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { GoogleCredentialButton } from './components/GoogleSignIn';
import axios from "axios";
import { getMemberTimeFormat, storeMemberTimeFormat, normaliseTimeFormat } from "./utils/memberTime";
import {
  markActive,
  lastActiveAt,
  clearLockState,
  requiresUnlock,
  isBiometricLockEnabled,
  setBiometricLockEnabled,
  runLocalUnlock
} from "./utils/biometricLock";
// Returning-member launch state: tells a brand-new visitor apart from a member coming home.
import {
  markReturningMember,
  clearReturningMember,
  hasAuthenticatedBefore,
  isAuthFailure,
  resolveLaunchState
} from "./utils/launchState";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Mic, MicOff, BookOpen, ChevronRight, Trash2, 
  ArrowLeft, Search, Settings, ChevronDown, Globe, 
  Shield, Download, ExternalLink, Sparkles, Loader2,
  Lock, Mail, User, Eye, EyeOff, LogOut, Check, AlertCircle, X,
  Crown, Star, Zap, Home, BarChart3, PenLine, Menu, Bell, Moon, Sun,
  CreditCard, Clock, Headphones, FileText, Heart, Trophy, Flame, Image,
  Plus, ChevronLeft, HelpCircle, Briefcase, Award, Monitor, Smartphone,
  MessageSquare, Info, Database, Share2, ToggleLeft, ToggleRight, Copy,
  Feather, Leaf, ArrowRight, Calendar, Play, Pause, RotateCcw, Volume2,
  MessageCircle, Cookie, Camera
} from "lucide-react";
import { 
  SparkleIcon, 
  DiamondSparkle, 
  LeafIcon, 
  FeatherIcon, 
  HeartOutlineIcon,
  MoonStarIcon 
} from "./components/ArvexisIcons";
import AuthLogo from "./auth/AuthLogo";
import AuthShell from "./auth/AuthShell";
import ArvexisLogo, { ArvexisWordmark, ArvexisLogoFull, ArvexisLogoBreathing, LOGO_SIZES, ArvexisLockup, ArvexisSymbolBreathing, LOCKUP_SIZES } from "./components/ArvexisLogo";
import { ArvexisSymbol, ArvexisAvatar } from "./components/ArvexisSymbol";
import ProfileEditor from "./components/ProfileEditor";
import { MemberAvatar } from "./components/MemberAvatar";
import ProfilePhotoSheet from "./components/ProfilePhotoSheet";
import SignedInDevices from "./components/SignedInDevices";
import LandingPage from "./LandingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import CopyrightPolicyPage from "./pages/CopyrightPolicyPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import AboutArvexisPage from "./pages/AboutArvexisPage";
import { memberView as memberAbout } from "./content/aboutArvexisContent";
import DreamConversation from "./components/DreamConversation";
import DreamJournal from "./components/DreamJournal";
import DreamsShell from "./components/DreamsShell";
import useFullScreenSurface from "./hooks/useFullScreenSurface";
import SignOutConfirm from "./components/SignOutConfirm";
import PreparingReflection from "./components/PreparingReflection";
import PremiumReflectionScreen from "./components/PremiumReflectionScreen";
import CompanionChat from "./components/CompanionChat";
import RealtimeVoiceMode from "./components/RealtimeVoiceMode";
import MemorySummary from "./components/MemorySummary";
import ArrivalHome from "./components/ArrivalHome";
import SharedMemories from "./components/SharedMemories";
import PastReflections from "./components/PastReflections";
import UsageThisPeriod, { UsageCompact } from "./components/UsageThisPeriod";
import { MembershipPlans } from "./components/MembershipPlans";
import { MembershipUsage } from "./components/MembershipUsage";
import PaymentReturn from "./components/PaymentReturn";
import FounderConsole from "./components/founder/FounderConsole";
import IntelligenceReviewPage from "./components/founder/IntelligenceView";
import FeedbackPage, { FeedbackSettingsRow } from "./components/FeedbackForm";
import S1OutputABDiagnostic from "./pages/S1OutputABDiagnostic";
import { UNRELEASED, isUnreleasedVisible } from "./featureGates";
import {
  isAndroidApp,
  fetchPlayBasePlans,
  purchasePlayPlan,
  restorePlayPurchases,
  openPlayManage,
} from "./utils/androidBilling";
import { isIosApp, iosPurchaseNotice } from "./utils/iosPurchaseGate";
import {
  fetchAppleProducts,
  purchaseApplePlan,
  restoreApplePurchases,
  openAppleManage,
} from "./utils/iosBilling";
import UnifiedConversation from "./components/UnifiedConversation";
import ConversationHistory from "./components/ConversationHistory";
import ArvexisPresenceScreen from "./components/ArvexisPresenceScreen";
import ExportMyDataModal from "./components/ExportMyDataModal";
import ArvexisVoiceAudition from "./components/ArvexisVoiceAudition";
import OnboardingV1 from "./onboarding/OnboardingV1";
import { checkOnboardingPending } from "./onboarding/onboardingStatus";
import { ChangePendingEmail } from "./components/ChangePendingEmail";
import { invalidatePlayback } from "./utils/listenPlayback";
import { deliverDownload } from './utils/deliverDownload';
import { installSessionRefresh } from './utils/sessionRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const API_BASE_URL = BACKEND_URL;

// ============================================================================
// PHASE 6B (STOP-2) — GOOGLE ACCOUNT-DELETION PROOF
// ============================================================================
// A Google account can only prove itself by going through the provider hop, which returns a
// single-use session_id in the URL fragment. This lifts that id out of the fragment for the
// deletion request the member already started, and never exchanges it for a session.
//
// It runs once at load, before any sign-in handling, and only when BOTH the deletion marker is on
// the URL and a fresh deletion intent exists in this tab. Anything else — no marker, no intent, a
// stale intent, a cancelled hop, a refresh, Back/Forward — captures nothing, signs nothing in and
// deletes nothing. Nothing is persisted server-side, and the proof is single-use in the browser
// too: the fragment and the marker are stripped from history the moment they are read, and the
// captured id is removed from the tab as soon as the deletion flow takes it. Arriving with a proof
// never deletes on its own: the member still has to confirm the deletion they began.
const DELETION_INTENT_KEY = 'arvexis_deletion_intent_at';
const DELETION_PROOF_KEY = 'arvexis_deletion_proof';
const DELETION_INTENT_MAX_AGE_MS = 10 * 60 * 1000;

export const startGoogleDeletionProof = () => {
  sessionStorage.setItem(DELETION_INTENT_KEY, String(Date.now()));
  sessionStorage.removeItem(DELETION_PROOF_KEY);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = `${window.location.origin}/settings?deletion_proof=1`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// Hands the captured proof to the deletion flow and spends it. A second read gets nothing.
export const takeGoogleDeletionProof = () => {
  const proof = sessionStorage.getItem(DELETION_PROOF_KEY);
  sessionStorage.removeItem(DELETION_PROOF_KEY);
  sessionStorage.removeItem(DELETION_INTENT_KEY);
  return proof || null;
};

(function captureGoogleDeletionProof() {
  try {
    const search = new URLSearchParams(window.location.search);
    if (search.get('deletion_proof') !== '1') return;
    const hash = window.location.hash || '';
    const sessionId = hash.includes('session_id=')
      ? hash.split('session_id=')[1]?.split('&')[0]
      : null;
    const startedAt = Number(sessionStorage.getItem(DELETION_INTENT_KEY) || 0);
    const fresh = Boolean(startedAt) && Date.now() - startedAt <= DELETION_INTENT_MAX_AGE_MS;
    // The provider material leaves the URL and the history entry either way, so a refresh or a
    // Back/Forward navigation cannot replay it.
    window.history.replaceState(null, '', `${window.location.pathname}?deletion_proof=1`);
    if (sessionId && fresh) {
      sessionStorage.setItem(DELETION_PROOF_KEY, sessionId);
    } else {
      sessionStorage.removeItem(DELETION_PROOF_KEY);
      sessionStorage.removeItem(DELETION_INTENT_KEY);
    }
  } catch (err) {
    // Captured nothing. The deletion flow will ask for proof again, and nothing is deleted.
  }
})();

// Configure axios to send cookies
axios.defaults.withCredentials = true;
installSessionRefresh();

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Subscription Context
const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

// Translations
const translations = {
  en: {
    // Branding
    appName: "ARVEXIS",
    tagline: "Where reflection becomes clarity.",
    dreamLens: "Arvexis",
    
    // Auth
    welcomeBack: "Welcome back",
    signInSubtitle: "Continue your conversation with Arvexis.",
    createAccount: "Create your account",
    createAccountSubtitle: "A private place to talk, reflect, and be yourself.",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    name: "Name",
    nameRequired: "Name is required",
    ageAffirm: "I confirm that I am 18 years of age or older.",
    ageRequired: "You must confirm that you are 18 years of age or older to create an account.",
    marketingOptIn: "Send me occasional Arvexis news, product updates, and offers.",
    signIn: "Sign In",
    signUp: "Begin Reflecting",
    signingIn: "Signing in...",
    signingUp: "Creating account...",
    noAccount: "New to Arvexis?",
    hasAccount: "Already have an account?",
    createOne: "Create an account",
    signInInstead: "Sign in",
    signOut: "Sign out",
    forgotPassword: "Forgot your password?",
    forgotPasswordTitle: "Reset your password",
    forgotPasswordSubtitle: "We'll send you a secure link to reset your password.",
    sendResetLink: "Send reset link",
    sending: "Sending...",
    checkYourEmail: "Check your email",
    resetLinkSent: "If an account exists with this email, you'll receive a password reset link shortly.",
    backToLogin: "Back to sign in",
    
    // Reset Password - EXACT SPEC COPY
    resetPasswordTitle: "Create a new password",
    resetPasswordSubtitle: "",
    newPassword: "New password",
    confirmNewPassword: "Confirm new password",
    resetPassword: "Update Password",
    resetting: "Updating...",
    resetLinkExpired: "This reset link has expired",
    resetLinkInvalid: "This reset link is no longer valid",
    requestNewLink: "Request a New Link",
    passwordResetSuccess: "Password updated",
    passwordResetSuccessSubtitle: "Your password has been changed successfully.",
    
    // Social Sign-In
    orContinueWith: "or continue with",
    continueWithGoogle: "Continue with Google",
    continueWithApple: "Continue with Apple",
    
    // Change Password
    changePassword: "Change Password",
    currentPassword: "Current password",
    newPasswordLabel: "New password",
    confirmNewPassword: "Confirm new password",
    changingPassword: "Changing password...",
    passwordChangedSuccess: "Password changed successfully",
    
    // Email Verification - EXACT SPEC COPY
    verifyingEmail: "Verifying your email…",
    pleaseWait: "",
    emailVerifiedTitle: "Email verified",
    emailVerifiedSubtitle: "You're all set.",
    verificationExpiredTitle: "This verification link has expired",
    verificationExpiredSubtitle: "For your security, verification links expire after 24 hours.",
    verificationFailedTitle: "This verification link isn't valid",
    verificationLinkInvalid: "Request a new verification email to continue.",
    verificationAlreadyDone: "Your email is already verified",
    networkErrorTitle: "We couldn't verify your email",
    networkErrorSubtitle: "Something interrupted the connection. Please try again.",
    continueToArvexis: "Continue to Arvexis",
    sendNewLink: "Send a New Link",
    tryAgain: "Try Again",
    
    passwordRequirements: "At least 10 characters with uppercase, lowercase, and a number",
    passwordMustContain: "Your password must contain:",
    passwordReq10Chars: "10+ characters",
    passwordReqUppercase: "One uppercase letter",
    passwordReqLowercase: "One lowercase letter",
    passwordReqNumber: "One number",
    invalidCredentials: "Invalid email or password. If you created your account with Google or Apple, continue with that instead, or use Forgot password to set one.",
    emailInUse: "This email is already registered",
    emailPendingDeletion: "This email belongs to an account that is being deleted. It becomes available again once the retention period ends, within 30 days.",
    accountDeleted: "This account is being deleted and can no longer be used to sign in.",
    passwordMismatch: "Passwords do not match",
    weakPassword: "Password does not meet requirements",
    networkError: "Connection error. Please try again.",
    tooManyAttempts: "Too many attempts. Please wait 15 minutes.",
    privacyNote: "A private place to be yourself.",
    signUpNote: "Your conversations. Your choices. Your space.",
    legalAgreePrefix: "By creating an account, you agree to the ",
    legalAgreeMiddle: " and acknowledge the ",
    legalAgreeSuffix: ".",
    
    // Onboarding
    onboard1Title: "Organize your inner patterns.",
    onboard1Sub: "A structured system for surfacing what matters over time.",
    onboard2Title: "One coherent reflection. No noise.",
    onboard2Sub: "Every entry follows the same five-section format for consistency and clarity.",
    onboard3Title: "Patterns over time.",
    onboard3Sub: "Start with Arvexis — your first reflection stream.",
    consentText: "I understand this is a reflection tool, not medical or diagnostic advice.",
    continue: "Continue",
    getStarted: "Get Started",
    
    // Home
    logDream: "Record a Dream",
    journal: "Journal",
    patterns: "Patterns",
    comingSoon: "Coming Soon",
    privateFooter: "Private by default. No social. No ads.",
    
    // Log Dream - Premium Recording Experience
    logDreamTitle: "Record a Dream",
    logDreamSubtitle: "Take your time. There's no rush.",
    logDreamSubtitle2: "Simply record your dream as you remember it.",
    howDidYouFeel: "How did you feel? (optional)",
    describeDream: "Write your dream here...",
    generateInterpretation: "Generate Interpretation",
    clearDream: "Clear",
    clearDreamConfirm: "Clear this dream?",
    clearDreamMessage: "This will remove everything you've entered.",
    
    // Recording Tab Labels
    recordTab: "Record",
    typeTab: "Type",
    tapToRecord: "Tap to start recording",
    speakNaturally: "Speak naturally in any language.",
    wellUnderstand: "We'll transcribe when you're done.",
    recordingLanguage: "Auto-detect",
    typeYourDream: "Type your dream",
    characterCount: "/4000",
    
    // Reassurance & Trust Cards
    everyDetailMatters: "Every detail matters.",
    everyDetailDesc: "Even small fragments can become meaningful over time.",
    interpretationPreview: "You will receive a structured interpretation in seconds",
    reflectionPreview: "You will receive a structured Reflection in seconds",
    generateReflection: "Capture Your Dream",
    reflectionPreviewDesc: "AI-powered insight based on psychology, symbolism, your dream history, and recurring patterns.",
    trustAcknowledgment: "I understand this is a reflection tool, not medical, psychological, diagnostic, or predictive advice.",
    learnMore: "Learn More",
    
    // My Dreams Button
    myDreams: "My Dreams",
    
    // Copy Interpretation
    copyInterpretation: "Copy",
    copyEntireInterpretation: "Copy Entire Interpretation",
    copySection: "Copy Section",
    interpretationCopied: "Interpretation copied",
    sectionCopied: "Section copied",
    
    // Home Hero - Dream Interpretation Identity
    heroPromptMorning: "What dream stayed with you this morning?",
    heroPromptAfternoon: "Which dream would you like to explore?",
    heroPromptEvening: "What dream would you like to understand?",
    heroPromptLate: "What dream is on your mind?",
    heroCTA: "Capture Your Dream",
    heroSubCTA: "Every dream has something to tell you.",
    quickActionNewDream: "New\nDream",
    
    // Create Keepsake Modal
    yourKeepsake: "Your Keepsake",
    createKeepsakeSubtitle: "Create a keepsake of your dream",
    createKeepsake: "Create Keepsake",
    shareDream: "Share",
    creatingKeepsake: "Creating your keepsake…",
    selectingArtwork: "Selecting artwork…",
    composingInterpretation: "Composing your interpretation…",
    preparingKeepsake: "Preparing your keepsake…",
    
    // Emotions - Canonical keys with display labels
    emotions: {
      calm: "Calm",
      anxious: "Anxious",
      sad: "Sad",
      confused: "Confused",
      peaceful: "Peaceful",
      fearful: "Fearful",
      hopeful: "Hopeful",
      curious: "Curious",
      nostalgic: "Nostalgic",
      unsettled: "Unsettled",
      neutral: "Neutral",
      other: "Other..."
    },
    customEmotionPlaceholder: "Describe how you felt...",
    
    // Reflection Result
    emotionalSummary: "Emotional Summary",
    keySymbols: "Key Symbols",
    integratedInterpretation: "Integrated Interpretation",
    questionToCarry: "A Question to Carry",
    groundingInsight: "Grounding Insight",
    generatedUsing: "Generated using structured pattern analysis.",
    saveToJournal: "Save to Journal",
    editDream: "Edit Dream",
    newDream: "Record a Dream",
    interpretationDisclaimer: "One perspective to explore — you are the expert on your own dreams.",
    reflectionFooter: "Generated through structured reflective analysis.",
    
    // Dreams List
    dreamsTitle: "Your Dreams",
    searchPlaceholder: "Search dreams...",
    noEntries: "No dreams recorded yet",
    startLogging: "Your first dream entry begins here.",
    
    // Settings
    settingsTitle: "Settings",
    language: "Language",
    privacy: "Privacy",
    dataStorage: "Data Storage",
    storedServer: "How your data is stored, exported and deleted.",
    exportKeepsake: "Create Keepsake",
    exportComingSoon: "Coming Soon",
    terms: "Terms of Service",
    privacyPolicy: "Privacy Policy",
    cookiePolicy: "Cookie Policy",
    copyrightPolicy: "Copyright Policy",
    helpSupport: "Help & Support",
    helpSupportSubtitle: "Contact us via email",
    deleteAccount: "Delete Account",
    deleteAccountSubtitle: "Permanently remove all your data",
    dangerZone: "DANGER ZONE",
    support: "SUPPORT",
    account: "ACCOUNT",
    legal: "LEGAL",
    
    // Data Storage Modal
    dataStorageTitle: "Data Storage",
    dataStorageDescription: "You can export or delete your data at any time.",
    dataStorageExportable: "Exportable at any time",
    dataStorageDeletable: "Deletable upon request",
    
    // Common
    back: "Back",
    arvexisHome: "Arvexis Home",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save",
    
    // Pattern Milestone
    milestoneTitle: "Your First Patterns",
    milestoneSubtitle: "Themes emerging across your dreams",
    keepReflecting: "Keep Dreaming",
    viewEntries: "View Dreams",
    appearsIn: "appears in",
    entries: "dreams",
    
    // Patterns Page
    patternsTitle: "Your Patterns",
    patternsSubtitle: "Evidence-based patterns from your dreams",
    dreamsLogged: "dreams logged",
    recurringThemes: "Recurring Themes",
    emotionalPatterns: "Emotional Patterns",
    timelineView: "Timeline",
    ofDreams: "of dreams",
    firstAppeared: "First appeared",
    viewRelatedEntries: "View related dreams",
    noPatternsYet: "Not enough data yet",
    noPatternsDescription: "Log at least 10 dreams to see your patterns emerge.",
    patternDisclaimer: "These patterns are observations from your dreams, not conclusions about you.",
    
    // Subscription & Premium
    freeTier: "Free",
    proTier: "Premium",
    trialTier: "Premium Trial",
    startFreeTrial: "Start Free Trial",
    startingTrial: "Starting...",
    trialDaysRemaining: "days remaining",
    trialEnded: "Trial ended",
    upgradeToPro: "Upgrade to Premium",
    upgrading: "Processing...",
    manageSubscription: "Manage Subscription",
    yourPatterns: "Your Patterns",
    patternsReadyToExplore: "Your patterns are ready to explore.",
    unlockFullPatterns: "Unlock full patterns",
    proFeatures: "What Premium adds",
    // The benefit hierarchy: the companion relationship first, capacity next, Patterns as a
    // supporting benefit. Nothing here claims that something Free already has is Premium-only,
    // and no allowance figure appears — those quantities are not set yet.
    proFeature1: "More room to talk with her",
    proFeature2: "More room to message her",
    proFeature3: "Step into the next room — a fresh conversation that picks up where you were",
    proFeature4: "The same Arvexis, the same memory, the same relationship",
    proFeature5: "Message History, Shared Memories, correction and Forget — on every plan",
    proFeature6: "Cancel anytime; what she remembers about you stays yours",
    premiumFootnote: "Every membership is the same Arvexis — her memory of you, Forget, privacy and crisis support. Memberships change how much room you have, never who she is.",
    cancelAnytime: "Cancel anytime",
    tryProFree: "Meet ARVEXIS free for 7 days",
    sevenDayTrial: "7-day free trial",
    dayTrial: "day free trial",
    noCardRequired: "No card required",
    monthly: "Monthly",
    month: "month",
    year: "year",
    subscriptionActive: "Your membership is active",
    trialActiveMessage: "You're exploring ARVEXIS. Choose Essential or Premium to continue after your trial.",
    subscriptionCanceled: "Premium access until period ends",
    paymentSuccess: "Payment Successful",
    paymentSuccessMessage: "Welcome to Premium.",
    paymentCanceled: "Payment Canceled",
    paymentCanceledMessage: "No worries. You can upgrade anytime from Settings.",
    explorePro: "Continue",
    continueFree: "Continue with Free"
  },
  es: {
    // Branding
    appName: "ARVEXIS",
    tagline: "Donde la reflexión se convierte en claridad.",
    dreamLens: "Arvexis",
    
    // Auth
    welcomeBack: "Qué bueno tenerte de vuelta",
    signInSubtitle: "Continúa tu conversación con Arvexis.",
    createAccount: "Crea tu cuenta",
    createAccountSubtitle: "Un lugar privado para hablar, reflexionar y ser quien eres.",
    email: "Correo electrónico",
    password: "Contraseña",
    confirmPassword: "Confirmar contraseña",
    name: "Nombre",
    nameRequired: "El nombre es obligatorio",
    ageAffirm: "Confirmo que tengo 18 años de edad o más.",
    ageRequired: "Debes confirmar que tienes 18 años de edad o más para crear una cuenta.",
    marketingOptIn: "Envíame ocasionalmente noticias, novedades del producto y ofertas de Arvexis.",
    signIn: "Iniciar sesión",
    signUp: "Comienza a Reflexionar",
    signingIn: "Iniciando sesión...",
    signingUp: "Creando cuenta...",
    noAccount: "¿Primera vez en Arvexis?",
    hasAccount: "¿Ya tienes una cuenta?",
    createOne: "Crea una cuenta",
    signInInstead: "Inicia sesión",
    signOut: "Cerrar sesión",
    forgotPassword: "¿Olvidaste tu contraseña?",
    forgotPasswordTitle: "Restablece tu contraseña",
    forgotPasswordSubtitle: "Te enviaremos un enlace seguro para restablecer tu contraseña.",
    sendResetLink: "Enviar enlace",
    sending: "Enviando...",
    checkYourEmail: "Revisa tu correo",
    resetLinkSent: "Si existe una cuenta con este correo, recibirás un enlace de restablecimiento de contraseña pronto.",
    backToLogin: "Volver a iniciar sesión",
    
    // Reset Password
    resetPasswordTitle: "Crear nueva contraseña",
    resetPasswordSubtitle: "Elige una contraseña segura para tu cuenta",
    newPassword: "Nueva contraseña",
    resetPassword: "Restablecer Contraseña",
    resetting: "Restableciendo...",
    resetLinkExpired: "Este enlace ha expirado. Por favor solicita uno nuevo.",
    passwordResetSuccess: "Contraseña restablecida",
    passwordResetSuccessSubtitle: "Ahora puedes iniciar sesión con tu nueva contraseña.",
    
    // Social Sign-In
    orContinueWith: "o continúa con",
    continueWithGoogle: "Continuar con Google",
    continueWithApple: "Continuar con Apple",
    
    // Change Password
    changePassword: "Cambiar Contraseña",
    currentPassword: "Contraseña actual",
    newPasswordLabel: "Nueva contraseña",
    confirmNewPassword: "Confirmar nueva contraseña",
    changingPassword: "Cambiando contraseña...",
    passwordChangedSuccess: "Contraseña cambiada exitosamente",
    
    // Email Verification - EXACT SPEC COPY (Spanish)
    verifyingEmail: "Verificando tu correo…",
    pleaseWait: "",
    emailVerifiedTitle: "Correo verificado",
    emailVerifiedSubtitle: "Todo listo.",
    verificationExpiredTitle: "Este enlace de verificación ha expirado",
    verificationExpiredSubtitle: "Por tu seguridad, los enlaces de verificación expiran después de 24 horas.",
    verificationFailedTitle: "Este enlace de verificación no es válido",
    verificationLinkInvalid: "Solicita un nuevo correo de verificación para continuar.",
    verificationAlreadyDone: "Tu correo ya está verificado",
    networkErrorTitle: "No pudimos verificar tu correo",
    networkErrorSubtitle: "Algo interrumpió la conexión. Por favor intenta de nuevo.",
    continueToArvexis: "Continuar a Arvexis",
    sendNewLink: "Enviar Nuevo Enlace",
    tryAgain: "Intentar de Nuevo",
    
    passwordRequirements: "Al menos 10 caracteres con mayúscula, minúscula y un número",
    passwordMustContain: "Tu contraseña debe contener:",
    passwordReq10Chars: "10+ caracteres",
    passwordReqUppercase: "Una letra mayúscula",
    passwordReqLowercase: "Una letra minúscula",
    passwordReqNumber: "Un número",
    invalidCredentials: "Correo o contraseña inválidos. Si creaste tu cuenta con Google o Apple, continúa con ese método, o usa Olvidé mi contraseña para crear una.",
    emailInUse: "Este correo ya está registrado",
    emailPendingDeletion: "Este correo pertenece a una cuenta que se está eliminando. Estará disponible de nuevo cuando termine el período de retención, dentro de 30 días.",
    accountDeleted: "Esta cuenta se está eliminando y ya no puede usarse para iniciar sesión.",
    passwordMismatch: "Las contraseñas no coinciden",
    weakPassword: "La contraseña no cumple los requisitos",
    networkError: "Error de conexión. Por favor intenta de nuevo.",
    tooManyAttempts: "Demasiados intentos. Por favor espera 15 minutos.",
    privacyNote: "Un lugar privado para ser quien eres.",
    signUpNote: "Tus conversaciones. Tus decisiones. Tu espacio.",
    legalAgreePrefix: "Al crear una cuenta, aceptas los ",
    legalAgreeMiddle: " y reconoces la ",
    legalAgreeSuffix: ".",
    
    // Onboarding
    onboard1Title: "Organiza tus patrones internos.",
    onboard1Sub: "Un sistema estructurado para revelar lo que importa con el tiempo.",
    onboard2Title: "Una reflexión coherente. Sin ruido.",
    onboard2Sub: "Cada entrada sigue el mismo formato de cinco secciones para consistencia y claridad.",
    onboard3Title: "Patrones con el tiempo.",
    onboard3Sub: "Comienza con Arvexis — tu primer flujo de reflexión.",
    consentText: "Entiendo que esta es una herramienta de reflexión, no consejo médico o diagnóstico.",
    continue: "Continuar",
    getStarted: "Comenzar",
    
    // Home
    logDream: "Registra un sueño",
    journal: "Diario",
    patterns: "Patrones",
    comingSoon: "Próximamente",
    privateFooter: "Privado por defecto. Sin social. Sin anuncios.",
    
    // Log Dream - Premium Recording Experience
    logDreamTitle: "Registra un sueño",
    logDreamSubtitle: "Tómate tu tiempo. No hay prisa.",
    logDreamSubtitle2: "Simplemente registra tu sueño como lo recuerdas.",
    howDidYouFeel: "¿Cómo te sentiste? (opcional)",
    describeDream: "Escribe tu sueño aquí...",
    generateInterpretation: "Generar Interpretación",
    clearDream: "Borrar",
    clearDreamConfirm: "¿Borrar este sueño?",
    clearDreamMessage: "Esto eliminará todo lo que has ingresado.",
    
    // Recording Tab Labels
    recordTab: "Grabar",
    typeTab: "Escribir",
    tapToRecord: "Toca para comenzar a grabar",
    speakNaturally: "Habla naturalmente en cualquier idioma.",
    wellUnderstand: "Transcribiremos cuando termines.",
    recordingLanguage: "Auto-detectar",
    typeYourDream: "Escribe tu sueño",
    characterCount: "/4000",
    
    // Reassurance & Trust Cards
    everyDetailMatters: "Cada detalle importa.",
    everyDetailDesc: "Incluso pequeños fragmentos pueden volverse significativos con el tiempo.",
    interpretationPreview: "Recibirás una interpretación estructurada en segundos",
    reflectionPreview: "Recibirás una Reflexión estructurada en segundos",
    generateReflection: "Guardar tu sueño",
    reflectionPreviewDesc: "Perspectiva impulsada por IA basada en psicología, simbolismo, tu historial de sueños y patrones recurrentes.",
    trustAcknowledgment: "Entiendo que esta es una herramienta de reflexión, no consejo médico, psicológico, diagnóstico o predictivo.",
    learnMore: "Más Información",
    
    // My Dreams Button
    myDreams: "Mis Sueños",
    
    // Copy Interpretation
    copyInterpretation: "Copiar",
    copyEntireInterpretation: "Copiar Interpretación Completa",
    copySection: "Copiar Sección",
    interpretationCopied: "Interpretación copiada",
    sectionCopied: "Sección copiada",
    
    // Home Hero - Dream Interpretation Identity
    heroPromptMorning: "¿Qué sueño te acompañó esta mañana?",
    heroPromptAfternoon: "¿Qué sueño te gustaría explorar?",
    heroPromptEvening: "¿Qué sueño quieres entender?",
    heroPromptLate: "¿Qué sueño tienes en mente?",
    heroCTA: "Guardar tu sueño",
    heroSubCTA: "Cada sueño tiene algo que decirte.",
    quickActionNewDream: "Nuevo\nSueño",
    
    // Create Keepsake Modal
    yourKeepsake: "Tu Recuerdo del Sueño",
    createKeepsakeSubtitle: "Crea un Recuerdo del Sueño",
    createKeepsake: "Crear Recuerdo del Sueño",
    shareDream: "Compartir",
    creatingKeepsake: "Creando tu Recuerdo del Sueño…",
    selectingArtwork: "Seleccionando imagen…",
    composingInterpretation: "Componiendo tu interpretación…",
    preparingKeepsake: "Preparando tu Recuerdo del Sueño…",
    
    // Emotions - Canonical keys with Spanish display labels
    emotions: {
      calm: "Tranquilo",
      anxious: "Ansioso",
      sad: "Triste",
      confused: "Confundido",
      peaceful: "Pacífico",
      fearful: "Temeroso",
      hopeful: "Esperanzado",
      curious: "Curioso",
      nostalgic: "Nostálgico",
      unsettled: "Inquieto",
      neutral: "Neutral",
      other: "Otro..."
    },
    customEmotionPlaceholder: "Describe cómo te sentiste...",
    
    // Reflection Result
    emotionalSummary: "Resumen Emocional",
    keySymbols: "Símbolos Clave",
    integratedInterpretation: "Interpretación Integrada",
    questionToCarry: "Algo para Considerar",
    groundingInsight: "Perspectiva de Cierre",
    generatedUsing: "Generado usando análisis de patrones estructurado.",
    saveToJournal: "Guardar en Diario",
    editDream: "Editar Sueño",
    newDream: "Registra un sueño",
    interpretationDisclaimer: "Una perspectiva para explorar — tú eres el experto en tus propios sueños.",
    reflectionFooter: "Generado mediante análisis reflexivo estructurado.",
    
    // Dreams List
    dreamsTitle: "Tus Sueños",
    searchPlaceholder: "Buscar sueños...",
    noEntries: "Aún no hay sueños registrados",
    startLogging: "Tu primer sueño comienza aquí.",
    
    // Settings
    settingsTitle: "Configuración",
    language: "Idioma",
    privacy: "Privacidad",
    dataStorage: "Almacenamiento de Datos",
    storedServer: "Cómo se almacenan, exportan y eliminan tus datos.",
    exportKeepsake: "Crear Recuerdo del Sueño",
    exportComingSoon: "Próximamente",
    terms: "Términos de Servicio",
    privacyPolicy: "Política de Privacidad",
    cookiePolicy: "Política de Cookies",
    copyrightPolicy: "Política de Derechos de Autor",
    helpSupport: "Ayuda y Soporte",
    helpSupportSubtitle: "Contáctanos por correo",
    deleteAccount: "Eliminar Cuenta",
    deleteAccountSubtitle: "Eliminar permanentemente todos tus datos",
    dangerZone: "ZONA DE PELIGRO",
    support: "SOPORTE",
    account: "CUENTA",
    legal: "LEGAL",
    
    // Data Storage Modal
    dataStorageTitle: "Almacenamiento de Datos",
    dataStorageDescription: "Puedes exportar o eliminar tus datos en cualquier momento.",
    dataStorageExportable: "Exportable en cualquier momento",
    dataStorageDeletable: "Eliminable a petición",
    
    // Common
    back: "Atrás",
    arvexisHome: "Inicio de Arvexis",
    delete: "Eliminar",
    cancel: "Cancelar",
    save: "Guardar",
    
    // Pattern Milestone
    milestoneTitle: "Tus Primeros Patrones",
    milestoneSubtitle: "Temas emergiendo a través de tus sueños",
    keepReflecting: "Seguir Soñando",
    viewEntries: "Ver Sueños",
    appearsIn: "aparece en",
    entries: "sueños",
    
    // Patterns Page
    patternsTitle: "Tus Patrones",
    patternsSubtitle: "Patrones basados en evidencia de tus sueños",
    dreamsLogged: "sueños registrados",
    recurringThemes: "Temas Recurrentes",
    emotionalPatterns: "Patrones Emocionales",
    timelineView: "Línea de Tiempo",
    ofDreams: "de sueños",
    firstAppeared: "Primera aparición",
    viewRelatedEntries: "Ver sueños relacionados",
    noPatternsYet: "Aún no hay suficientes datos",
    noPatternsDescription: "Registra al menos 10 sueños para ver tus patrones emerger.",
    patternDisclaimer: "Estos patrones son observaciones de tus sueños, no conclusiones sobre ti.",
    
    // Subscription & Premium
    freeTier: "Gratis",
    proTier: "Premium",
    trialTier: "Prueba Premium",
    startFreeTrial: "Comenzar Prueba Gratis",
    startingTrial: "Iniciando...",
    trialDaysRemaining: "días restantes",
    trialEnded: "Prueba terminada",
    upgradeToPro: "Mejorar a Premium",
    upgrading: "Procesando...",
    manageSubscription: "Gestionar Suscripción",
    yourPatterns: "Tus Patrones",
    patternsReadyToExplore: "Tus patrones están listos para explorar.",
    unlockFullPatterns: "Desbloquear patrones completos",
    proFeatures: "Lo que añade Premium",
    proFeature1: "Más espacio para hablar con ella",
    proFeature2: "Más espacio para escribirle",
    proFeature3: "Pasar a la siguiente sala — una conversación nueva que sigue donde estabais",
    proFeature4: "La misma Arvexis, la misma memoria, la misma relación",
    proFeature5: "Historial de mensajes, Recuerdos compartidos, corregir y olvidar — en todos los planes",
    proFeature6: "Cancela cuando quieras; lo que recuerda de ti sigue siendo tuyo",
    premiumFootnote: "Todas las membresías son la misma Arvexis — su memoria de ti, Olvidar, la privacidad y el apoyo en crisis. Las membresías cambian cuánto espacio tienes, nunca quién es ella.",
    cancelAnytime: "Cancela cuando quieras",
    tryProFree: "Conoce a ARVEXIS gratis 7 días",
    sevenDayTrial: "7 días de prueba gratis",
    dayTrial: "días de prueba gratis",
    noCardRequired: "Sin tarjeta requerida",
    monthly: "Mensual",
    month: "mes",
    year: "año",
    subscriptionActive: "Tu membresía está activa",
    trialActiveMessage: "Estás conociendo ARVEXIS. Elige Essential o Premium para continuar después de tu prueba.",
    subscriptionCanceled: "Acceso Premium hasta fin del período",
    paymentSuccess: "Pago Exitoso",
    paymentSuccessMessage: "Bienvenida a Premium.",
    paymentCanceled: "Pago Cancelado",
    paymentCanceledMessage: "Sin problema. Puedes actualizar en cualquier momento desde Configuración.",
    explorePro: "Continuar",
    continueFree: "Continuar con Gratis"
  }
};

const EMOTIONS = ["calm", "anxious", "sad", "confused", "peaceful", "fearful", "hopeful", "curious", "nostalgic", "unsettled", "neutral", "other"];

// ============================================================================
// NAVIGATION COMPONENTS - Premium UI v2.0 (Based on Approved Mockups)
// ============================================================================

// Atmospheric Background Component
const AtmosphericBackground = ({ variant = 'home' }) => {
  return (
    <div className={`atmospheric-bg atmospheric-bg--${variant}`}>
      <div 
        className="atmospheric-bg__image" 
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/bg-atmospheric.png)` }}
      />
      <div className="atmospheric-bg__overlay" />
      {/* 6D: the `planetary-orb` disc that used to render here was a second orb implementation.
          It was unreachable — no caller has ever passed variant="home" — so it is removed.
          ArvexisPresence is the one and only Arvexis orb. */}
      <div className="atmospheric-bg__noise" />
    </div>
  );
};

// Bottom Navigation Bar (Mobile) - v2.0 with Gold active state and dot indicator
const BottomNavV2 = ({ currentPath, lang }) => {
  const navigate = useNavigate();
  
  const navItems = [
    { path: '/home', icon: Home, label: lang === 'es' ? 'Inicio' : 'Home', testId: 'home' },
    // Compact bottom-nav label only. The route, the icon, the page title ("Dream Journal" /
    // "Diario de Sueños") and every Dream behaviour are unchanged.
    { path: '/dreams', icon: BookOpen, label: lang === 'es' ? 'Diario' : 'Journal', testId: 'dreams' },
    { path: '/profile', icon: User, label: lang === 'es' ? 'Perfil' : 'Profile', testId: 'profile' },
  ];

  return (
    <nav className="bottom-nav" data-testid="bottom-nav">
      {navItems.map(({ path, icon: Icon, label, testId }) => {
        const isActive = currentPath === path || 
          (path === '/dreams' && (currentPath === '/journal' || currentPath.startsWith('/reflection') || currentPath.startsWith('/interpretation')));
        
        return (
          <button
            key={path}
            onClick={() => navigate(path)}
            className={`nav-item ${isActive ? 'active' : ''}`}
            data-testid={`nav-${testId}`}
          >
            <Icon className="nav-item__icon" strokeWidth={1.5} />
            <span className="nav-item__label">{label}</span>
          </button>
        );
      })}
    </nav>
  );
};

// ============================================================================
// MOBILE HAMBURGER MENU
// Full-screen slide-out navigation for mobile
// ============================================================================
const MobileMenu = ({ isOpen, onClose, currentPath, lang }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isPro } = useSubscription();
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  const handleNavigation = (path, state) => {
    navigate(path, state ? { state } : undefined);
    onClose();
  };

  const handleLogout = async () => {
    await logout();
    // Explicit sign-out returns the member to the public welcome, not to a sign-in form.
    navigate('/', { replace: true });
    onClose();
  };

  // Lock the page behind the drawer so iOS never scroll-chains into it.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  // G2-C5: the Home row is contextual. Opened over Home it is redundant — closing the drawer
  // already reveals Home. Opened from any other authenticated surface it is a member's reliable
  // route back, so it stays. No new route, no changed destination.
  const onHome = currentPath === '/home';

  const menuItems = [
    ...(onHome ? [] : [{ path: '/home', icon: Home, label: lang === 'es' ? 'Inicio' : 'Home' }]),
    // RELATIONSHIP — the relationship surfaces, and with them the forget, correct and
    // delete controls, are findable without going through Settings.
    { group: lang === 'es' ? 'RELACIÓN' : 'RELATIONSHIP' },
    { path: '/settings', state: { open: 'shared-memories' }, key: 'shared-memories', icon: Heart, label: lang === 'es' ? 'Recuerdos Compartidos' : 'Shared Memories' },
    { path: '/settings', state: { open: 'message-history' }, key: 'message-history', icon: MessageCircle, label: lang === 'es' ? 'Historial de Mensajes' : 'Message History' },
    { path: '/settings', state: { open: 'saved-talks' }, key: 'saved-talks', icon: Mic, label: lang === 'es' ? 'Charlas Guardadas' : 'Saved Talks' },
    { group: lang === 'es' ? 'SUEÑOS' : 'DREAMS' },
    { path: '/log', icon: PenLine, label: lang === 'es' ? 'Registra un sueño' : 'Record a Dream' },
    { path: '/dreams', icon: BookOpen, label: lang === 'es' ? 'Diario de Sueños' : 'Dream Journal' },
    { group: lang === 'es' ? 'CUENTA' : 'ACCOUNT' },
    // D1: the drawer's member header is the single Profile entrance. The duplicate ACCOUNT
    // "Profile" row is retired. The route, the screen and the header's destination are unchanged.
    { path: '/settings', icon: Settings, label: lang === 'es' ? 'Configuración' : 'Settings' },
  ];

  if (!isPro) {
    menuItems.push({ path: '/subscription', icon: Crown, label: lang === 'es' ? 'Mejorar a Premium' : 'Upgrade to Premium', highlight: true });
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}
            onClick={onClose}
            data-testid="mobile-menu-backdrop"
          />
          
          {/* Menu Panel */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
            className="fixed top-0 left-0 bottom-0 z-50 w-[300px] min-[1200px]:w-[420px] flex flex-col"
            style={{ 
              background: 'rgba(6, 16, 26, 0.98)',
              borderRight: '1px solid rgba(128, 149, 166, 0.28)'
            }}
            data-testid="mobile-menu-panel"
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between px-5 shrink-0"
              style={{ 
                paddingTop: 'max(20px, calc(env(safe-area-inset-top, 0px) + 8px))',
                paddingBottom: '16px',
                borderBottom: '1px solid rgba(128, 149, 166, 0.25)'
              }}
            >
              <button 
                onClick={() => handleNavigation('/home')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                data-testid="mobile-menu-logo"
              >
                <ArvexisLockup width={160} />
              </button>
              <button
                onClick={onClose}
                className="w-11 h-11 rounded-full flex items-center justify-center border border-[#8095A6]/30 outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
                style={{ background: 'rgba(7, 20, 33, 0.78)' }}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X size={18} style={{ color: '#A8B3BF' }} />
              </button>
            </div>

            {/* Scroll region — member identity + navigation. iOS momentum scrolling,
                contained so the page behind never rubber-bands. */}
            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: 'touch' }}
              data-testid="mobile-menu-scroll"
            >
            {/* User Info - Now clickable */}
            {user && (
              <button 
                onClick={() => handleNavigation('/profile')}
                className="w-full px-5 py-4 hover:bg-white/5 transition-colors text-left"
                style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}
                data-testid="mobile-menu-avatar"
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(212, 168, 67, 0.2), rgba(212, 168, 67, 0.05))',
                      border: '1px solid rgba(212, 168, 67, 0.3)'
                    }}
                    data-testid="mobile-menu-member-avatar"
                  >
                    {/* Same authoritative precedence as Profile: their photo, their Google photo,
                        their initials, then the monogram. The gold treatment this row already had
                        for initials is preserved. */}
                    <MemberAvatar
                      user={user}
                      size={38}
                      symbolSize={22}
                      initialsStyle={{ fontFamily: 'inherit', fontSize: 16, fontWeight: 600,
                                       color: 'var(--gold)', letterSpacing: 'normal' }}
                    />
                  </div>
                  <div className="flex-1">
                    <p style={{ fontSize: '14px', fontWeight: 500, color: '#FFFFFF' }}>
                      {user.name || user.email?.split('@')[0]}
                    </p>
                    <p style={{ fontSize: '12px', color: isPro ? 'var(--gold)' : 'rgba(255,255,255,0.5)' }}>
                      {isPro ? 'Premium' : 'Free'}
                    </p>
                  </div>
                  <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.4)' }} />
                </div>
              </button>
            )}

            {/* Navigation Items */}
            <nav className="py-3 px-3">
              {menuItems.map((item) => {
                if (item.group) {
                  return (
                    <p
                      key={`group-${item.group}`}
                      className="px-3 pt-4 pb-2"
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.22em',
                        color: '#A8B3BF',
                      }}
                      data-testid={`menu-group-${item.group.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.group}
                    </p>
                  );
                }
                const isActive = currentPath === item.path && !item.state;
                return (
                  <button
                    key={item.key || item.path}
                    onClick={() => handleNavigation(item.path, item.state)}
                    className="w-full min-h-[48px] flex items-center gap-3 px-3 py-3 rounded-xl mb-1 transition-colors hover:bg-white/[0.03] outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
                    style={{ 
                      background: isActive ? 'rgba(232, 197, 143, 0.10)' : 'transparent',
                      border: isActive ? '1px solid rgba(232, 197, 143, 0.28)' : '1px solid transparent'
                    }}
                    data-testid={`menu-${item.key || item.path.replace('/', '')}`}
                  >
                    <item.icon 
                      size={20} 
                      style={{ 
                        color: item.highlight ? '#E8C58F' : (isActive ? '#E8C58F' : '#A8B3BF')
                      }} 
                    />
                    <span style={{ 
                      fontSize: '16px', 
                      fontWeight: isActive ? 600 : 400,
                      color: item.highlight ? '#E8C58F' : (isActive ? '#F4EFE7' : '#F4EFE7')
                    }}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </nav>
            </div>

            {/* Footer */}
            <div 
              className="px-5 py-4 shrink-0"
              style={{ 
                borderTop: '1px solid rgba(128, 149, 166, 0.25)',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)'
              }}
            >
              <button
                onClick={() => setShowSignOutConfirm(true)}
                className="w-full min-h-[48px] flex items-center gap-3 px-3 py-3 rounded-xl transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
                style={{ background: 'rgba(239, 68, 68, 0.08)' }}
                data-testid="menu-logout"
              >
                <LogOut size={20} style={{ color: '#EF4444' }} />
                <span style={{ fontSize: '15px', color: '#EF4444' }}>
                  {lang === 'es' ? 'Cerrar Sesión' : 'Sign Out'}
                </span>
              </button>
            </div>
          </motion.div>

          {/* Sign Out — the same approved confirmation the Settings surface uses. */}
          {showSignOutConfirm && (
            <SignOutConfirm
              lang={lang}
              prefix="menu"
              onCancel={() => setShowSignOutConfirm(false)}
              onConfirm={handleLogout}
            />
          )}
        </>
      )}
    </AnimatePresence>
  );
};

// App Shell v2.0 - Responsive wrapper with atmospheric backgrounds
const AppShellV2 = ({ children, currentPath, variant = 'dark', showNav = true, lang = 'en' }) => {
  return (
    <div className="app-shell">
      <AtmosphericBackground variant={variant} />
      <div className="screen">
        {children}
      </div>
      {showNav && <BottomNavV2 currentPath={currentPath} lang={lang} />}
    </div>
  );
};

// Backward compatible alias
const AppShell = AppShellV2;

// ============================================================================
// AUTHENTICATION COMPONENTS
// ============================================================================

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Which of the four launch states this boot resolved to, so `/` can tell a brand-new visitor
  // apart from a member coming home. See utils/launchState.js.
  const [launchState, setLaunchState] = useState('first-time');

  // Check auth status on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // OAuthCallbackHandler will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setIsLoading(false);
      return;
    }
    checkAuthStatus();
  }, []);

  const markAuthenticated = (data) => {
    setUser(data);
    setIsAuthenticated(true);
    setLaunchState('authenticated');
    // This device has signed in at least once. Only the fact — no token, no address, no content.
    markReturningMember();
  };

  const checkAuthStatus = async () => {
    const returning = hasAuthenticatedBefore();
    try {
      const response = await axios.get(`${API}/auth/me`);
      markAuthenticated(response.data);
    } catch (err) {
      // A cold launch on poor signal is NOT a sign-out. Only the server may end a session.
      if (!isAuthFailure(err)) {
        setUser(null);
        setIsAuthenticated(false);
        setLaunchState(resolveLaunchState({ reason: 'network', returning }));
        return;
      }
      // The access cookie lives 15 minutes; the refresh cookie lives 7 days. An expired access
      // cookie is the ordinary condition of a member returning the next morning — not a logout.
      // Spend the one refresh the server already supports before concluding anything. The refresh
      // credential is an httpOnly cookie this code cannot read, so nothing is weakened here.
      try {
        await axios.post(`${API}/auth/refresh`);
        const retry = await axios.get(`${API}/auth/me`);
        markAuthenticated(retry.data);
      } catch (refreshErr) {
        setUser(null);
        setIsAuthenticated(false);
        setLaunchState(resolveLaunchState({
          reason: isAuthFailure(refreshErr) ? 'auth' : 'network',
          returning
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    markAuthenticated(response.data);
    return response.data;
  };

  const register = async (email, password, name, preferredLanguage = 'en', ageAffirmed = false,
                          marketingOptIn = false) => {
    // CRITICAL: Clear account-specific localStorage before new registration
    // This prevents onboarding state from bleeding over from previous accounts
    // DO NOT use localStorage.clear() - preserve language preferences
    const accountSpecificKeys = [
      'arvexis_welcome_seen',      // Onboarding completion
      'arvexis_onboarded',         // Legacy onboarding key
      'arvexis_token',             // Auth token
      'arvexis_reflection_read',   // Read reflection items
      'arvexis_trust_acknowledged', // Trust modal
      'arvexis_wellness_checkins', // Wellness data
      'arvexis_milestone_3_shown', // Milestones
      'arvexis_user_name'          // Cached user name
    ];
    accountSpecificKeys.forEach(key => localStorage.removeItem(key));
    sessionStorage.removeItem('arvexis_milestone_shown_session');
    
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name,
      preferred_language: preferredLanguage,
      age_affirmed: ageAffirmed,
      // Optional, and false unless the member checked it themselves.
      marketing_opt_in: marketingOptIn
    });
    // No session is created at registration — the email-ownership gate requires the member to
    // confirm the address first. Do not mark the app authenticated here, or the activation
    // screen is skipped and the member is sent to /home without a session.
    return response.data;
  };

  const logout = async () => {
    // Read Aloud now survives navigation inside Arvexis. Signing out is not navigation: nothing
    // she was reading may outlive the session. This stops what is scheduled and nothing else —
    // no audio session, no microphone, no Talk.
    try { invalidatePlayback(); } catch (err) { /* nothing was playing */ }
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Ignore errors, clear state anyway
    }
    // CRITICAL: Clear account-specific localStorage on logout
    // This prevents onboarding state from bleeding over to new accounts
    // DO NOT use localStorage.clear() - preserve language preferences
    const accountSpecificKeys = [
      'arvexis_welcome_seen',      // Onboarding completion
      'arvexis_onboarded',         // Legacy onboarding key
      'arvexis_token',             // Auth token
      'arvexis_reflection_read',   // Read reflection items
      'arvexis_trust_acknowledged', // Trust modal
      'arvexis_wellness_checkins', // Wellness data
      'arvexis_milestone_3_shown', // Milestones
      'arvexis_user_name'          // Cached user name
    ];
    accountSpecificKeys.forEach(key => localStorage.removeItem(key));
    sessionStorage.removeItem('arvexis_milestone_shown_session');
    
    // An explicit sign-out is not an expired session. The member asked to leave, so this device
    // stops being treated as theirs and the public welcome becomes correct again.
    clearReturningMember();
    clearLockState();
    setLaunchState('first-time');
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API}/auth/refresh`);
      return true;
    } catch (err) {
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  const loginWithGoogleCredential = async (credential) => {
    const response = await axios.post(`${API}/auth/google/verify`, { credential });
    markAuthenticated(response.data);
    return response.data;
  };

  const loginWithGoogle = async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId });
    markAuthenticated(response.data);
    return response.data;
  };

  const loginWithApple = async (idToken, userInfo) => {
    const response = await axios.post(`${API}/auth/apple/callback`, { id_token: idToken, user_info: userInfo });
    markAuthenticated(response.data);
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, { 
      current_password: currentPassword, 
      new_password: newPassword 
    });
    return response.data;
  };

  // Refresh user data from server (after profile updates)
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated,
      launchState,
      login,
      register,
      logout,
      refreshToken,
      checkAuthStatus,
      loginWithGoogle, loginWithGoogleCredential,
      loginWithApple,
      changePassword,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Subscription Provider
const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSubscription = async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/subscription/info`);
      setSubscription(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/subscription/pricing`);
      setPricing(response.data);
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  };

  useEffect(() => {
    fetchSubscription();
    fetchPricing(); // Pricing doesn't require auth
  }, [isAuthenticated]);

  const startTrial = async () => {
    try {
      const response = await axios.post(`${API}/subscription/trial`);
      if (response.data.success) {
        setSubscription(response.data.subscription_info);
      }
      return response.data;
    } catch (err) {
      console.error('Failed to start trial:', err);
      return { success: false, message: 'Failed to start trial' };
    }
  };

  const createCheckout = async (billingInterval = 'monthly') => {
    // Apple requires IAP for digital subscriptions bought inside an iOS app, and StoreKit is not
    // implemented yet, so the native iOS build never opens Stripe Checkout. Gated here, in the one
    // function every caller goes through, rather than at each button. Web and Android unaffected.
    if (isIosApp()) {
      return { blocked: 'ios' };
    }
    try {
      const origin = window.location.origin;
      const response = await axios.post(`${API}/subscription/checkout`, {
        origin_url: origin,
        billing_interval: billingInterval
      });
      return response.data;
    } catch (err) {
      console.error('Failed to create checkout:', err);
      return null;
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        await fetchSubscription(); // Refresh subscription info
      }
      return response.data;
    } catch (err) {
      console.error('Failed to check payment status:', err);
      return null;
    }
  };

  const isPro = subscription?.can_access_pro_features || false;
  const isTrial = subscription?.status === 'TRIAL';
  const canStartTrial = subscription?.can_start_trial || false;

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      pricing,
      isLoading,
      isPro,
      isTrial,
      canStartTrial,
      startTrial,
      createCheckout,
      checkPaymentStatus,
      refreshSubscription: fetchSubscription
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * REQUIRE FACE ID — the member's own privacy choice, stored as a single local flag. No password,
 * no token, no PII, no biometric identifier; the match itself is performed by iOS.
 */
const BiometricLockSetting = ({ lang }) => {
  const [enabled, setEnabled] = useState(isBiometricLockEnabled);
  
  const choose = (value) => {
    setBiometricLockEnabled(value);
    setEnabled(value);
  };
  
  const optionClass = (active) => `min-h-[44px] px-4 rounded-lg text-sm font-medium transition-all ${
    active
      ? 'bg-[#E8C58F]/10 text-[#E8C58F] border border-[#E8C58F]/34'
      : 'bg-[#F4EFE7]/[0.03] text-[#F4EFE7]/56 border border-transparent hover:bg-[#F4EFE7]/[0.06]'
  }`;
  
  return (
    <div className="settings-row" data-testid="biometric-lock-setting">
      <div className="settings-row__icon">
        <Lock className="w-5 h-5" />
      </div>
      <div className="settings-row__content">
        <span className="settings-row__label">
          {lang === 'es' ? 'Requerir Face ID' : 'Require Face ID'}
        </span>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => choose(true)}
          className={optionClass(enabled)}
          data-testid="biometric-lock-on-btn"
        >
          {lang === 'es' ? 'Sí' : 'On'}
        </button>
        <button
          onClick={() => choose(false)}
          className={optionClass(!enabled)}
          data-testid="biometric-lock-off-btn"
        >
          {lang === 'es' ? 'No' : 'Off'}
        </button>
      </div>
    </div>
  );
};

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <ArvexisSymbolBreathing size={80} />
        </motion.div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // The privacy curtain sits HERE — inside the authenticated boundary, above every member screen.
  // Rendering the lock INSTEAD of children means no dream, message or reflection is ever mounted or
  // fetched before the member has unlocked. A blur or overlay would still mount them underneath.
  return <BiometricGate>{children}</BiometricGate>;
};

/**
 * ARVEXIS LOCKED — a local privacy gate in front of a session the server has already approved.
 *
 * Face ID (or the iPhone's own passcode) is asked for by iOS, which tells us only yes or no. A
 * successful unlock draws the curtain back; it does NOT create, refresh, extend or replace the
 * session. If the session has genuinely expired, ProtectedRoute has already sent the member to
 * Welcome Back and this screen never appears — the session question is answered first, always.
 */
const BiometricGate = ({ children }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const lockEnabled = isBiometricLockEnabled();
  // Cold launch with the lock on always asks.
  const [locked, setLocked] = useState(() => requiresUnlock({ enabled: lockEnabled, coldLaunch: true }));
  const [outcome, setOutcome] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const unlockedThisSession = useRef(!requiresUnlock({ enabled: lockEnabled, coldLaunch: true }));
  
  // FOREGROUND-RETURN POLICY. Leaving stamps the time; coming back asks only after a real absence,
  // so glancing at a notification for three seconds never costs a Face ID prompt.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        markActive();
        return;
      }
      setLocked((wasLocked) => wasLocked || requiresUnlock({
        enabled: isBiometricLockEnabled(),
        unlockedThisSession: unlockedThisSession.current,
        lastActive: lastActiveAt()
      }));
    };
    const onPageHide = () => markActive();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);
  
  const attemptUnlock = async () => {
    setVerifying(true);
    // allowDeviceCredential hands the fallback to Apple: if Face ID fails, is unavailable, or
    // attempts are exceeded, iOS presents its own passcode sheet. Arvexis never creates, requests,
    // stores or manages a PIN of its own.
    const result = await runLocalUnlock({ reason: 'Unlock Arvexis' });
    setVerifying(false);
    if (result.ok) {
      unlockedThisSession.current = true;
      setOutcome(null);
      setLocked(false);
      return;
    }
    // Cancellation, a wrong face, a lockout — all keep Arvexis locked. None is ever a bypass.
    setOutcome(result.outcome);
  };
  
  const handleSignOut = async () => {
    await logout();
    navigate('/', { replace: true });
  };
  
  if (!locked) return children;
  
  // Only when the device can verify nobody at all — no biometry AND no passcode — is a way in
  // offered, and it is the member's own Arvexis password: stronger than Face ID, not weaker, and
  // it stores nothing new.
  const noDeviceSecurity = outcome === 'no-device-security';
  const message = noDeviceSecurity
    ? 'This iPhone has no Face ID or passcode set, so it cannot verify you. Sign in to continue.'
    : outcome === 'cancelled' ? 'Arvexis stays locked until you unlock it.'
    : outcome === 'locked-out' ? 'Too many attempts. Your iPhone will ask for its passcode instead.'
    : outcome === 'unavailable' ? 'Face ID is unavailable right now. Your iPhone will ask for its passcode instead.'
    : outcome === 'failed' ? 'That did not match. Try again.'
    : null;
  
  return (
    <div
      className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center px-6"
      data-testid="biometric-lock-screen"
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center text-center max-w-sm w-full"
      >
        <ArvexisSymbolBreathing size={88} />
        
        <h1
          className="mt-8 text-2xl font-light tracking-wide text-[#F4EFE7]"
          data-testid="biometric-lock-title"
        >
          Arvexis Locked
        </h1>
        <p className="mt-3 text-sm text-[#F4EFE7]/56">
          A private place to be yourself.
        </p>
        
        {message && (
          <p className="mt-6 text-sm text-[#E8C58F]/80" data-testid="biometric-lock-message">
            {message}
          </p>
        )}
        
        {!noDeviceSecurity && (
          <button
            onClick={attemptUnlock}
            disabled={verifying}
            className="mt-8 w-full min-h-[52px] rounded-full bg-[#E8C58F] text-[#0F172A] font-medium tracking-wide transition-opacity disabled:opacity-60"
            data-testid="biometric-unlock-btn"
          >
            {verifying ? 'Verifying…' : 'Unlock Arvexis'}
          </button>
        )}
        
        {noDeviceSecurity && (
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="mt-8 w-full min-h-[52px] rounded-full bg-[#E8C58F] text-[#0F172A] font-medium tracking-wide"
            data-testid="biometric-sign-in-btn"
          >
            Sign In
          </button>
        )}
        
        <button
          onClick={handleSignOut}
          className="mt-4 min-h-[44px] text-sm text-[#F4EFE7]/56 hover:text-[#F4EFE7]/80 transition-colors"
          data-testid="biometric-sign-out-btn"
        >
          Sign Out
        </button>
      </motion.div>
    </div>
  );
};

/**
 * THE FRONT DOOR. `/` used to render acquisition content to everyone, including members who were
 * already signed in — which is how an established member could reopen Arvexis and be greeted as a
 * stranger. The landing page is for people who have not arrived yet.
 */
const LandingGate = ({ lang, setLang }) => {
  const { isAuthenticated, isLoading, launchState } = useAuth();
  
  // Say nothing until the session question has an answer. Showing either the landing page or a
  // sign-in screen first would be a guess, and the member would watch it change under them.
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <ArvexisSymbolBreathing size={80} />
        </motion.div>
      </div>
    );
  }
  
  // A member with a live session never sees the front door. `replace` so Back cannot land them
  // back outside their own account.
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  
  // Signed in on this device before: greet them by sending them to sign in ("Welcome back"),
  // not to a pitch for something they already have. `connection-unknown` lands here too — we
  // could not reach the server, which is not grounds for treating a member as a stranger.
  if (launchState === 'session-expired' || launchState === 'connection-unknown') {
    return <Navigate to="/login" replace />;
  }
  
  // Genuinely new, or explicitly signed out: the public welcome is the right first impression.
  return <LandingPage lang={lang} setLang={setLang} />;
};

// Account-state notice: authenticated but unverified → features stay locked until verification
const UnverifiedEmailNotice = ({ user, lang }) => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  if (!user || user.email_verified !== false) return null;

  const resend = async () => {
    if (sending || sent) return;
    setSending(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/resend-verification`, { email: user.email });
    } catch (err) {
      // Backend always answers generically; surface the same "sent" state either way
    } finally {
      setSending(false);
      setSent(true);
    }
  };

  return (
    <div
      className="fixed left-0 right-0 z-30 flex justify-center px-5"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 74px)' }}
      data-testid="unverified-email-notice"
    >
      <div
        className="w-full max-w-md rounded-xl px-4 py-3 flex items-center justify-between gap-3"
        style={{ background: 'rgba(13,15,24,0.82)', border: '1px solid rgba(230,192,110,0.35)', backdropFilter: 'blur(12px)' }}
      >
        <p className="text-sm text-[#E5E7EB] leading-snug">
          {lang === 'es'
            ? 'Verifica tu correo para desbloquear Arvexis.'
            : 'Verify your email to unlock Arvexis.'}
          <span className="block text-xs text-[#9CA3AF] mt-0.5">
            {sent
              ? (lang === 'es' ? 'Enlace enviado. Revisa tu bandeja de entrada.' : 'Link sent. Check your inbox.')
              : (lang === 'es' ? 'Revisa tu bandeja de entrada o pide un enlace nuevo.' : 'Check your inbox, or request a new link.')}
          </span>
        </p>
        {!sent && (
          <button
            onClick={resend}
            disabled={sending}
            className="shrink-0 text-xs font-semibold text-[#E6C06E] hover:text-[#F0D08A] transition-colors disabled:opacity-50"
            data-testid="unverified-email-resend-btn"
          >
            {sending ? (lang === 'es' ? 'Enviando…' : 'Sending…') : (lang === 'es' ? 'Reenviar' : 'Resend')}
          </button>
        )}
      </div>
    </div>
  );
};

// Social Sign-In Button Component
const SocialSignInButton = ({ provider, lang, t }) => {
  const { loginWithGoogleCredential } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);

  // Oct 25: verified Google sign-in. The Google ID token is checked on the server.
  const handleGoogleCredential = async (credential) => {
    setIsLoading(true);
    setGoogleError(null);
    try {
      await loginWithGoogleCredential(credential);
      window.location.href = '/home';
    } catch (err) {
      setIsLoading(false);
      setGoogleError(lang === 'es' ? 'No pudimos verificar tu inicio de sesión con Google. Inténtalo de nuevo.'
        : "We couldn't verify your Google sign-in. Please try again.");
    }
  };
  const handleGoogleError = () => setGoogleError(lang === 'es' ? 'El inicio de sesión con Google no se completó.'
    : "Google sign-in didn't complete.");
  
  const handleAppleSignIn = async () => {
    setIsLoading(true);
    try {
      // Apple Sign-In via Apple JS SDK
      // Load Apple JS SDK if not already loaded
      if (!window.AppleID) {
        const script = document.createElement('script');
        script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.async = true;
        script.onload = () => initAppleSignIn();
        document.head.appendChild(script);
      } else {
        initAppleSignIn();
      }
    } catch (err) {
      console.error('Apple sign-in error:', err);
      setIsLoading(false);
    }
  };
  
  const initAppleSignIn = () => {
    try {
      window.AppleID.auth.init({
        clientId: 'com.arvexisapp.web', // Apple Service ID
        scope: 'name email',
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        usePopup: true
      });
      
      window.AppleID.auth.signIn().then(async (response) => {
        // Exchange the authorization with backend
        try {
          const result = await axios.post(`${API}/auth/apple/callback`, {
            id_token: response.authorization.id_token,
            user_info: response.user // Only present on first auth
          });
          
          // Set user and redirect
          window.location.href = '/home';
        } catch (err) {
          console.error('Apple auth backend error:', err);
          setIsLoading(false);
        }
      }).catch((err) => {
        console.error('Apple sign-in cancelled or failed:', err);
        setIsLoading(false);
      });
    } catch (err) {
      console.error('Apple SDK init error:', err);
      setIsLoading(false);
    }
  };
  
  const isGoogle = provider === 'google';
  const isApple = provider === 'apple';

  const renderButton = (onClick) => (
    <button
      onClick={onClick}
      disabled={isLoading}
      className="w-full py-3.5 px-4 rounded-xl flex items-center justify-center gap-3 transition-all duration-200"
      style={{ 
        backgroundColor: isApple ? '#000000' : 'rgba(255,255,255,0.05)',
        border: `1px solid ${isApple ? '#333' : 'rgba(255,255,255,0.08)'}`,
        color: 'rgba(255,255,255,0.9)'
      }}
      data-testid={`social-signin-${provider}`}
    >
      {isLoading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <>
          {isGoogle && (
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          {isApple && (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
          )}
          <span className="font-medium text-sm">
            {isGoogle ? t.continueWithGoogle : t.continueWithApple}
          </span>
        </>
      )}
    </button>
  );

  if (!isGoogle) return renderButton(handleAppleSignIn);
  return (
    <div className="w-full">
      {isLoading ? renderButton(undefined) : (
        <GoogleCredentialButton onCredential={handleGoogleCredential} onError={handleGoogleError} lang={lang}
          testId="social-signin-google-verified" renderNative={(go) => renderButton(go)} />
      )}
      {googleError && <p data-testid="social-signin-google-error" className="mt-2 text-xs text-center" style={{ color: '#F2A7A7' }}>{googleError}</p>}
    </div>
  );
};

// Login Screen
// Public escape hatch — an explicit, always-visible route back to the public Landing so a visitor
// is never trapped between the auth screens or dependent on browser back.
const PublicHomeLink = ({ t, testId }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate('/')}
      className="inline-flex items-center gap-2 text-[13px] text-white/55 hover:text-white/85 transition-colors"
      data-testid={testId}
    >
      <ArrowLeft className="w-4 h-4" />
      <span>{t.arvexisHome}</span>
    </button>
  );
};

const LoginScreen = ({ lang, t, setLang }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState(location.state?.email || '');
  const [notice] = useState(location.state?.message || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendState, setResendState] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const resendVerification = async () => {
    setResendState('sending');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: unverifiedEmail });
      setResendState('sent');
    } catch (err) {
      setResendState(err.response?.status === 429 ? 'rate_limited' : 'failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const userData = await login(email, password);
      // Set app language to user's saved preference
      if (setLang && userData?.preferred_language) {
        setLang(userData.preferred_language);
      }
      navigate('/home');
    } catch (err) {
      const status = err.response?.status;
      const raw = err.response?.data?.detail;
      const detail = typeof raw === 'string' ? raw : (raw?.message || '');

      // Email-ownership gate: not a credential failure. Offer the recovery path.
      if (status === 403 && raw?.code === 'email_not_verified') {
        setUnverifiedEmail(raw.email || email);
        setError(raw.message);
        return;
      }

      if (status === 429) {
        setError(t.tooManyAttempts);
      } else if (status === 401 && detail.toLowerCase().includes('deleted')) {
        setError(t.accountDeleted);
      } else if (status === 401) {
        setError(t.invalidCredentials);
      } else if (!err.response) {
        setError(t.networkError);
      } else {
        setError(detail || t.invalidCredentials);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      testId="login-screen"
      header={<div data-testid="login-public-header"><PublicHomeLink t={t} testId="login-home-btn" /></div>}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full"
      >
          <div className="text-center mb-10">
            <h1 
              className="text-2xl font-semibold mb-2"
              style={{ color: 'rgba(255,255,255,0.95)', fontFamily: 'var(--font-display)' }}
            >
              {t.welcomeBack}
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.50)', fontSize: '14px' }}>
              {t.signInSubtitle}
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px', display: 'block', marginBottom: '8px' }}>{t.email}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'rgba(255,255,255,0.35)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-input"
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  data-testid="login-email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px', display: 'block', marginBottom: '8px' }}>{t.password}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'rgba(255,255,255,0.35)' }} />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth-input pr-12"
                  placeholder="••••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {/* Forgot Password Link */}
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm transition-colors"
                  style={{ color: '#C9A054' }}
                  data-testid="forgot-password-link"
                >
                  {t.forgotPassword}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {notice && !error && (
              <div className="text-sm text-emerald-300/80 text-center" data-testid="login-notice">
                {notice}
              </div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="auth-error"
                data-testid="login-error"
              >
                {error}
              </motion.div>
            )}

            {/* Email-ownership gate: recovery path, not a credential error */}
            {unverifiedEmail && (
              <div className="mt-3 text-center" data-testid="unverified-recovery">
                {resendState === 'sent' ? (
                  <p className="text-sm text-emerald-300/80" data-testid="resend-sent">
                    {lang === 'es'
                      ? 'Enviado. Revisa tu correo, incluida la carpeta de spam.'
                      : 'Sent. Check your inbox, including spam.'}
                  </p>
                ) : resendState === 'rate_limited' ? (
                  <p className="text-sm text-amber-300/80" data-testid="resend-rate-limited">
                    {lang === 'es'
                      ? 'Demasiados intentos. Espera unos minutos.'
                      : 'Too many attempts. Please wait a few minutes.'}
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={resendVerification}
                    disabled={resendState === 'sending'}
                    className="text-sm text-amber-400/80 hover:text-amber-400 underline disabled:opacity-50"
                    data-testid="resend-verification-btn"
                  >
                    {resendState === 'sending'
                      ? (lang === 'es' ? 'Enviando…' : 'Sending…')
                      : (lang === 'es' ? 'Enviar el enlace de nuevo' : 'Send the link again')}
                  </button>
                )}
                <ChangePendingEmail
                  currentEmail={unverifiedEmail}
                  lang={lang}
                  onChanged={(next) => {
                    setUnverifiedEmail(next);
                    setEmail(next);
                    setResendState('');
                    setError('');
                  }}
                />
              </div>
            )}


            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-4 text-base"
              data-testid="login-submit"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t.signingIn}
                </span>
              ) : t.signIn}
            </button>
          </form>

          {/* Social Sign-In Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>{t.orContinueWith}</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          </div>

          {/* Social Sign-In Buttons */}
          <div className="flex flex-col gap-3">
            <SocialSignInButton provider="google" lang={lang} t={t} />
            <SocialSignInButton provider="apple" lang={lang} t={t} />
          </div>

          {/* Register Link */}
          <p className="text-center mt-8 text-sm" style={{ color: 'rgba(255,255,255,0.50)' }}>
            {t.noAccount}{' '}
            <button
              onClick={() => navigate('/register')}
              className="font-medium"
              style={{ color: '#C9A054' }}
              data-testid="go-to-register"
            >
              {t.createOne}
            </button>
          </p>

          {/* Trust Indicator - Simplified */}
          <div className="mt-10 flex flex-col items-center gap-1">
            <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.40)' }}>
              <Shield className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.30)' }} />
              <span>{t.privacyNote}</span>
            </div>
          </div>
      </motion.div>
    </AuthShell>
  );
};

// Register Screen
const RegisterScreen = ({ lang, t, setLang }) => {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ageAffirmed, setAgeAffirmed] = useState(false);
  // Marketing consent: optional, unchecked, and never required to create an account.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  // Language continuity: open in the visitor's explicit language (Landing choice / stored preference).
  const [selectedLanguage, setSelectedLanguage] = useState(lang === 'es' ? 'es' : 'en');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ valid: false, checks: [] });
  
  // Use translations based on selectedLanguage, not app-wide lang
  const formT = translations[selectedLanguage];

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  // Password strength checker
  const [awaitingVerification, setAwaitingVerification] = useState(null);
  const [regResendState, setRegResendState] = useState('');

  const resendActivation = async () => {
    setRegResendState('sending');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setRegResendState('sent');
    } catch (err) {
      setRegResendState(err.response?.status === 429 ? 'rate_limited' : 'failed');
    }
  };

  useEffect(() => {
    const checks = [
      { label: '10+ characters', valid: password.length >= 10 },
      { label: 'Uppercase letter', valid: /[A-Z]/.test(password) },
      { label: 'Lowercase letter', valid: /[a-z]/.test(password) },
      { label: 'Number', valid: /\d/.test(password) },
    ];
    setPasswordStrength({
      valid: checks.every(c => c.valid),
      checks
    });
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate name is required
    if (!name || !name.trim()) {
      setError(formT.nameRequired);
      return;
    }

    // Validate password match
    if (password !== confirmPassword) {
      setError(formT.passwordMismatch);
      return;
    }

    // Validate password strength
    if (!passwordStrength.valid) {
      setError(formT.weakPassword);
      return;
    }

    // Adults only. The member must make the affirmation themselves; the server refuses too.
    if (!ageAffirmed) {
      setError(formT.ageRequired);
      return;
    }

    setIsLoading(true);

    try {
      const created = await register(email, password, name.trim(), selectedLanguage, ageAffirmed,
                                    marketingOptIn);
      // Set the app language to match user's selection
      if (setLang) setLang(selectedLanguage);
      if (created?.activation_required) {
        setAwaitingVerification(created);
        return;
      }
      navigate('/home');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      
      if (status === 400 && detail.includes('being deleted')) {
        setError(formT.emailPendingDeletion);
      } else if (status === 400 && detail.includes('already registered')) {
        setError(formT.emailInUse);
      } else if (status === 422) {
        setError(formT.weakPassword);
      } else if (!err.response) {
        setError(formT.networkError);
      } else {
        setError(detail || formT.networkError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Email-ownership gate: registration succeeded, but access waits on confirmation.
  if (awaitingVerification) {
    return (
      <AuthShell testId="activation-required-screen">
        <div className="text-center">
          <h1
            className="text-2xl mb-4"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: 'rgba(255,255,255,0.92)' }}
            data-testid="activation-title"
          >
            {selectedLanguage === 'es' ? 'Confirma tu correo' : 'Confirm your email'}
          </h1>
          <p
            className="text-sm leading-relaxed mb-2"
            style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.55)' }}
            data-testid="activation-message"
          >
            {selectedLanguage === 'es'
              ? `Enviamos un enlace a ${email}. Ábrelo para terminar de crear tu cuenta.`
              : `We sent a link to ${email}. Open it to finish setting up your account.`}
          </p>
          <p className="text-xs mb-8" style={{ color: 'rgba(255,255,255,0.35)' }}>
            {selectedLanguage === 'es'
              ? 'Revisa también la carpeta de spam.'
              : 'Check your spam folder too.'}
          </p>

          {regResendState === 'sent' ? (
            <p className="text-sm text-emerald-300/80" data-testid="activation-resend-sent">
              {selectedLanguage === 'es' ? 'Enviado de nuevo.' : 'Sent again.'}
            </p>
          ) : regResendState === 'rate_limited' ? (
            <p className="text-sm text-amber-300/80" data-testid="activation-resend-limited">
              {selectedLanguage === 'es'
                ? 'Demasiados intentos. Espera unos minutos.'
                : 'Too many attempts. Please wait a few minutes.'}
            </p>
          ) : (
            <button
              type="button"
              onClick={resendActivation}
              disabled={regResendState === 'sending'}
              className="text-sm text-amber-400/80 hover:text-amber-400 underline disabled:opacity-50"
              data-testid="activation-resend-btn"
            >
              {regResendState === 'sending'
                ? (selectedLanguage === 'es' ? 'Enviando…' : 'Sending…')
                : (selectedLanguage === 'es' ? 'Enviar el enlace de nuevo' : 'Send the link again')}
            </button>
          )}

          <ChangePendingEmail
            currentEmail={email}
            lang={selectedLanguage}
            onChanged={(next) => {
              setEmail(next);
              setRegResendState('');
              setAwaitingVerification({ ...awaitingVerification, email: next });
            }}
          />

          <div className="mt-8">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-white/40 hover:text-white/60"
              data-testid="activation-back-to-login"
            >
              {selectedLanguage === 'es' ? 'Ir a iniciar sesión' : 'Go to sign in'}
            </button>
          </div>
        </div>
      </AuthShell>
    );
  }


  return (
    <AuthShell
      testId="register-screen"
      header={<div data-testid="register-public-header"><PublicHomeLink t={t} testId="register-home-btn" /></div>}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full"
      >
          <div className="text-center mb-10">
            <h1 className="text-2xl font-semibold text-[#E5E7EB] mb-2">
              {formT.createAccount}
            </h1>
            <p className="text-[#9CA3AF] text-sm">
              {formT.createAccountSubtitle}
            </p>
          </div>

          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name (Required) */}
            <div>
              <label className="block text-[#9CA3AF] text-sm mb-2">{formT.name}</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="auth-input"
                  placeholder={selectedLanguage === 'es' ? 'Tu nombre' : 'Your name'}
                  required
                  autoComplete="name"
                  data-testid="register-name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-[#9CA3AF] text-sm mb-2">{formT.email}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-input"
                  placeholder={selectedLanguage === 'es' ? 'tu@ejemplo.com' : 'you@example.com'}
                  required
                  autoComplete="email"
                  data-testid="register-email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-[#9CA3AF] text-sm mb-2">{formT.password}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth-input pr-12"
                  placeholder="••••••••••"
                  required
                  autoComplete="new-password"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#9CA3AF] transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password Requirements - Always Visible */}
              <div className="mt-3 p-3 bg-[#111827] rounded-lg border border-[#1F2937]">
                <p className="text-[#9CA3AF] text-xs mb-2 font-medium">{formT.passwordMustContain}</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <Check className={`w-3 h-3 flex-shrink-0 ${password.length >= 10 ? 'text-[#D4A843]' : 'text-[#4B5563]'}`} />
                    <span className={password.length >= 10 ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}>
                      {formT.passwordReq10Chars}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Check className={`w-3 h-3 flex-shrink-0 ${/[A-Z]/.test(password) ? 'text-[#D4A843]' : 'text-[#4B5563]'}`} />
                    <span className={/[A-Z]/.test(password) ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}>
                      {formT.passwordReqUppercase}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Check className={`w-3 h-3 flex-shrink-0 ${/[a-z]/.test(password) ? 'text-[#D4A843]' : 'text-[#4B5563]'}`} />
                    <span className={/[a-z]/.test(password) ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}>
                      {formT.passwordReqLowercase}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Check className={`w-3 h-3 flex-shrink-0 ${/\d/.test(password) ? 'text-[#D4A843]' : 'text-[#4B5563]'}`} />
                    <span className={/\d/.test(password) ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}>
                      {formT.passwordReqNumber}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-[#9CA3AF] text-sm mb-2">{formT.confirmPassword}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="auth-input"
                  placeholder="••••••••••"
                  required
                  autoComplete="new-password"
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            {/* Language Selection - Explicit choice, not inferred */}
            <div className="mt-6 pt-5 border-t border-white/10">
              <label className="block text-sm text-[#9CA3AF] mb-3">
                {selectedLanguage === 'es' ? 'Idioma preferido' : 'Preferred Language'}
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedLanguage('en')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-200 ${
                    selectedLanguage === 'en'
                      ? 'bg-[#D4A843]/20 border-2 border-[#D4A843] text-[#D4A843]'
                      : 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:bg-white/10'
                  }`}
                  data-testid="register-lang-en"
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedLanguage('es')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-200 ${
                    selectedLanguage === 'es'
                      ? 'bg-[#D4A843]/20 border-2 border-[#D4A843] text-[#D4A843]'
                      : 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:bg-white/10'
                  }`}
                  data-testid="register-lang-es"
                >
                  Español
                </button>
              </div>
            </div>

            {/* Adults only. Unchecked by default; the server refuses registration without it. */}
            <div className="mt-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ageAffirmed}
                  onChange={(e) => setAgeAffirmed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-[#D4A843] cursor-pointer"
                  data-testid="register-age-affirm"
                />
                <span className="text-sm text-[#9CA3AF] leading-snug">{formT.ageAffirm}</span>
              </label>
            </div>

            {/* Marketing consent. Entirely optional, unchecked by default, not required to
                create an account, and separate from both the 18+ affirmation above and the
                Terms/Privacy acceptance below. Nothing here is preselected. */}
            <div className="mt-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-[#D4A843] cursor-pointer"
                  data-testid="register-marketing-opt-in"
                />
                <span className="text-sm text-[#9CA3AF] leading-snug">{formT.marketingOptIn}</span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="auth-error"
                data-testid="register-error"
              >
                {error}
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-4 text-base mt-2"
              data-testid="register-submit"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {formT.signingUp}
                </span>
              ) : formT.signUp}
            </button>
          </form>

          {/* Legal line */}
          <p
            className="text-center mt-4 text-[#6B7280] text-xs leading-relaxed"
            data-testid="register-legal-line"
          >
            {formT.legalAgreePrefix}
            <a
              href="/terms"
              className="text-[#D4A843] hover:text-[#E8C050] underline underline-offset-2"
              data-testid="register-terms-link"
            >
              {formT.terms}
            </a>
            {formT.legalAgreeMiddle}
            <a
              href="/privacy"
              className="text-[#D4A843] hover:text-[#E8C050] underline underline-offset-2"
              data-testid="register-privacy-link"
            >
              {formT.privacyPolicy}
            </a>
            {formT.legalAgreeSuffix}
          </p>

          {/* Login Link */}
          <p className="text-center mt-6 text-[#9CA3AF] text-sm">
            {formT.hasAccount}{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-[#D4A843] hover:text-[#E8C050] font-medium"
              data-testid="go-to-login"
            >
              {formT.signInInstead}
            </button>
          </p>

          {/* Trust Indicators */}
          <div className="mt-8 space-y-3">
            <div className="flex items-center gap-3 text-[#6B7280] text-xs">
              <Shield className="w-4 h-4 text-[#D4A843]" />
              <span>{formT.signUpNote}</span>
            </div>
          </div>
      </motion.div>
    </AuthShell>
  );
};

// Forgot Password Screen
const ForgotPasswordScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { email });
      setIsSubmitted(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t.tooManyAttempts);
      } else {
        // Always show success to prevent email enumeration
        setIsSubmitted(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      testId="forgot-password-screen"
      header={
        <button
          onClick={() => navigate('/login')}
          className="btn-circle"
          style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.12)' }}
          data-testid="forgot-back-btn"
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.8)' }} />
        </button>
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full"
      >
          <div className="text-center mb-10">
            <h1 className="text-2xl font-semibold text-[#E5E7EB] mb-2">
              {t.forgotPasswordTitle}
            </h1>
            <p className="text-[#9CA3AF] text-sm">
              {t.forgotPasswordSubtitle}
            </p>
          </div>

          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-500/10 border border-red-500/20 rounded-lg p-4"
                >
                  <p className="text-red-400 text-sm text-center">{error}</p>
                </motion.div>
              )}
              
              {/* Email */}
              <div>
                <label className="block text-[#9CA3AF] text-sm mb-2">{t.email}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-input"
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                    disabled={isLoading}
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full py-4 text-base disabled:opacity-50"
                data-testid="forgot-password-submit"
              >
                {isLoading ? t.sending : t.sendResetLink}
              </button>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#D4A843]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[#D4A843]" />
              </div>
              <h2 className="text-lg font-medium text-[#E5E7EB] mb-2">
                {t.checkYourEmail}
              </h2>
              <p className="text-[#9CA3AF] text-sm mb-6">
                {t.resetLinkSent}
              </p>
            </motion.div>
          )}

          {/* Back to Login Link */}
          <p className="text-center mt-8 text-[#9CA3AF] text-sm">
            <button
              onClick={() => navigate('/login')}
              className="text-[#D4A843] hover:text-[#E8C050] font-medium flex items-center gap-1 mx-auto"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backToLogin}
            </button>
          </p>
      </motion.div>
    </AuthShell>
  );
};

// Reset Password Screen
const ResetPasswordScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('form'); // 'form', 'success', 'expired', 'invalid'
  // Non-sensitive correlation ID so one tap can be matched across frontend telemetry and backend ingress
  const correlationIdRef = useRef(`r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
  const sendResetDiagnostic = async (stage, errorCategory, errorMessage, metadata = {}) => {
    // Channel 2: GET image beacon — reaches the backend even from page contexts where POSTs are blocked
    try {
      const q = new URLSearchParams({
        event_type: 'password_reset', stage, cid: correlationIdRef.current,
        ...(errorCategory ? { error_category: errorCategory } : {}),
        ...(errorMessage ? { error_message: String(errorMessage).substring(0, 300) } : {}),
        meta: JSON.stringify({ ...metadata, has_token: !!token, token_length: token?.length || 0, referrer: (document.referrer || '').substring(0, 120), standalone: !!window.navigator.standalone, page_origin: window.location.origin, api_base: API_BASE_URL }).substring(0, 1400)
      });
      new window.Image().src = `${API_BASE_URL}/api/auth/diagnostic/beacon?${q.toString()}&_=${Date.now()}`;
    } catch (beaconErr) { /* best effort */ }
    // Channel 1: POST
    try {
      await axios.post(`${API_BASE_URL}/api/auth/diagnostic`, {
        event_type: 'password_reset',
        stage,
        error_category: errorCategory,
        error_message: errorMessage?.substring(0, 500),
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        timestamp: new Date().toISOString(),
        metadata: { ...metadata, correlation_id: correlationIdRef.current, url: `${window.location.origin}${window.location.pathname}`, has_token: !!token, token_length: token?.length || 0 }
      });
    } catch (diagErr) {
      console.error('[Diagnostic] Failed to send:', diagErr.message);
    }
  };
  useEffect(() => { sendResetDiagnostic('init', token ? null : 'missing_token', null); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  // Handle no token
  useEffect(() => {
    if (!token) {
      setStatus('invalid');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }
    
    // Validate password strength
    const hasMinLength = password.length >= 10;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    
    if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber) {
      setError(t.weakPassword);
      return;
    }
    
    setIsLoading(true);
    
    const MAX_ATTEMPTS = 3; // bounded retry for transient no-response / 5xx (stale connection, cold start)
    const submit = async (attempt) => {
      const startTime = Date.now();
      await sendResetDiagnostic('request_start', null, null, { attempt });
      try {
        await axios.post(`${API_BASE_URL}/api/auth/reset-password`, {
          token,
          password,
          correlation_id: correlationIdRef.current
        }, { timeout: 20000 });
        await sendResetDiagnostic('success', null, null, { attempt, duration_ms: Date.now() - startTime });
        setStatus('success');
      } catch (err) {
        const errorCategory = err.response ? `http_${err.response.status}` : (err.code === 'ECONNABORTED' ? 'timeout' : 'no_response');
        const respHeaders = err.response?.headers || {};
        const respBody = typeof err.response?.data === 'string' ? err.response.data : JSON.stringify(err.response?.data || '');
        await sendResetDiagnostic('request_failed', errorCategory, err.message, {
          attempt,
          duration_ms: Date.now() - startTime,
          has_response: !!err.response,
          response_status: err.response?.status,
          response_detail: (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response?.data?.detail || ''))?.substring(0, 200),
          response_content_type: respHeaders['content-type'],
          response_server: respHeaders['server'],
          cf_mitigated: respHeaders['cf-mitigated'],
          cf_ray: respHeaders['cf-ray'],
          body_snippet: respBody.replace(/\s+/g, ' ').substring(0, 160),
          error_code: err.code
        });
        const isTransient = !err.response || err.response.status >= 500;
        if (isTransient && attempt < MAX_ATTEMPTS) {
          await sendResetDiagnostic('retry_scheduled', errorCategory, null, { attempt, next_attempt: attempt + 1 });
          await new Promise(r => setTimeout(r, 2000 * attempt));
          return submit(attempt + 1);
        }
        if (err.response?.status === 429) {
          setError(t.tooManyAttempts);
        } else if (err.response?.status === 422) {
          setError(t.weakPassword);
        } else if (err.response?.status === 400) {
          const detail = (err.response?.data?.detail || '').toLowerCase();
          if (detail.includes('expired')) {
            setStatus('expired');
          } else {
            setStatus('invalid');
          }
        } else {
          setError(t.networkError);
        }
      }
    };
    
    try {
      await submit(1);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell testId="reset-password-screen">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full"
      >
          {/* Form State */}
          {status === 'form' && (
            <>
              <h1 className="text-lg font-medium text-[#E5E7EB] text-center mb-8">
                {t.resetPasswordTitle}
              </h1>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-500/10 border border-red-500/20 rounded-lg p-3"
                  >
                    <p className="text-red-400 text-sm text-center">{error}</p>
                  </motion.div>
                )}

                {/* New Password */}
                <div>
                  <label className="block text-[#9CA3AF] text-sm mb-2">{t.newPassword}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-[#12101A] border border-white/10 rounded-lg text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#D4A843]/50"
                      placeholder="••••••••••"
                      required
                      autoComplete="new-password"
                      disabled={isLoading}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#9CA3AF] transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {/* Password Requirements - Quiet */}
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center gap-2 text-xs">
                      <Check className={`w-3 h-3 ${password.length >= 10 ? 'text-[#D4A843]' : 'text-[#4B5563]'}`} />
                      <span className={password.length >= 10 ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}>
                        {t.passwordReq10Chars}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Check className={`w-3 h-3 ${/[A-Z]/.test(password) ? 'text-[#D4A843]' : 'text-[#4B5563]'}`} />
                      <span className={/[A-Z]/.test(password) ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}>
                        {t.passwordReqUppercase}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Check className={`w-3 h-3 ${/[a-z]/.test(password) ? 'text-[#D4A843]' : 'text-[#4B5563]'}`} />
                      <span className={/[a-z]/.test(password) ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}>
                        {t.passwordReqLowercase}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Check className={`w-3 h-3 ${/\d/.test(password) ? 'text-[#D4A843]' : 'text-[#4B5563]'}`} />
                      <span className={/\d/.test(password) ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}>
                        {t.passwordReqNumber}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-[#9CA3AF] text-sm mb-2">{t.confirmNewPassword || t.confirmPassword}</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-[#12101A] border border-white/10 rounded-lg text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#D4A843]/50"
                    placeholder="••••••••••"
                    required
                    autoComplete="new-password"
                    disabled={isLoading}
                    data-testid="reset-confirm-password"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 bg-[#D4A843] hover:bg-[#C49A3D] disabled:opacity-50 text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
                  data-testid="reset-password-submit"
                >
                  {isLoading ? t.resetting : t.resetPassword}
                </button>
              </form>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center space-y-6">
              <div className="w-12 h-12 bg-[#D4A843]/10 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-6 h-6 text-[#D4A843]" />
              </div>
              <div className="space-y-2">
                <h1 className="text-lg font-medium text-[#E5E7EB]">
                  {t.passwordResetSuccess}
                </h1>
                <p className="text-[#9CA3AF] text-sm">
                  {t.passwordResetSuccessSubtitle}
                </p>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-[#D4A843] hover:bg-[#C49A3D] text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
                data-testid="go-to-login-after-reset"
              >
                {t.signIn}
              </button>
            </div>
          )}

          {/* Expired State */}
          {status === 'expired' && (
            <div className="text-center space-y-6">
              <div className="w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div className="space-y-2">
                <h1 className="text-lg font-medium text-[#E5E7EB]">
                  {t.resetLinkExpired}
                </h1>
              </div>
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full py-3 bg-[#D4A843] hover:bg-[#C49A3D] text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
                data-testid="request-new-reset-link"
              >
                {t.requestNewLink}
              </button>
            </div>
          )}

          {/* Invalid State */}
          {status === 'invalid' && (
            <div className="text-center space-y-6">
              <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <div className="space-y-2">
                <h1 className="text-lg font-medium text-[#E5E7EB]">
                  {t.resetLinkInvalid}
                </h1>
              </div>
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full py-3 bg-[#D4A843] hover:bg-[#C49A3D] text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
                data-testid="request-new-reset-link-invalid"
              >
                {t.requestNewLink}
              </button>
            </div>
          )}
      </motion.div>
    </AuthShell>
  );
};

// Email Verification Screen - Per V1 Spec
const EmailVerificationScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // States: verifying, success, expired, invalid, already_verified, network_error
  const [status, setStatus] = useState('verifying');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [diagnosticInfo, setDiagnosticInfo] = useState(null);
  const mountedRef = useRef(true);
  const verificationStartedRef = useRef(false);
  // Non-sensitive correlation ID so one Founder tap can be matched across frontend telemetry and backend ingress
  const correlationIdRef = useRef(`v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
  const safeUrl = `${window.location.origin}${window.location.pathname}`; // never log the token query param

  // Diagnostic telemetry helper - sends metadata to backend for iOS debugging
  const sendDiagnostic = async (stage, errorCategory, errorMessage, metadata = {}) => {
    // Channel 2: GET image beacon — reaches the backend even from page contexts where POSTs are blocked
    try {
      const q = new URLSearchParams({
        event_type: 'email_verification', stage, cid: correlationIdRef.current,
        ...(errorCategory ? { error_category: errorCategory } : {}),
        ...(errorMessage ? { error_message: String(errorMessage).substring(0, 300) } : {}),
        meta: JSON.stringify({ ...metadata, has_token: !!token, token_length: token?.length || 0, referrer: (document.referrer || '').substring(0, 120), standalone: !!window.navigator.standalone, page_origin: window.location.origin, api_base: API_BASE_URL }).substring(0, 1400)
      });
      new window.Image().src = `${API_BASE_URL}/api/auth/diagnostic/beacon?${q.toString()}&_=${Date.now()}`;
    } catch (beaconErr) { /* best effort */ }
    try {
      await axios.post(`${API_BASE_URL}/api/auth/diagnostic`, {
        event_type: 'email_verification',
        stage,
        error_category: errorCategory,
        error_message: errorMessage?.substring(0, 500),
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        timestamp: new Date().toISOString(),
        metadata: {
          ...metadata,
          correlation_id: correlationIdRef.current,
          url: safeUrl,
          api_base: API_BASE_URL,
          has_token: !!token,
          token_length: token?.length || 0
        }
      });
    } catch (diagErr) {
      // Diagnostic send failed - log locally only
      console.error('[Diagnostic] Failed to send:', diagErr.message);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    
    // Only start verification once, even across StrictMode remounts
    if (!verificationStartedRef.current && token) {
      verificationStartedRef.current = true;
      // Log diagnostic: verification attempt starting
      sendDiagnostic('init', null, null, { 
        screen: 'EmailVerificationScreen',
        token_present: true 
      });
      verifyEmail();
    } else if (!token) {
      setStatus('invalid');
      sendDiagnostic('init', 'missing_token', 'No token in URL params');
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [token]);

  const verifyEmail = async (attempt = 1) => {
    const startTime = Date.now();
    const MAX_ATTEMPTS = 3; // bounded retry for transient no-response / 5xx (e.g. backend cold start)
    
    try {
      // Log diagnostic: request starting
      await sendDiagnostic('request_start', null, null, {
        attempt,
        target_url: `${API_BASE_URL}/api/auth/verify-email`
      });
      
      const response = await axios.post(`${API_BASE_URL}/api/auth/verify-email`, {
        token,
        correlation_id: correlationIdRef.current
      }, { timeout: 20000 });
      
      // Log diagnostic: success
      await sendDiagnostic('success', null, null, {
        attempt,
        duration_ms: Date.now() - startTime,
        status: response.status
      });
      
      // Only update state if component is still mounted
      if (mountedRef.current) {
        setVerifiedEmail(response.data?.email || '');
        setStatus('success');
      }
    } catch (err) {
      const duration = Date.now() - startTime;
      
      // Capture detailed error info for diagnostics
      const errorInfo = {
        attempt,
        duration_ms: duration,
        has_response: !!err.response,
        response_status: err.response?.status,
        response_data: err.response?.data?.detail?.substring(0, 200),
        error_code: err.code,
        error_message: err.message?.substring(0, 200),
        is_network_error: !err.response,
        is_timeout: err.code === 'ECONNABORTED' || err.message?.includes('timeout'),
        is_cors: err.message?.includes('CORS') || err.message?.includes('cross-origin'),
        response_content_type: err.response?.headers?.['content-type'],
        response_server: err.response?.headers?.['server'],
        cf_mitigated: err.response?.headers?.['cf-mitigated'],
        cf_ray: err.response?.headers?.['cf-ray'],
        body_snippet: (typeof err.response?.data === 'string' ? err.response.data : JSON.stringify(err.response?.data || '')).replace(/\s+/g, ' ').substring(0, 160)
      };
      
      setDiagnosticInfo(errorInfo);
      
      // Determine error category
      let errorCategory = 'unknown';
      if (!err.response) {
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          errorCategory = 'timeout';
        } else if (err.message?.includes('Network')) {
          errorCategory = 'network';
        } else if (err.message?.includes('CORS') || err.message?.includes('cross-origin')) {
          errorCategory = 'cors';
        } else {
          errorCategory = 'no_response';
        }
      } else {
        errorCategory = `http_${err.response.status}`;
      }
      
      // Send diagnostic telemetry
      await sendDiagnostic('request_failed', errorCategory, err.message, errorInfo);
      
      // Only update state if component is still mounted
      if (!mountedRef.current) return;
      
      const isTransient = !err.response || err.response.status >= 500;
      if (isTransient && attempt < MAX_ATTEMPTS) {
        await sendDiagnostic('retry_scheduled', errorCategory, null, { attempt, next_attempt: attempt + 1 });
        await new Promise(r => setTimeout(r, 2000 * attempt));
        if (!mountedRef.current) return;
        return verifyEmail(attempt + 1);
      }
      
      if (err.response?.status === 400) {
        const detail = (err.response?.data?.detail || '').toLowerCase();
        
        if (detail.includes('expired')) {
          setStatus('expired');
        } else if (detail.includes('already verified')) {
          // Backend confirmed account IS verified
          setStatus('already_verified');
        } else if (detail.includes('already been used')) {
          // Token used but account NOT confirmed verified - check account state
          await checkAccountVerificationStatus();
        } else if (detail.includes('invalid')) {
          setStatus('invalid');
        } else {
          setStatus('invalid');
        }
      } else {
        // Network/server error - no response received
        setStatus('network_error');
      }
    }
  };
  
  // Check actual account verification status when token is already used
  const checkAccountVerificationStatus = async () => {
    try {
      // Try to resend - if account is already verified, backend will tell us
      // This is a workaround since we can't check email_verified directly
      const response = await axios.post(`${API_BASE_URL}/api/auth/resend-verification`, { 
        email: '' // Empty email will fail, but we're checking the error response
      });
      // If we get here, something unexpected happened
      setStatus('invalid');
    } catch (err) {
      // Any error means we should show invalid state
      // The user can enter their email to get a new link
      if (mountedRef.current) {
        setStatus('invalid');
      }
    }
  };

  const handleResendVerification = async () => {
    if (!resendEmail || resending) return;
    setResending(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/resend-verification`, { email: resendEmail });
      // Navigate to login with success message
      navigate('/login', { state: { message: 'Verification email sent. Check your inbox.' } });
    } catch (err) {
      // Still navigate, the backend always returns success for security
      navigate('/login', { state: { message: 'If the email exists, a verification link has been sent.' } });
    }
  };

  const handleContinue = () => {
    // One obvious path after ownership is proven: the normal sign-in form, with the confirmed
    // address prefilled. Never gate this on the legacy localStorage token — auth is cookie-based.
    navigate('/login', {
      state: {
        email: verifiedEmail,
        message: lang === 'es'
          ? 'Correo confirmado. Inicia sesión para continuar.'
          : 'Email confirmed. Sign in to continue.',
      },
    });
  };

  return (
    <AuthShell testId="verify-email-screen">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full text-center"
      >
          {/* A. Verification processing */}
          {status === 'verifying' && (
            <div className="space-y-6">
              <div className="w-10 h-10 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-[#E5E7EB] text-base">
                {t.verifyingEmail}
              </p>
            </div>
          )}

          {/* B. Verification successful */}
          {status === 'success' && (
            <div className="space-y-6">
              <div className="w-12 h-12 bg-[#D4A843]/10 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-6 h-6 text-[#D4A843]" />
              </div>
              <div className="space-y-2">
                <h1 className="text-lg font-medium text-[#E5E7EB]">
                  {t.emailVerifiedTitle}
                </h1>
                <p className="text-[#9CA3AF] text-sm">
                  {t.emailVerifiedSubtitle}
                </p>
              </div>
              <button
                onClick={handleContinue}
                className="w-full py-3 bg-[#D4A843] hover:bg-[#C49A3D] text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
                data-testid="continue-to-arvexis"
              >
                {t.continueToArvexis}
              </button>
            </div>
          )}

          {/* C. Link expired */}
          {status === 'expired' && (
            <div className="space-y-6">
              <div className="w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div className="space-y-2">
                <h1 className="text-lg font-medium text-[#E5E7EB]">
                  {t.verificationExpiredTitle}
                </h1>
                <p className="text-[#9CA3AF] text-sm">
                  {t.verificationExpiredSubtitle}
                </p>
              </div>
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-[#12101A] border border-white/10 rounded-lg text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#D4A843]/50"
                  data-testid="resend-email-input"
                />
                <button
                  onClick={handleResendVerification}
                  disabled={!resendEmail || resending}
                  className="w-full py-3 bg-[#D4A843] hover:bg-[#C49A3D] disabled:opacity-50 text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
                  data-testid="send-new-link"
                >
                  {resending ? '...' : t.sendNewLink}
                </button>
              </div>
            </div>
          )}

          {/* D. Already verified */}
          {status === 'already_verified' && (
            <div className="space-y-6">
              <div className="w-12 h-12 bg-[#D4A843]/10 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-6 h-6 text-[#D4A843]" />
              </div>
              <div className="space-y-2">
                <h1 className="text-lg font-medium text-[#E5E7EB]">
                  {t.verificationAlreadyDone}
                </h1>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-[#D4A843] hover:bg-[#C49A3D] text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
                data-testid="go-to-signin"
              >
                {t.signIn}
              </button>
            </div>
          )}

          {/* D. Invalid/used link */}
          {status === 'invalid' && (
            <div className="space-y-6">
              <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <div className="space-y-2">
                <h1 className="text-lg font-medium text-[#E5E7EB]">
                  {t.verificationFailedTitle}
                </h1>
                <p className="text-[#9CA3AF] text-sm">
                  {t.verificationLinkInvalid}
                </p>
              </div>
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-[#12101A] border border-white/10 rounded-lg text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#D4A843]/50"
                  data-testid="resend-email-input-invalid"
                />
                <button
                  onClick={handleResendVerification}
                  disabled={!resendEmail || resending}
                  className="w-full py-3 bg-[#D4A843] hover:bg-[#C49A3D] disabled:opacity-50 text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
                  data-testid="send-new-link-invalid"
                >
                  {resending ? '...' : t.sendNewLink}
                </button>
              </div>
            </div>
          )}

          {/* Network error */}
          {status === 'network_error' && (
            <div className="space-y-6">
              <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <div className="space-y-2">
                <h1 className="text-lg font-medium text-[#E5E7EB]">
                  {t.networkErrorTitle}
                </h1>
                <p className="text-[#9CA3AF] text-sm">
                  {t.networkErrorSubtitle}
                </p>
              </div>
              {/* Diagnostic info for debugging iOS failures */}
              {diagnosticInfo && (
                <div className="text-left p-3 bg-[#1a1625] rounded-lg border border-white/5 text-[10px] text-[#6B7280] space-y-1">
                  <p><span className="text-[#9CA3AF]">Category:</span> {diagnosticInfo.error_category || diagnosticInfo.is_network_error ? 'network' : 'unknown'}</p>
                  <p><span className="text-[#9CA3AF]">Duration:</span> {diagnosticInfo.duration_ms}ms</p>
                  {diagnosticInfo.error_code && <p><span className="text-[#9CA3AF]">Code:</span> {diagnosticInfo.error_code}</p>}
                  {diagnosticInfo.error_message && <p><span className="text-[#9CA3AF]">Message:</span> {diagnosticInfo.error_message}</p>}
                  <p><span className="text-[#9CA3AF]">Reported to server</span></p>
                </div>
              )}
              <button
                onClick={() => {
                  verificationStartedRef.current = false;
                  setStatus('verifying');
                  setDiagnosticInfo(null);
                  verifyEmail();
                }}
                className="w-full py-3 bg-[#D4A843] hover:bg-[#C49A3D] text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
                data-testid="try-again"
              >
                {t.tryAgain}
              </button>
            </div>
          )}
      </motion.div>
    </AuthShell>
  );
};

// Google OAuth Callback Handler
const GoogleAuthCallback = ({ lang, t }) => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      handleGoogleCallback(sessionId);
    } else {
      setStatus('error');
      setError('Authentication failed. Please try again.');
    }
  }, [searchParams]);
  
  const handleGoogleCallback = async (sessionId) => {
    try {
      await loginWithGoogle(sessionId);
      setStatus('success');
      // Redirect to home after brief success message
      setTimeout(() => navigate('/home'), 1500);
    } catch (err) {
      console.error('Google auth callback error:', err);
      setStatus('error');
      setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col">
      <div className="fixed inset-0 bg-gradient-to-b from-[#0F172A] via-[#0F172A] to-[#0D1117] pointer-events-none" />
      
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm text-center"
        >
          {/* Logo - Full Lockup per Brand Doctrine */}
          <div className="mb-10">
            <AuthLogo width={160} />
          </div>

          {status === 'processing' && (
            <>
              <div className="w-16 h-16 border-4 border-[#D4A843] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-semibold text-[#E5E7EB] mb-2">
                {lang === 'es' ? 'Completando inicio de sesión...' : 'Completing sign in...'}
              </h1>
              <p className="text-[#9CA3AF] text-sm">
                {lang === 'es' ? 'Por favor espera' : 'Please wait'}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-[#D4A843]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-[#D4A843]" />
              </div>
              <h1 className="text-2xl font-semibold text-[#E5E7EB] mb-2">
                {lang === 'es' ? '¡Bienvenido!' : 'Welcome!'}
              </h1>
              <p className="text-[#9CA3AF] text-sm">
                {lang === 'es' ? 'Redirigiendo a la aplicación...' : 'Redirecting to app...'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-semibold text-[#E5E7EB] mb-2">
                {lang === 'es' ? 'Error de autenticación' : 'Authentication Error'}
              </h1>
              <p className="text-[#9CA3AF] text-sm mb-8">
                {error}
              </p>
              <button
                onClick={() => navigate('/login')}
                className="btn-primary w-full py-4 text-base"
              >
                {t.backToLogin}
              </button>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

// Change Password Screen
const PasswordField = ({ label, value, onChange, visible, onToggleVisible, error, helper, testId, autoComplete }) => (
  <div className="pw-field">
    <label className="pw-label" htmlFor={testId}>{label}</label>
    <div style={{ position: 'relative' }}>
      <input
        id={testId}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="••••••••••"
        autoComplete={autoComplete}
        className={error ? 'pw-input pw-input--error' : 'pw-input'}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={onToggleVisible}
        className="pw-toggle"
        aria-label={visible ? 'Hide password' : 'Show password'}
        data-testid={`${testId}-toggle`}
      >
        {visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
      </button>
    </div>
    {helper && !error && <p className="pw-help" data-testid={`${testId}-helper`}>{helper}</p>}
    {error && (
      <p className="pw-error" data-testid={`${testId}-error`}>
        <AlertCircle />
        <span>{error}</span>
      </p>
    )}
  </div>
);

const ChangePasswordScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const goBack = useShellBack();
  const { changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Phase 6 — the three demonstrated validation states, each shown on the field it belongs to.
  const [fieldErrors, setFieldErrors] = useState({ current: '', next: '', confirm: '' });
  const [formError, setFormError] = useState('');

  const copy = {
    en: {
      eyebrow: 'SETTINGS',
      sub: 'Update your password to keep your account secure.',
      helper: 'Min 10 characters, 1 uppercase, 1 lowercase, 1 number',
      required: 'This field is required.',
      incorrect: 'The current password is incorrect.',
      weak: 'Password must be at least 10 characters and include an uppercase letter, a lowercase letter, and a number.',
      mismatch: 'Passwords do not match.',
      different: 'New password must be different from your current password.',
      failed: 'We could not update your password. Please try again.',
      update: 'Update Password',
      cancel: 'Cancel',
    },
    es: {
      eyebrow: 'CONFIGURACIÓN',
      sub: 'Actualiza tu contraseña para mantener tu cuenta segura.',
      helper: 'Mínimo 10 caracteres, 1 mayúscula, 1 minúscula, 1 número',
      required: 'Este campo es obligatorio.',
      incorrect: 'La contraseña actual es incorrecta.',
      weak: 'La contraseña debe tener al menos 10 caracteres e incluir una mayúscula, una minúscula y un número.',
      mismatch: 'Las contraseñas no coinciden.',
      different: 'La nueva contraseña debe ser diferente de la actual.',
      failed: 'No pudimos actualizar tu contraseña. Inténtalo de nuevo.',
      update: 'Actualizar Contraseña',
      cancel: 'Cancelar',
    },
  }[lang === 'es' ? 'es' : 'en'];

  const validatePassword = (password) => {
    return password.length >= 10 &&
           /[A-Z]/.test(password) &&
           /[a-z]/.test(password) &&
           /[0-9]/.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const next = { current: '', next: '', confirm: '' };
    if (!currentPassword) next.current = copy.required;
    if (!newPassword) next.next = copy.required;
    else if (!validatePassword(newPassword)) next.next = copy.weak;
    if (!confirmPassword) next.confirm = copy.required;
    else if (newPassword !== confirmPassword) next.confirm = copy.mismatch;

    if (next.current || next.next || next.confirm) {
      setFieldErrors(next);
      return;
    }
    setFieldErrors(next);
    setIsLoading(true);

    try {
      // The existing password-change contract is unchanged.
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('incorrect')) {
        setFieldErrors({ current: copy.incorrect, next: '', confirm: '' });
      } else if (detail.includes('different')) {
        setFieldErrors({ current: '', next: copy.different, confirm: '' });
      } else {
        setFormError(copy.failed);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex flex-col">
        <div className="fixed inset-0 bg-gradient-to-b from-[#0F172A] via-[#0F172A] to-[#0D1117] pointer-events-none" />

        <main className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-sm text-center"
          >
            <div className="mb-10">
              <ArvexisLockup width={LOCKUP_SIZES.MD} />
            </div>

            <div className="w-16 h-16 bg-[#D4A843]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#D4A843]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#E5E7EB] mb-2">
              {t.passwordChangedSuccess}
            </h1>
            <p className="text-[#9CA3AF] text-sm mb-8">
              {lang === 'es'
                ? 'Tu contraseña ha sido actualizada. Por favor inicia sesión con tu nueva contraseña.'
                : 'Your password has been updated. Please sign in with your new password.'}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn-primary w-full py-4 text-base"
              data-testid="go-to-login-after-change"
            >
              {t.signIn}
            </button>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen utility-surface utility-surface--atmospheric" data-testid="change-password-surface">
      {/* Header — mobile only. On desktop the sidebar carries navigation. */}
      <header
        className="flex items-center gap-4 px-5 account-mobile-header"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 47px) + 12px)',
          paddingBottom: '12px'
        }}
      >
        <button
          onClick={goBack}
          className="btn-circle"
          style={{ background: 'transparent', border: '1px solid rgba(232, 197, 143, 0.38)' }}
          data-testid="change-password-back-btn"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: 'rgba(232, 197, 143, 0.9)' }} />
        </button>
        <span
          className="account-wordmark"
          style={{ fontSize: '12px', letterSpacing: '0.42em', color: 'rgba(244,239,231,0.8)', margin: '0 auto', paddingRight: '44px' }}
          data-testid="change-password-wordmark"
        >
          ARVEXIS
        </span>
      </header>

      <div className="utility-head account-col" data-testid="change-password-head">
        <p className="utility-head__eyebrow">{copy.eyebrow}</p>
        <h1 className="utility-head__title">{t.changePassword}</h1>
        <p className="utility-head__sub">{copy.sub}</p>
      </div>

      <main className="account-col" style={{ padding: '10px 20px 48px' }} data-testid="change-password-main">
        <form onSubmit={handleSubmit} noValidate>
          <PasswordField
            label={t.currentPassword}
            value={currentPassword}
            onChange={(v) => setCurrentPassword(v)}
            visible={showCurrentPassword}
            onToggleVisible={() => setShowCurrentPassword(!showCurrentPassword)}
            error={fieldErrors.current}
            testId="current-password-input"
            autoComplete="current-password"
          />

          <PasswordField
            label={t.newPasswordLabel}
            value={newPassword}
            onChange={(v) => setNewPassword(v)}
            visible={showNewPassword}
            onToggleVisible={() => setShowNewPassword(!showNewPassword)}
            error={fieldErrors.next}
            helper={copy.helper}
            testId="new-password-input"
            autoComplete="new-password"
          />

          <PasswordField
            label={t.confirmNewPassword}
            value={confirmPassword}
            onChange={(v) => setConfirmPassword(v)}
            visible={showConfirmPassword}
            onToggleVisible={() => setShowConfirmPassword(!showConfirmPassword)}
            error={fieldErrors.confirm}
            testId="confirm-password-input"
            autoComplete="new-password"
          />

          {formError && (
            <p className="pw-error" data-testid="change-password-form-error">
              <AlertCircle />
              <span>{formError}</span>
            </p>
          )}

          <div className="pw-actions">
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
              style={{ height: '52px', borderRadius: '999px', fontSize: '15px' }}
              data-testid="change-password-submit"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t.changingPassword}
                </span>
              ) : copy.update}
            </button>
            <button
              type="button"
              onClick={goBack}
              className="pw-cancel"
              data-testid="change-password-cancel"
            >
              {copy.cancel}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

// ============================================================================
// END AUTHENTICATION COMPONENTS
// ============================================================================

// ============================================================================
// SUBSCRIPTION COMPONENTS
// Per Premium Experience Constitution
// ============================================================================

// Subscription Screen - Main subscription management
const SubscriptionScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const goBack = useShellBack();
  const { subscription, pricing, isPro, isTrial, canStartTrial, startTrial, createCheckout, refreshSubscription } = useSubscription();
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [billingInterval] = useState('monthly'); // V1: memberships are monthly only
  // Android only: Google Play's own product truth, including the localized formatted prices the
  // member actually sees. Never a hard-coded Android price.
  const [playConfig, setPlayConfig] = useState(null);
  const [playPrices, setPlayPrices] = useState(null);
  // iPhone only: App Store product truth and localized prices (StoreKit 2). Never a hard-coded iOS price.
  const [appleConfig, setAppleConfig] = useState(null);
  const [applePrices, setApplePrices] = useState(null);

  useEffect(() => {
    if (!isIosApp()) return;
    let alive = true;
    const verify = async (signed_transaction) =>
      (await axios.post(`${API}/billing/apple/restore`, { signed_transaction })).data;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/billing/apple/config`);
        if (!alive) return;
        setAppleConfig(data);
        if (!data?.configured) return;
        const prices = await fetchAppleProducts(data.products);
        if (!alive) return;
        setApplePrices(prices);
        await restoreApplePurchases(verify).catch(() => {});
      } catch (e) {
        console.error('App Store billing unavailable:', e);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!isAndroidApp()) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/billing/play/config`);
        if (!alive) return;
        setPlayConfig(data);
        const plans = await fetchPlayBasePlans(data?.subscription_id);
        if (!alive) return;
        const byInterval = {};
        (plans || []).forEach((plan) => {
          if (plan.basePlanId === data?.base_plans?.monthly) byInterval.monthly = plan;
        });
        setPlayPrices(byInterval);
        // A purchase must be re-linked if it exists but this account does not know about it.
        await restorePlayPurchases(async (purchaseToken) => {
          const res = await axios.post(`${API}/billing/play/restore`, {
            purchase_token: purchaseToken,
          });
          return res.data;
        }).catch(() => {});
      } catch (e) {
        console.error('Play billing unavailable:', e);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Restore Purchase: the store that sold the membership re-links it; the web re-reads Stripe's record.
  const handleRestore = async () => {
    setIsRestoring(true);
    try {
      if (isIosApp()) {
        await restoreApplePurchases(async (signed_transaction) =>
          (await axios.post(`${API}/billing/apple/restore`, { signed_transaction })).data);
      } else if (isAndroidApp()) {
        await restorePlayPurchases(async (purchase_token) =>
          (await axios.post(`${API}/billing/play/restore`, { purchase_token })).data);
      }
      await refreshSubscription();
      alert(lang === 'es' ? 'Hemos comprobado tus compras. Si tenías una membresía activa, ya aparece aquí.'
                          : 'Your purchases have been checked. Any active membership now shows here.');
    } catch (e) {
      console.error('Restore failed:', e);
      alert(lang === 'es' ? 'No se pudieron restaurar las compras ahora mismo.' : 'Purchases could not be restored right now.');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleStartTrial = async () => {
    setIsStartingTrial(true);
    const result = await startTrial();
    setIsStartingTrial(false);
    
    if (result.success) {
      navigate('/home');
    }
  };

  // Stage 2: `choice` is the membership chosen on the plan cards ("essential" or "premium").
  const handleUpgrade = async (choice = billingInterval) => {
    setIsUpgrading(true);
    try {
      // On Android, Premium is bought through Google Play Billing. Stripe checkout is never
      // opened inside the Play app.
      if (isAndroidApp()) {
        const basePlanId = playConfig?.base_plans?.[billingInterval];
        if (!playConfig?.subscription_id || !basePlanId) {
          alert(lang === 'es' ? 'Google Play no está disponible ahora mismo.'
                              : 'Google Play is not available right now.');
          return;
        }
        const result = await purchasePlayPlan({
          subscriptionId: playConfig.subscription_id,
          basePlanId,
          accountId: subscription?.user_id || playConfig.account_id,
          verify: async (purchaseToken) => {
            const res = await axios.post(`${API}/billing/play/verify`, {
              purchase_token: purchaseToken,
              base_plan_id: basePlanId,
            });
            return res.data;
          },
        });
        if (result?.activated) {
          window.location.reload();
        }
        return;
      }
      // On iPhone, Premium is bought through the App Store. Stripe checkout is never opened there.
      if (isIosApp()) {
        const productId = appleConfig?.products?.[choice];
        if (!appleConfig?.configured || !productId || !appleConfig?.app_account_token) {
          alert(lang === 'es' ? 'La App Store no está disponible ahora mismo.'
                              : 'The App Store is not available right now.');
          return;
        }
        const result = await purchaseApplePlan({
          productId,
          appAccountToken: appleConfig.app_account_token,
          verify: async (signed_transaction) =>
            (await axios.post(`${API}/billing/apple/verify`, { signed_transaction })).data,
        });
        if (result?.activated) {
          window.location.reload();
        }
        return;
      }
      const checkout = await createCheckout(choice);
      if (checkout?.blocked === 'ios') {
        alert(iosPurchaseNotice(lang));
        return;
      }
      if (checkout?.url) {
        window.location.href = checkout.url;
      }
    } catch (e) {
      console.error('Upgrade failed:', e);
    } finally {
      setIsUpgrading(false);
    }
  };

  // Calculate trial days remaining
  const getTrialDaysRemaining = () => {
    if (!subscription?.trial_end) return 0;
    const end = new Date(subscription.trial_end);
    const now = new Date();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  // On Android the member-visible price is Google Play's own localized formatted string, never a
  // figure this app composed. The web keeps the Stripe-backed display figures.
  const playPriceFor = (interval) =>
    playPrices?.[interval]?.formattedPrice || applePrices?.[interval] || null;

  // Canonical Founder pricing is served by the backend; Membership plans read it from there.
  const trialDays = pricing?.trial_days || 7;

  // Premium is the member-facing name on this surface. The centre of the product is the
  // companion relationship — not Patterns, which is now a supporting benefit.
  const premium = lang === 'es'
    ? {
        eyebrow: 'MEMBRESÍA ARVEXIS',
        hero: 'Más de la compañera que llega a conocerte.',
        lede: 'Más continuidad. Más espacio para hablar, crear y explorar tu mundo.',
        tierFree: 'Sin membresía activa', tierPremium: 'Premium', tierTrial: 'Meet ARVEXIS',
        capability: 'Capacidad', freeHead: 'Gratis', premiumHead: 'Premium',
        freeSub: 'Una relación real con Arvexis.', premiumSub: 'Ir más lejos juntas.',
        startTrial: `Comenzar prueba Premium de ${trialDays} días`,
        trialNote: 'No se requiere tarjeta para empezar. Incluye cantidades de prueba para las funciones Premium. Vuelves a Gratis si no mejoras.',
        reminder: 'Recuérdame un día antes de que termine Meet ARVEXIS',
        upgrade: 'Mejorar a Premium',
        buyNow: 'O compra Premium ahora',
      }
    : {
        eyebrow: 'ARVEXIS MEMBERSHIP',
        hero: 'More of the companion that gets to know you.',
        lede: 'More continuity. More room to talk, create, and explore your world.',
        tierFree: 'No active membership', tierPremium: 'Premium', tierTrial: 'Meet ARVEXIS',
        capability: 'Capability', freeHead: 'Free', premiumHead: 'Premium',
        freeSub: 'A real Arvexis relationship.', premiumSub: 'Go further together.',
        startTrial: `Start ${trialDays}-Day Premium Trial`,
        trialNote: 'No card required to start. Includes trial allowances for Premium features. You return to Free if you do not upgrade.',
        reminder: 'Remind me one day before Meet ARVEXIS ends',
        upgrade: 'Upgrade to Premium',
        buyNow: 'Or buy Premium now',
      };

  // The comparison contract, exactly as ruled. One grammar of words, no ticks and no crosses,
  // and nothing that turns something Free already has into a Premium-only claim.
  //
  // `gate` marks a row whose capability does not exist yet: it is built in its final position so
  // this page never needs restructuring, and it is invisible to real members until the
  // capability is real. See src/featureGates.js.
  const comparisonRows = (lang === 'es'
    ? [
        { key: 'relationship', label: 'Relación con Arvexis y Recuerdos Compartidos', free: 'Incluido', premium: 'Incluido' },
        { key: 'message', label: 'Message', free: 'Incluido', premium: 'Más capacidad' },
        { key: 'talk', label: 'Talk', free: 'Incluido', premium: 'Más capacidad' },
        { key: 'continuity', label: 'Continuidad Message ↔ Talk', free: 'Incluido', premium: 'Incluido' },
        { key: 'handoff', label: 'Pasar a la siguiente sala', free: 'No incluido', premium: 'Incluido' },
        { key: 'multimodal', label: 'Imágenes, documentos, audio y vídeo', free: 'Incluido', premium: 'Incluido' },
        { key: 'creation', label: 'Escritura y creación de imágenes', free: 'Incluido', premium: 'Incluido' },
        ]
    : [
        { key: 'relationship', label: 'Arvexis relationship & Shared Memories', free: 'Included', premium: 'Included' },
        { key: 'message', label: 'Message', free: 'Included', premium: 'More capacity' },
        { key: 'talk', label: 'Talk', free: 'Included', premium: 'More capacity' },
        { key: 'continuity', label: 'Message ↔ Talk continuity', free: 'Included', premium: 'Included' },
        { key: 'handoff', label: 'Step into the next room', free: 'Not included', premium: 'Included' },
        { key: 'multimodal', label: 'Images, documents, audio & video', free: 'Included', premium: 'Included' },
        { key: 'creation', label: 'Writing & image creation', free: 'Included', premium: 'Included' },
        ]
  ).filter((row) => !row.gate || isUnreleasedVisible(row.gate));

  // The real numbers, before she pays. Read from /api/subscription/plans, which reads the same
  // configuration the gates enforce, so what she is shown here is what she is actually held to.
  // Member's words, not ours; monthly counts; nothing called unlimited; and only capabilities
  // with a Founder-approved allowance appear at all.
  const [planAllowances, setPlanAllowances] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/subscription/plans`)
      .then((r) => { if (alive) setPlanAllowances(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const countFmt = new Intl.NumberFormat(lang === 'es' ? 'es-ES' : 'en-US');
  const allowanceLabels = lang === 'es'
    ? {
        message: 'Mensajes cada mes',
        talk: 'Minutos de Talk cada mes',
        image: 'Imágenes que ella crea',
        audio: 'Grabaciones de audio que ella escucha',
        video: 'Videos que subes para que ella los vea',
        note: (mins) => `Las cantidades se renuevan cada mes. Cada grabación de audio puede durar hasta ${mins} minutos.`,
      }
    : {
        message: 'Messages each month',
        talk: 'Talk minutes each month',
        image: 'Images she creates',
        audio: 'Audio recordings she listens to',
        video: 'Videos you upload for her to watch',
        note: (mins) => `Allowances reset every month. Each audio recording can be up to ${mins} minutes long.`,
      };

  const allowanceRows = planAllowances
    ? [
        ['message', 'message'], ['talk', 'talk_minutes'], ['image', 'image_creation'],
        ['audio', 'shared_audio'], ['video', 'video'],
      ].map(([key, field]) => ({
        key: `allowance-${key}`,
        label: allowanceLabels[key],
        free: countFmt.format(planAllowances.free[field]),
        premium: countFmt.format(planAllowances.premium[field]),
      }))
    : [];

  const allRows = [...comparisonRows, ...allowanceRows];

  // What the trial actually includes, before she starts it. The trial carries Premium FEATURE
  // access on its own smaller totals for the 7 days, so saying only "Premium trial" would let
  // her expect the monthly Premium numbers. Read from the canonical `trial` block of
  // /api/subscription/plans — never duplicated here — so it cannot drift from what she is held to.
  const trialAllowanceLine = planAllowances?.trial
    ? (lang === 'es'
        ? `Tu prueba de ${planAllowances.trial_days} días incluye `
          + `${countFmt.format(planAllowances.trial.message)} mensajes, `
          + `${countFmt.format(planAllowances.trial.listen_minutes)} minutos de Escuchar, `
          + `${countFmt.format(planAllowances.trial.talk_minutes)} minutos de Talk, `
          + `${countFmt.format(planAllowances.trial.dreams)} Sueño con Reflexión, `
          + `${countFmt.format(planAllowances.trial.image_creation)} imagen y una experiencia de Pasar a la siguiente sala, `
          + `en total para los ${planAllowances.trial_days} días.`
        : `Your ${planAllowances.trial_days}-day trial includes `
          + `${countFmt.format(planAllowances.trial.message)} messages, `
          + `${countFmt.format(planAllowances.trial.listen_minutes)} Listen minutes, `
          + `${countFmt.format(planAllowances.trial.talk_minutes)} Talk minutes, `
          + `${countFmt.format(planAllowances.trial.dreams)} Dream with Reflection, `
          + `${countFmt.format(planAllowances.trial.image_creation)} image and one Step Into The Next Room experience, `
          + `in total across the ${planAllowances.trial_days} days.`)
    : null;

  // The reminder control is built in its final place, but reminder delivery does not exist, so
  // it stays invisible and non-interactive rather than becoming a promise we cannot keep.
  // The reminder the member explicitly asks for: one service message, three days before the
  // trial ends. Opt-in lives on their own record; nothing is queued and no list is joined.
  const [reminderOptIn, setReminderOptIn] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/subscription/trial-reminder`)
      .then((r) => { if (alive) setReminderOptIn(Boolean(r.data?.opt_in)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const toggleReminder = async () => {
    const next = !reminderOptIn;
    setReminderSaving(true);
    setReminderOptIn(next);
    try {
      await axios.patch(`${API}/subscription/trial-reminder`, { enabled: next });
    } catch {
      setReminderOptIn(!next);
    } finally {
      setReminderSaving(false);
    }
  };

  const Comparison = () => (
    <div className="cmp" data-testid="premium-comparison">
      <div className="cmp__head" aria-hidden="false">
        <div className="cmp__cap cmp__caphead">{premium.capability}</div>
        <div className="cmp__cell cmp__cellhead">
          <span className="cmp__plan">{premium.freeHead}</span>
          <span className="cmp__plansub">{premium.freeSub}</span>
        </div>
        <div className="cmp__cell cmp__cellhead cmp__cell--prem">
          <span className="cmp__plan cmp__plan--prem">{premium.premiumHead}</span>
          <span className="cmp__plansub">{premium.premiumSub}</span>
        </div>
      </div>
      {allRows.map((row) => (
        <div className="cmp__row" key={row.key} data-testid={`comparison-row-${row.key}`}>
          <div className="cmp__cap">{row.label}</div>
          <div className="cmp__cell" data-testid={`comparison-${row.key}-free`}>
            <span className="cmp__val">{row.free}</span>
          </div>
          <div className="cmp__cell cmp__cell--prem" data-testid={`comparison-${row.key}-premium`}>
            <span className="cmp__val cmp__val--prem">{row.premium}</span>
          </div>
        </div>
      ))}
      {planAllowances && (
        <p className="cmp__note" data-testid="comparison-allowance-note">
          {allowanceLabels.note(Math.round(planAllowances.shared_audio_max_seconds / 60))}
        </p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen utility-surface utility-surface--atmospheric" data-testid="subscription-surface">
      <header
        className="flex items-center gap-4 px-5 account-mobile-header"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 47px) + 12px)',
          paddingBottom: '12px'
        }}
      >
        <button
          onClick={goBack}
          className="rounded-full flex items-center justify-center shrink-0"
          style={{ width: 44, height: 44, background: 'transparent',
                   border: '1px solid rgba(232, 197, 143, 0.38)' }}
          aria-label={lang === 'es' ? 'Volver' : 'Back'}
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: 'rgba(232, 197, 143, 0.9)' }} />
        </button>
        <span
          className="account-wordmark"
          style={{ fontSize: '12px', letterSpacing: '0.42em', color: 'rgba(244, 239, 231, 0.8)',
                   margin: '0 auto' }}
          data-testid="subscription-wordmark"
        >
          ARVEXIS
        </span>
        <button type="button" onClick={handleRestore} disabled={isRestoring} className="mp-restore"
          style={{ color: '#F2D58C', marginLeft: 'auto' }} data-testid="restore-purchase-btn">
          {lang === 'es' ? 'Restaurar compra' : 'Restore Purchase'}
        </button>
      </header>

      <div className="utility-head account-col account-col--wide sub__hero"
        style={(!isPro || isTrial) ? { textAlign: 'center' } : undefined}>
        {isPro && !isTrial && <p className="utility-head__eyebrow" data-testid="subscription-eyebrow">{premium.eyebrow}</p>}
        <h1 className="utility-head__title" data-testid="subscription-title">
          {isPro ? t.manageSubscription : premium.hero}
        </h1>
        <p className="utility-head__sub" data-testid="subscription-lede">{premium.lede}</p>
      </div>

      <main className="account-col account-col--wide sub__main" style={{ padding: '10px 14px 48px' }}>
        {/* Where the member stands today — only when there is something to state. A member on
            Free sees the comparison first, as ruled: value before price. */}
        {(isPro || isTrial || subscription?.status === 'CANCELED') && (
          <>
            <div className="sub__card sub__status" data-testid="subscription-status-card">
              <div className="sub__statushead">
                <span className={isPro ? 'sub__crown sub__crown--on' : 'sub__crown'}>
                  <Crown className="w-5 h-5" />
                </span>
                <div>
                  <p className="sub__tier" data-testid="subscription-tier">
                    {subscription?.complimentary_test_access ? (lang === 'es' ? 'Acceso de prueba gratuito' : 'Complimentary test access') : isTrial ? 'Meet ARVEXIS' : (isPro ? (subscription?.membership === 'essential' ? 'Essential' : premium.tierPremium) : premium.tierFree)}
                  </p>
                  {isTrial && (
                    <p className="sub__tiermeta">
                      {getTrialDaysRemaining()} {t.trialDaysRemaining}
                    </p>
                  )}
                  {subscription?.status === 'CANCELED' && (
                    <p className="sub__tiermeta">{t.subscriptionCanceled}</p>
                  )}
                </div>
              </div>
              {isTrial && <p className="sub__note">{t.trialActiveMessage}</p>}
              {isTrial && trialAllowanceLine && (
                <p className="sub__note" data-testid="trial-allowances-active">{trialAllowanceLine}</p>
              )}
              {isPro && !isTrial && <p className="sub__note">{t.subscriptionActive}</p>}
            </div>

          </>
        )}

        {subscription?.complimentary_test_access && (
          <p className="sub__trialnote" data-testid="tester-access-note">
            {lang === 'es'
              ? 'Gracias por probar ARVEXIS. Tu acceso de prueba es gratuito, con los límites de Premium, y nunca se convierte en un pago. Al terminar, todo lo tuyo se conserva y podrás elegir una membresía.'
              : 'Thank you for testing ARVEXIS. Your test access is free, with Premium limits, and never turns into a charge. When it ends, everything of yours is kept and you can choose a membership.'}
          </p>
        )}
        {(isPro || isTrial || subscription?.complimentary_test_access) && <MembershipUsage lang={lang} />}

        {/* Stage 2: the three memberships, read from the same configuration the gates enforce. */}
        {(!isPro || isTrial) && (
          <MembershipPlans lang={lang} plans={planAllowances} storePrices={applePrices} canStartTrial={canStartTrial} isTrial={isTrial}
            onStartTrial={handleStartTrial} onChoose={handleUpgrade} busy={isUpgrading || isStartingTrial} />
        )}

        {isPro && !isTrial && (
          <button
            onClick={async () => {
              // The provider that owns the subscription decides where the member goes — never
              // the platform they happen to be holding. A member who bought on the web must
              // reach Stripe even inside the Android app, and a Play member must never be sent
              // to Stripe. Absent provider means a Stripe-billed member from before the field
              // existed, which is every paid member on the web today.
              const provider = subscription?.subscription_provider || 'stripe';

              if (provider === 'google_play') {
                try {
                  if (isAndroidApp()) {
                    const url = playConfig?.manage_url
                      || (await axios.get(`${API}/billing/play/manage-url`)).data?.manage_url;
                    if (!url) throw new Error('no manage url');
                    await openPlayManage(url);
                    return;
                  }
                  // Any other surface: tell the member the truth and open Google's own page.
                  const { data } = await axios.get(`${API}/billing/play/manage-url`);
                  alert(lang === 'es'
                    ? 'Tu suscripción Premium se factura a través de Google Play. Gestiónala o cancélala en Google Play.'
                    : 'Your Premium subscription is billed through Google Play. Manage or cancel it in Google Play.');
                  if (data?.manage_url) {
                    window.open(data.manage_url, '_blank', 'noopener');
                  }
                } catch (err) {
                  console.error('Play manage error:', err);
                  alert(lang === 'es'
                    ? 'No se pudo abrir la gestión de suscripciones de Google Play.'
                    : 'Could not open Google Play subscription management.');
                }
                return;
              }

              if (provider === 'apple') {
                // Apple owns this subscription: manage it in the App Store, never at Stripe.
                if (isIosApp()) {
                  try {
                    await openAppleManage();
                    return;
                  } catch (err) {
                    console.error('App Store manage error:', err);
                  }
                }
                alert(lang === 'es'
                  ? 'Tu suscripción Premium se factura a través de Apple. Gestiónala en los ajustes de suscripciones de tu dispositivo.'
                  : 'Your Premium subscription is billed through Apple. Manage it in your device subscription settings.');
                return;
              }

              try {
                const response = await axios.post(`${API}/subscription/customer-portal`, {
                  return_url: window.location.origin + '/profile'
                });
                if (response.data.portal_url) {
                  window.location.href = response.data.portal_url;
                } else {
                  // Never fail silently: if the portal URL is absent the member is told.
                  alert(lang === 'es' ? 'No se pudo abrir el portal de suscripción' : 'Could not open subscription portal');
                }
              } catch (err) {
                console.error('Portal error:', err);
                alert(lang === 'es' ? 'No se pudo abrir el portal de suscripción' : 'Could not open subscription portal');
              }
            }}
            className="sub__secondary"
            data-testid="manage-subscription-btn"
          >
            {t.manageSubscription}
          </button>
        )}

        {isTrial && (
          <div className="sub__card" data-testid="trial-reminder-card">
            <button type="button" className="sub__reminder" onClick={toggleReminder} disabled={reminderSaving}
              aria-pressed={reminderOptIn} data-testid="trial-reminder-toggle">
              <span className={`sub__switch${reminderOptIn ? ' sub__switch--on' : ''}`} aria-hidden="true">
                <span className="sub__knob" />
              </span>
              <span className="sub__reminderlabel">{premium.reminder}</span>
            </button>
          </div>
        )}

        {/* The quiet last word. Free is not a trailer for Premium. */}
        {isPro && !isTrial && (
          <div className="sub__truth">
            <span className="sub__inf" aria-hidden="true">∞</span>
            <p data-testid="premium-footnote">{t.premiumFootnote}</p>
          </div>
        )}
      </main>

      <style>{`
        .sub__card {
          padding: 18px; margin-bottom: 14px; border-radius: 18px;
          background: rgba(255, 255, 255, 0.028);
          border: 1px solid rgba(244, 239, 231, 0.10);
        }
        .sub__statushead { display: flex; align-items: center; gap: 14px; }
        .sub__crown {
          width: 46px; height: 46px; border-radius: 50%; display: flex; align-items: center;
          justify-content: center; border: 1px solid rgba(244, 239, 231, 0.14);
          color: rgba(244, 239, 231, 0.45);
        }
        .sub__crown--on { border-color: rgba(232, 197, 143, 0.5); color: #E8C58F; }
        .sub__tier { font-size: 17px; color: #F4EFE7; }
        .sub__tiermeta { margin-top: 3px; font-size: 13px; color: #E8C58F; }
        .sub__note {
          margin-top: 12px; font-size: 13.5px; line-height: 1.55;
          color: rgba(244, 239, 231, 0.48);
        }
        .sub__cardtitle {
          display: flex; align-items: center; gap: 8px; font-size: 15px; color: #F4EFE7;
          margin-bottom: 14px;
        }
        .sub__features { display: flex; flex-direction: column; gap: 11px; }
        .sub__feature {
          display: flex; align-items: flex-start; gap: 10px; font-size: 14.5px;
          line-height: 1.45; color: rgba(244, 239, 231, 0.74);
        }

        /* ---- Free vs Premium comparison ------------------------------------------------
           One grammar of words, no ticks and no crosses. The three-column comparison holds at
           EVERY width — a phone gets the same at-a-glance read, with its own spacing and its own
           column weights, not a squeezed copy of the desktop table and not a stack of repeated
           FREE/PREMIUM labels. Free reads as complete; Premium carries restrained champagne
           emphasis through border and type, not volume. */
        .cmp {
          margin-bottom: 20px; border-radius: 18px;
          background: rgba(255, 255, 255, 0.028);
          border: 1px solid rgba(244, 239, 231, 0.10);
          font-family: 'Inter', system-ui, sans-serif;
        }
        .cmp__head, .cmp__row {
          display: grid;
          grid-template-columns: minmax(0, 1.28fr) minmax(0, 1fr) minmax(0, 1.12fr);
          align-items: stretch;
        }
        .cmp__row { border-top: 1px solid rgba(244, 239, 231, 0.07); }
        .cmp__cap {
          display: flex; align-items: center; padding: 15px 2px 15px 13px;
          font-size: 13px; line-height: 1.38; color: #F4EFE7;
        }
        .cmp__caphead {
          font-size: 13px; font-weight: 600; color: rgba(244, 239, 231, 0.9);
          padding-top: 18px; padding-bottom: 18px;
        }
        .cmp__cell {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 15px 6px; min-width: 0; text-align: center;
        }
        .cmp__cellhead { padding-top: 15px; padding-bottom: 15px; }
        .cmp__cell--prem {
          background: rgba(232, 197, 143, 0.05);
          box-shadow: inset 1px 0 0 rgba(232, 197, 143, 0.3),
                      inset -1px 0 0 rgba(232, 197, 143, 0.3);
        }
        .cmp__head .cmp__cell--prem {
          border-radius: 10px 10px 0 0;
          box-shadow: inset 1px 0 0 rgba(232, 197, 143, 0.3),
                      inset -1px 0 0 rgba(232, 197, 143, 0.3),
                      inset 0 1px 0 rgba(232, 197, 143, 0.3);
        }
        .cmp__row:last-of-type .cmp__cell--prem {
          border-radius: 0 0 10px 10px;
          box-shadow: inset 1px 0 0 rgba(232, 197, 143, 0.3),
                      inset -1px 0 0 rgba(232, 197, 143, 0.3),
                      inset 0 -1px 0 rgba(232, 197, 143, 0.3);
        }
        .cmp__plan {
          font-family: 'Cormorant Garamond', Georgia, serif; font-size: 21px; line-height: 1.1;
          color: #F4EFE7;
        }
        .cmp__plan--prem { color: #E8C58F; }
        .cmp__plansub {
          margin-top: 4px; font-size: 10.5px; line-height: 1.35;
          color: rgba(244, 239, 231, 0.42);
        }
        .cmp__val { font-size: 13px; line-height: 1.45; color: rgba(244, 239, 231, 0.74); }
        .cmp__val--prem { color: #E8C58F; }
        .cmp__note {
          margin: 0;
          padding: 14px 20px 16px;
          border-top: 1px solid rgba(244, 239, 231, 0.07);
          font-size: 12px;
          line-height: 1.6;
          color: rgba(244, 239, 231, 0.5);
        }

        @media (min-width: 700px) {
          .sub__main { padding-left: 20px !important; padding-right: 20px !important; }
          .cmp__head, .cmp__row {
            grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr);
          }
          .cmp__cap { padding: 13px 18px; font-size: 14px; }          .cmp__caphead {
            font-size: 11px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase;
            color: rgba(244, 239, 231, 0.5); padding-top: 22px; padding-bottom: 22px;
          }
          .cmp__cell { padding: 13px 14px; }
          .cmp__cellhead { padding-top: 16px; padding-bottom: 16px; }
          .cmp__plan { font-size: 23px; }
          .cmp__plansub { font-size: 11.5px; }
          .cmp__val { font-size: 13.5px; }
        }

        /* ---- Hero: centred on a phone, as the mockup sets it. The desktop reference, which
           the Founder accepted, keeps its left-aligned editorial hero. ---- */
        .sub__hero { text-align: center; padding-bottom: 8px; }
        .sub__hero .utility-head__sub { max-width: 34ch; margin-left: auto; margin-right: auto; }
        @media (min-width: 700px) {
          .sub__hero { text-align: left; }
          .sub__hero .utility-head__sub { max-width: none; margin-left: 0; margin-right: 0; }
        }

        /* ---- Pricing: value first, then price. Both figures stay readable at once. ---- */
        .pr__toggle {
          display: flex; gap: 6px; padding: 5px; margin-bottom: 18px; border-radius: 999px;
          background: rgba(255, 255, 255, 0.035); border: 1px solid rgba(244, 239, 231, 0.09);
        }
        .pr__pill {
          flex: 1; min-height: 46px; border-radius: 999px; border: 1px solid transparent;
          background: transparent; color: rgba(244, 239, 231, 0.6); cursor: pointer;
          font-family: 'Inter', system-ui, sans-serif; font-size: 14.5px; font-weight: 600;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          line-height: 1.15;
          transition: background-color 180ms ease, color 180ms ease;
        }
        .pr__pill--on {
          background: linear-gradient(135deg, #E8C58F 0%, #D4A843 100%); color: #0A0A0F;
        }
        .pr__pillnote { font-size: 11px; font-weight: 500; opacity: 0.8; }
        .pr__prices {
          display: grid; grid-template-columns: 1fr 1fr; align-items: start;
        }
        .pr__col {
          padding: 4px 10px; text-align: center; opacity: 0.45;
          transition: opacity 200ms ease;
        }
        .pr__col--on { opacity: 1; }
        .pr__col + .pr__col { border-left: 1px solid rgba(244, 239, 231, 0.09); }
        .pr__amount {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-size: 32px; line-height: 1.15; font-weight: 600; letter-spacing: -0.015em;
          color: #F4EFE7;
        }
        .pr__per {
          font-family: 'Inter', system-ui, sans-serif; font-size: 14px; font-weight: 400;
          color: rgba(244, 239, 231, 0.48);
        }
        .pr__note {
          margin-top: 7px; font-family: 'Inter', system-ui, sans-serif; font-size: 11.5px;
          line-height: 1.45; color: rgba(232, 197, 143, 0.82);
        }
        .pr__cta { width: 100%; margin-top: 22px; }
        .pr .sub__trialnote { margin-top: 12px; }
        .pr .sub__reminder { margin-top: 16px; justify-content: center; }
        @media (min-width: 700px) {
          .pr__amount { font-size: 38px; }
          .pr__note { font-size: 12.5px; }
          .pr .sub__reminder { justify-content: flex-start; }
        }

        /* ---- Trial reminder: the member's own opt-in ---- */
        .sub__reminder {
          display: flex; align-items: center; gap: 12px; padding: 4px 2px;
          font-family: 'Inter', system-ui, sans-serif; cursor: pointer;
          background: none; border: 0; width: 100%; text-align: left;
        }
        .sub__reminder:disabled { opacity: 0.6; cursor: default; }
        .sub__switch {
          flex: 0 0 auto; width: 44px; height: 24px; border-radius: 999px;
          background: rgba(255, 255, 255, 0.10); border: 1px solid rgba(244, 239, 231, 0.14);
          display: flex; align-items: center; padding: 2px;
          transition: background-color 180ms ease, border-color 180ms ease;
        }
        .sub__switch--on {
          background: rgba(232, 197, 143, 0.28); border-color: rgba(232, 197, 143, 0.55);
          justify-content: flex-end;
        }
        .sub__knob {
          width: 18px; height: 18px; border-radius: 50%;
          background: rgba(244, 239, 231, 0.55);
          transition: background-color 180ms ease;
        }
        .sub__switch--on .sub__knob { background: #E8C58F; }
        .sub__reminderlabel { font-size: 13px; color: rgba(244, 239, 231, 0.55); }

        /* ---- The quiet last word ---- */
        .sub__truth { margin-top: 30px; text-align: center; }
        .sub__inf {
          display: block; font-size: 24px; line-height: 1; color: rgba(232, 197, 143, 0.7);
          margin-bottom: 12px;
        }
        .sub__truth p {
          font-family: 'Inter', system-ui, sans-serif; font-size: 12.5px; line-height: 1.7;
          color: rgba(244, 239, 231, 0.42); max-width: 46ch; margin: 0 auto;
        }
        .sub__toggle { display: flex; gap: 8px; margin-bottom: 16px; }
        .sub__pill {
          flex: 1; min-height: 44px; border-radius: 999px; font-size: 14px;
          background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(244, 239, 231, 0.12);
          color: rgba(244, 239, 231, 0.62); cursor: pointer;
          transition: background-color 180ms ease, color 180ms ease, border-color 180ms ease;
        }
        .sub__pill--on {
          background: rgba(232, 197, 143, 0.12); border-color: rgba(232, 197, 143, 0.5);
          color: #E8C58F;
        }
        .sub__price { text-align: center; }
        /* Commercial figures read as fact, not editorial: the functional sans, not the serif.
           The serif stays for emotional/brand headings such as "Arvexis Premium". */
        .sub__amount {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-size: 34px; line-height: 1.15; font-weight: 600; letter-spacing: -0.01em;
          color: #F4EFE7;
        }
        .sub__per { font-family: 'Inter', system-ui, sans-serif; font-size: 15px; font-weight: 400; color: rgba(244, 239, 231, 0.48); }
        .sub__equiv {
          margin-top: 7px; font-family: 'Inter', system-ui, sans-serif; font-size: 13.5px;
          color: #E8C58F;
        }
        .sub__lede {
          margin-top: 10px; font-family: 'Inter', system-ui, sans-serif; font-size: 13.5px;
          line-height: 1.65; color: rgba(244, 239, 231, 0.48);
        }
        .sub__footnote {
          margin-top: 16px; padding-top: 14px; font-size: 12.5px; line-height: 1.65;
          color: rgba(244, 239, 231, 0.42);
          border-top: 1px solid rgba(244, 239, 231, 0.08);
        }
        .sub__stack { display: flex; flex-direction: column; gap: 14px; }
        .sub__primary {
          min-height: 54px; border-radius: 999px; font-size: 15.5px; font-weight: 600;
          color: #0A0A0F; border: 1px solid rgba(232, 197, 143, 0.5);
          background: linear-gradient(135deg, #E8C58F 0%, #D4A843 100%); cursor: pointer;
        }
        .sub__primary:disabled, .sub__secondary:disabled { opacity: 0.55; cursor: not-allowed; }
        .sub__secondary {
          width: 100%; min-height: 54px; border-radius: 999px; font-size: 15.5px; font-weight: 500;
          background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(244, 239, 231, 0.14);
          color: #F4EFE7; cursor: pointer; transition: background-color 180ms ease;
        }
        .sub__secondary:hover { background: rgba(232, 197, 143, 0.08); }
        .sub__trialnote {
          font-size: 12.5px; line-height: 1.6; text-align: center;
          color: rgba(244, 239, 231, 0.42);
        }
        .sub__buynow {
          display: block; margin: 2px auto 0; padding: 8px 4px; background: none; border: 0;
          font-size: 13.5px; font-weight: 500; letter-spacing: 0.01em;
          color: rgba(232, 197, 143, 0.82); cursor: pointer;
          text-decoration: underline; text-decoration-thickness: 1px;
          text-underline-offset: 3px; transition: color 180ms ease;
        }
        .sub__buynow:hover { color: #E8C58F; }
        .sub__buynow:disabled { opacity: 0.55; cursor: not-allowed; }
      `}</style>
    </div>
  );
};

// Subscription Success Screen
const SubscriptionSuccessScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const { checkPaymentStatus, refreshSubscription } = useSubscription();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    const verifyPayment = async () => {
      if (sessionId) {
        await checkPaymentStatus(sessionId);
        await refreshSubscription();
      }
      setIsVerifying(false);
    };
    verifyPayment();
  }, [sessionId]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#D4A843] animate-spin mx-auto mb-4" />
          <p className="text-[#9CA3AF]">Verifying payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-sm"
      >
        <div className="w-20 h-20 bg-[#D4A843]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Crown className="w-10 h-10 text-[#D4A843]" />
        </div>
        
        <h1 className="text-2xl font-semibold text-[#E5E7EB] mb-2">
          {t.paymentSuccess}
        </h1>
        <p className="text-[#9CA3AF] mb-8">
          {t.paymentSuccessMessage}
        </p>
        
        <button
          onClick={() => navigate('/home')}
          className="w-full py-4 bg-[#D4A843] hover:bg-[#B88010] text-[#0F172A] font-semibold rounded-xl transition-colors"
          data-testid="explore-pro-btn"
        >
          {t.explorePro}
        </button>
      </motion.div>
    </div>
  );
};

// Subscription Cancel Screen
const SubscriptionCancelScreen = ({ lang, t }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-sm"
      >
        <div className="w-20 h-20 bg-[#4B5563]/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <X className="w-10 h-10 text-[#6B7280]" />
        </div>
        
        <h1 className="text-2xl font-semibold text-[#E5E7EB] mb-2">
          {t.paymentCanceled}
        </h1>
        <p className="text-[#9CA3AF] mb-8">
          {t.paymentCanceledMessage}
        </p>
        
        <button
          onClick={() => navigate('/home')}
          className="w-full py-4 bg-[#1F2937] hover:bg-[#374151] text-[#E5E7EB] font-medium rounded-xl transition-colors"
          data-testid="continue-free-btn"
        >
          {t.continueFree}
        </button>
      </motion.div>
    </div>
  );
};

// Pro Upgrade Banner (for use in InsightsScreen)
const ProUpgradeBanner = ({ t, teaserMessage, onStartTrial, onUpgrade, canStartTrial }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-[#D4A843]/10 to-[#B88010]/10 border border-[#D4A843]/30 rounded-xl p-6 mb-6"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-[#D4A843]/20 rounded-full flex items-center justify-center flex-shrink-0">
          <Crown className="w-6 h-6 text-[#D4A843]" />
        </div>
        <div className="flex-1">
          <h3 className="text-[#E5E7EB] font-medium mb-1">
            {t.yourPatternIntelligence}
          </h3>
          <p className="text-[#9CA3AF] text-sm mb-4">
            {teaserMessage || t.patternsReadyToExplore}
          </p>
          
          <div className="flex flex-wrap gap-3">
            {canStartTrial && (
              <button
                onClick={onStartTrial}
                className="px-4 py-2 bg-[#D4A843] hover:bg-[#B88010] text-[#0F172A] text-sm font-medium rounded-lg transition-colors"
                data-testid="banner-start-trial"
              >
                {t.tryProFree}
              </button>
            )}
            <button
              onClick={onUpgrade}
              className={`px-4 py-2 ${canStartTrial ? 'bg-[#1F2937] hover:bg-[#374151] text-[#E5E7EB]' : 'bg-[#D4A843] hover:bg-[#B88010] text-[#0F172A]'} text-sm font-medium rounded-lg transition-colors`}
              data-testid="banner-upgrade"
            >
              {t.unlockFullInsights}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================================
// END SUBSCRIPTION COMPONENTS
// ============================================================================

// Pattern Milestone Modal Component
const PatternMilestoneModal = ({ isOpen, onClose, milestoneData, lang, t, onViewEntry }) => {
  const navigate = useNavigate();
  
  if (!isOpen || !milestoneData || !milestoneData.milestone_reached) return null;
  
  const message = lang === 'es' ? milestoneData.message_es : milestoneData.message_en;
  
  const handleViewEntries = (dreamIds) => {
    // Navigate to the first entry in the theme
    if (dreamIds && dreamIds.length > 0) {
      onClose();
      navigate(`/reflection/${dreamIds[0]}`);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.9)' }}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="milestone-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="pattern-milestone-modal"
          >
            {/* Header */}
            <div className="text-center mb-6">
              <div className="milestone-badge mb-4">
                <span className="text-2xl">✦</span>
              </div>
              <h2 className="text-xl font-semibold text-[#E5E7EB] mb-2">
                {t.milestoneTitle}
              </h2>
              <p className="text-[#9CA3AF] text-sm">
                {t.milestoneSubtitle}
              </p>
            </div>

            {/* Message */}
            {message && (
              <div className="milestone-message mb-6" data-testid="milestone-message">
                <p className="text-[#E5E7EB] text-sm leading-relaxed">
                  {message}
                </p>
              </div>
            )}

            {/* Theme Evidence Cards */}
            <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
              {milestoneData.recurring_themes.slice(0, 3).map((themeData, index) => (
                <div 
                  key={index} 
                  className="theme-evidence-card"
                  data-testid={`theme-evidence-${themeData.theme}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="theme-tag">{themeData.theme}</span>
                    <span className="text-[#6B7280] text-xs">
                      {t.appearsIn} {themeData.count} {t.entries}
                    </span>
                  </div>
                  {themeData.dream_excerpts && themeData.dream_excerpts[0] && (
                    <p className="text-[#9CA3AF] text-xs italic line-clamp-2 mb-2">
                      &ldquo;{themeData.dream_excerpts[0]}&rdquo;
                    </p>
                  )}
                  <button
                    onClick={() => handleViewEntries(themeData.dream_ids)}
                    className="text-[#D4A843] text-xs hover:text-[#E8C050] transition-colors flex items-center gap-1"
                    data-testid={`view-entries-${themeData.theme}`}
                  >
                    {t.viewEntries}
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={onClose}
              className="btn-primary w-full"
              data-testid="milestone-dismiss-btn"
            >
              {t.keepReflecting}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// LANGUAGE SELECTION SCREEN — For First Meeting language recovery
// Styled to match the "Begin Reflecting" aesthetic
// =============================================================================
const LanguageSelectionScreen = ({ lang, setLang, t }) => {
  const navigate = useNavigate();
  const [selectedLang, setSelectedLang] = useState(lang);
  
  const handleContinue = () => {
    // Update app language
    if (setLang) setLang(selectedLang);
    // Navigate back to home, which will show First Meeting
    navigate('/home');
  };
  
  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#0F172A] via-[#0F172A] to-[#0D1117] pointer-events-none" />
      
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm text-center"
        >
          {/* Language-specific logo */}
          <div className="flex justify-center mb-10">
            <img 
              src={selectedLang === 'es' ? '/arvexis-logo-spanish.png' : '/arvexis-logo-transparent.png'}
              alt="Arvexis"
              className="h-24 w-auto object-contain"
            />
          </div>
          
          <h1 className="text-2xl font-semibold text-[#E5E7EB] mb-3" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            {selectedLang === 'es' ? 'Selecciona tu idioma' : 'Select your language'}
          </h1>
          <p className="text-[#9CA3AF] text-sm mb-10">
            {selectedLang === 'es' 
              ? 'Arvexis te hablará en el idioma que elijas' 
              : 'Arvexis will speak to you in your chosen language'}
          </p>
          
          {/* Language Selection */}
          <div className="flex gap-3 mb-10">
            <button
              type="button"
              onClick={() => setSelectedLang('en')}
              className={`flex-1 py-4 px-4 rounded-xl font-medium transition-all duration-200 ${
                selectedLang === 'en'
                  ? 'bg-[#D4A843]/20 border-2 border-[#D4A843] text-[#D4A843]'
                  : 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:bg-white/10'
              }`}
              data-testid="language-select-en"
            >
              English
            </button>
            <button
              type="button"
              onClick={() => setSelectedLang('es')}
              className={`flex-1 py-4 px-4 rounded-xl font-medium transition-all duration-200 ${
                selectedLang === 'es'
                  ? 'bg-[#D4A843]/20 border-2 border-[#D4A843] text-[#D4A843]'
                  : 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:bg-white/10'
              }`}
              data-testid="language-select-es"
            >
              Español
            </button>
          </div>
          
          {/* Continue Button */}
          <button 
            onClick={handleContinue}
            className="btn-primary w-full"
            data-testid="language-continue-btn"
          >
            {selectedLang === 'es' ? 'Comienza a Reflexionar' : 'Begin Reflecting'}
          </button>
        </motion.div>
      </main>
      
      {/* Tagline */}
      <div className="p-6 text-center relative z-10">
        <p className="text-[#6B7280] text-sm" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
          {selectedLang === 'es' 
            ? 'Todos merecemos ser escuchados y comprendidos.' 
            : 'We all deserve to be heard and understood.'}
        </p>
      </div>
    </div>
  );
};

// Onboarding
const Onboarding = ({ lang, setLang, t, onComplete }) => {
  const [step, setStep] = useState(0);
  const [consent, setConsent] = useState(false);

  const screens = [
    { title: t.onboard1Title, sub: t.onboard1Sub },
    { title: t.onboard2Title, sub: t.onboard2Sub },
    { title: t.onboard3Title, sub: t.onboard3Sub }
  ];

  const handleNext = () => {
    if (step < 2) {
      setStep(step + 1);
    }
  };

  const handleStart = () => {
    if (consent) {
      localStorage.setItem('arvexis_onboarded', 'true');
      onComplete();
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col">
      {/* Language Toggle */}
      <div className="flex justify-end p-6">
        <div className="flex bg-[#1F2937] rounded-lg p-1">
          <button 
            onClick={() => setLang('en')}
            className={`px-3 py-1 text-sm rounded ${lang === 'en' ? 'bg-[#D4A843] text-[#0F172A]' : 'text-[#9CA3AF]'}`}
          >
            EN
          </button>
          <button 
            onClick={() => setLang('es')}
            className={`px-3 py-1 text-sm rounded ${lang === 'es' ? 'bg-[#D4A843] text-[#0F172A]' : 'text-[#9CA3AF]'}`}
          >
            ES
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 max-w-md mx-auto">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="text-center mb-12"
        >
          <ArvexisLockup width={LOCKUP_SIZES.MD} />
          
          <h1 className="onboarding-title mt-8">{screens[step].title}</h1>
          <p className="onboarding-subtitle">{screens[step].sub}</p>
        </motion.div>

        {/* Progress Dots */}
        <div className="flex gap-2 mb-8">
          {[0, 1, 2].map(i => (
            <div key={i} className={`progress-dot ${i === step ? 'active' : ''}`} />
          ))}
        </div>

        {/* Consent (on last screen) */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full mb-6"
          >
            <label className="checkbox-container">
              <input 
                type="checkbox" 
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="consent-checkbox"
              />
              <span className="text-[#9CA3AF] text-sm">{t.consentText}</span>
            </label>
          </motion.div>
        )}

        {/* Button */}
        {step < 2 ? (
          <button 
            onClick={handleNext}
            className="btn-primary w-full"
            data-testid="continue-btn"
          >
            {t.continue}
          </button>
        ) : (
          <button 
            onClick={handleStart}
            disabled={!consent}
            className="btn-primary w-full"
            data-testid="get-started-btn"
          >
            {t.getStarted}
          </button>
        )}
      </div>

      {/* Tagline */}
      <div className="p-6 text-center">
        <p className="text-[#6B7280] text-sm">{t.tagline}</p>
      </div>
    </div>
  );
};

// Home Screen - Premium Redesign
// Home Screen - Premium UI v2.0 (Based on Approved Mockup)
// ============================================================================
// SPRINT III — PREMIUM HOME EXPERIENCE
// "Welcome back." - A calm sanctuary that invites reflection
// ============================================================================

// Today's Insight messages - Thoughtful, contextual reflections
// Each insight has a short version (for card) and expanded content (for detail view)
// ARCHITECTURE NOTE: This curated library will evolve into personalized reflections
// based on user's dreams, memories, and growth over time. The closing card type
// varies to keep the experience fresh and alive.
// Closing card types: 'invitation' | 'question' | 'reminder' | 'pause' | 'reflection'
const INSIGHT_DATA = [
  {
    short: { 
      en: "Your dreams are showing you what your mind is ready to understand.",
      es: "Tus sueños te muestran lo que tu mente está lista para comprender."
    },
    expanded: {
      en: "Dreams often reveal truths we're emotionally prepared to process. They work like a gentle guide, surfacing insights at exactly the right moment in our journey. Pay attention to what keeps appearing—your subconscious knows what you're ready to see.",
      es: "Los sueños a menudo revelan verdades que estamos emocionalmente preparados para procesar. Funcionan como una guía gentil, revelando ideas en el momento exacto de nuestro viaje. Presta atención a lo que sigue apareciendo—tu subconsciente sabe lo que estás listo para ver."
    },
    prompt: {
      en: "What has been appearing in your dreams lately?",
      es: "¿Qué ha estado apareciendo en tus sueños últimamente?"
    },
    closingType: 'question',
    closingLabel: { en: "Today's Question", es: "Pregunta del Día" }
  },
  {
    short: { 
      en: "A recurring symbol may be inviting you toward growth.",
      es: "Un símbolo recurrente puede estar invitándote hacia el crecimiento."
    },
    expanded: {
      en: "When a symbol appears repeatedly across your dreams, it's rarely coincidence. Recurring imagery often represents an area of life seeking your attention. Rather than analyzing it logically, sit with the feeling it evokes. Growth often begins where our attention is being drawn.",
      es: "Cuando un símbolo aparece repetidamente en tus sueños, rara vez es coincidencia. Las imágenes recurrentes a menudo representan un área de la vida que busca tu atención. En lugar de analizarlo lógicamente, siéntate con el sentimiento que evoca. El crecimiento a menudo comienza donde nuestra atención está siendo atraída."
    },
    prompt: {
      en: "What symbols keep returning to your dreams?",
      es: "¿Qué símbolos siguen regresando a tus sueños?"
    },
    closingType: 'invitation',
    closingLabel: { en: "An Invitation", es: "Una Invitación" }
  },
  {
    short: { 
      en: "Every dream carries a message worth exploring.",
      es: "Cada sueño lleva un mensaje que vale la pena explorar."
    },
    expanded: {
      en: "Even the most mundane or confusing dreams carry meaning. Your sleeping mind doesn't waste its time—every image, emotion, and narrative was chosen for a reason. The key isn't to decode everything perfectly, but to remain curious and open to what emerges.",
      es: "Incluso los sueños más mundanos o confusos llevan significado. Tu mente dormida no pierde el tiempo—cada imagen, emoción y narrativa fue elegida por una razón. La clave no es decodificar todo perfectamente, sino permanecer curioso y abierto a lo que emerge."
    },
    prompt: {
      en: "What dream from this week still lingers in your mind?",
      es: "¿Qué sueño de esta semana aún permanece en tu mente?"
    },
    closingType: 'reflection',
    closingLabel: { en: "A Moment to Reflect", es: "Un Momento para Reflexionar" }
  },
  {
    short: { 
      en: "Your subconscious speaks in patterns — listen gently.",
      es: "Tu subconsciente habla en patrones — escucha con gentileza."
    },
    expanded: {
      en: "Over time, your dreams form a conversation with yourself. Patterns emerge not to confuse you, but to communicate. The language of the subconscious is symbolic, emotional, and deeply personal. Gentle attention reveals more than forceful analysis ever could.",
      es: "Con el tiempo, tus sueños forman una conversación contigo mismo. Los patrones emergen no para confundirte, sino para comunicarse. El lenguaje del subconsciente es simbólico, emocional y profundamente personal. La atención gentil revela más de lo que cualquier análisis forzado podría."
    },
    prompt: {
      en: "What patterns have you noticed in your dream journal?",
      es: "¿Qué patrones has notado en tu diario de sueños?"
    },
    closingType: 'reminder',
    closingLabel: { en: "A Gentle Reminder", es: "Un Recordatorio Gentil" }
  },
  {
    short: { 
      en: "The themes in your dreams reveal your inner wisdom.",
      es: "Los temas en tus sueños revelan tu sabiduría interior."
    },
    expanded: {
      en: "Dreams often process what our waking minds haven't fully addressed. The themes that appear—whether they involve relationships, transitions, or fears—reflect areas where your inner wisdom is actively working. Trust that your psyche is always moving toward wholeness.",
      es: "Los sueños a menudo procesan lo que nuestras mentes despiertas no han abordado completamente. Los temas que aparecen—ya sea que involucren relaciones, transiciones o miedos—reflejan áreas donde tu sabiduría interior está trabajando activamente. Confía en que tu psique siempre se mueve hacia la plenitud."
    },
    prompt: {
      en: "What theme has been most present in your recent dreams?",
      es: "¿Qué tema ha estado más presente en tus sueños recientes?"
    },
    closingType: 'pause',
    closingLabel: { en: "Pause for a Moment", es: "Pausa por un Momento" }
  },
  {
    short: { 
      en: "What surfaces repeatedly deserves your quiet attention.",
      es: "Lo que emerge repetidamente merece tu atención tranquila."
    },
    expanded: {
      en: "Repetition in dreams is never random. When something keeps appearing—a person, a place, a feeling—your subconscious is underlining it for you. Instead of rushing to interpret, simply notice. Often, the meaning reveals itself when we stop searching so hard.",
      es: "La repetición en los sueños nunca es aleatoria. Cuando algo sigue apareciendo—una persona, un lugar, un sentimiento—tu subconsciente lo está subrayando para ti. En lugar de apresurarte a interpretar, simplemente nota. A menudo, el significado se revela cuando dejamos de buscar tan intensamente."
    },
    prompt: {
      en: "What has been repeating in your dreams recently?",
      es: "¿Qué se ha repetido en tus sueños recientemente?"
    },
    closingType: 'question',
    closingLabel: { en: "Today's Question", es: "Pregunta del Día" }
  },
  {
    short: { 
      en: "Your dreams remember what you're ready to discover.",
      es: "Tus sueños recuerdan lo que estás listo para descubrir."
    },
    expanded: {
      en: "Dreams have a remarkable way of surfacing exactly what we need, exactly when we need it. They draw from memories, emotions, and insights we may have forgotten or not yet fully understood. Trust the timing of what appears—your dreams are always on your side.",
      es: "Los sueños tienen una manera notable de revelar exactamente lo que necesitamos, exactamente cuando lo necesitamos. Se nutren de memorias, emociones y percepciones que quizás hayamos olvidado o aún no comprendido completamente. Confía en el momento de lo que aparece—tus sueños siempre están de tu lado."
    },
    prompt: {
      en: "What has a recent dream helped you remember or realize?",
      es: "¿Qué te ha ayudado a recordar o darte cuenta un sueño reciente?"
    },
    closingType: 'invitation',
    closingLabel: { en: "An Invitation", es: "Una Invitación" }
  }
];

// Legacy array for backwards compatibility
const INSIGHT_MESSAGES = INSIGHT_DATA.map(d => d.short.en);

// Icons for different closing card types
const CLOSING_CARD_ICONS = {
  invitation: Feather,
  question: HelpCircle,
  reminder: Bell,
  pause: Clock,
  reflection: Sparkles
};

// ============================================================================
// P0.2 — TODAY'S REFLECTION DETAILS SCREEN
// A dedicated experience for reading the daily reflection
// "Like reading a page from a premium journal—not opening another dashboard"
// ARCHITECTURE: Prepared for future personalization based on user's dreams,
// memories, and growth over time. The closing card type varies to keep fresh.
// ============================================================================
const TodaysReflectionScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  
  // Calculate today's insight index (same algorithm as HomeScreen)
  const today = new Date().toDateString();
  const hash = today.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);
  const insightIndex = Math.abs(hash) % INSIGHT_DATA.length;
  const insight = INSIGHT_DATA[insightIndex];
  
  // Get localized content
  const shortReflection = insight?.short?.[lang] || insight?.short?.en || '';
  const expandedReflection = insight?.expanded?.[lang] || insight?.expanded?.en || '';
  const closingPrompt = insight?.prompt?.[lang] || insight?.prompt?.en || '';
  const closingType = insight?.closingType || 'invitation';
  const closingLabel = insight?.closingLabel?.[lang] || insight?.closingLabel?.en || 'An Invitation';
  const ClosingIcon = CLOSING_CARD_ICONS[closingType] || Feather;
  
  // Format today's date beautifully
  const formattedDate = new Date().toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  return (
    <div 
      className="min-h-screen relative"
      style={{ background: 'linear-gradient(180deg, #0D0F18 0%, #12141F 50%, #0D0F18 100%)' }}
      data-testid="todays-reflection-screen"
    >
      {/* Ambient background glow */}
      <div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: 'radial-gradient(ellipse at center top, rgba(212,175,55,0.04) 0%, transparent 50%)'
        }}
      />
      
      {/* Header with Back button - Global Navigation Standard */}
      <header 
        className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between"
        style={{ 
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', 
          paddingLeft: 'max(20px, env(safe-area-inset-left, 0px))',
          paddingRight: 'max(20px, env(safe-area-inset-right, 0px))',
          paddingBottom: '16px',
          background: 'linear-gradient(180deg, rgba(13,15,24,0.95) 0%, rgba(13,15,24,0.8) 80%, transparent 100%)'
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          data-testid="todays-reflection-back"
        >
          <ArrowLeft size={20} />
          <span 
            className="text-sm font-medium"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            {lang === 'es' ? 'Inicio' : 'Home'}
          </span>
        </button>
        
        {/* Date badge */}
        <div 
          className="text-xs text-white/50"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          {formattedDate}
        </div>
      </header>
      
      {/* Main Content */}
      <main 
        className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-8"
        style={{ 
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 100px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 60px)'
        }}
      >
        {/* Section Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <p 
            style={{ 
              fontSize: '11px',
              fontWeight: 600,
              color: '#E6C06E',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: '8px'
            }}
          >
            {lang === 'es' ? 'REFLEXIÓN DE HOY' : "TODAY'S REFLECTION"}
          </p>
          
          {/* Decorative line */}
          <div 
            className="w-12 h-[2px] rounded-full"
            style={{ background: 'linear-gradient(90deg, #E6C06E 0%, transparent 100%)' }}
          />
        </motion.div>
        
        {/* The Reflection - Hero Quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-10"
        >
          <p 
            className="text-[28px] sm:text-[32px] lg:text-[36px] leading-[1.25]"
            style={{ 
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 500,
              color: '#FFFFFF',
              fontStyle: 'italic'
            }}
          >
            &ldquo;{shortReflection}&rdquo;
          </p>
        </motion.div>
        
        {/* Divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full h-[1px] mb-10"
          style={{ 
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
          }}
        />
        
        {/* Expanded Explanation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-10"
        >
          <h3 
            className="text-sm font-medium mb-4"
            style={{ 
              color: 'rgba(255,255,255,0.5)',
              fontFamily: 'Inter, system-ui, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            {lang === 'es' ? 'Por Qué Esto Importa' : 'Why This Matters'}
          </h3>
          
          <p 
            className="text-[16px] sm:text-[17px] leading-[1.8]"
            style={{ 
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 400,
              color: 'rgba(255,255,255,0.75)'
            }}
          >
            {expandedReflection}
          </p>
        </motion.div>
        
        {/* Closing Prompt Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="relative overflow-hidden"
          style={{
            borderRadius: '24px',
            background: 'rgba(212,175,55,0.06)',
            border: '1px solid rgba(212,175,55,0.15)',
            padding: '28px'
          }}
        >
          {/* Subtle glow */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at top left, rgba(212,175,55,0.08) 0%, transparent 60%)'
            }}
          />
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ 
                  background: 'rgba(212,175,55,0.15)',
                  border: '1px solid rgba(212,175,55,0.25)'
                }}
              >
                <ClosingIcon size={18} className="text-[#E6C06E]" />
              </div>
              <h4 
                className="text-sm font-medium"
                style={{ 
                  color: '#E6C06E',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
              >
                {closingLabel}
              </h4>
            </div>
            
            <p 
              className="text-[18px] sm:text-[20px] leading-[1.5]"
              style={{ 
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.9)',
                fontStyle: 'italic'
              }}
            >
              {closingPrompt}
            </p>
          </div>
        </motion.div>
        
        {/* Action Buttons - Softened to flow naturally from the reflection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-10 flex flex-col gap-4"
        >
          {/* Gentle transition text */}
          <p 
            className="text-center text-sm mb-2"
            style={{ 
              color: 'rgba(255,255,255,0.45)',
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontStyle: 'italic'
            }}
          >
            {lang === 'es' 
              ? 'Cuando estés listo...'
              : 'When you\'re ready...'}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {/* Primary CTA - Softer, inviting */}
            <button
              onClick={() => navigate('/log')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium"
              style={{
                background: 'rgba(212,175,55,0.12)',
                border: '1px solid rgba(212,175,55,0.3)',
                color: '#E6C06E',
                fontSize: '15px'
              }}
              data-testid="reflection-capture-dream-btn"
            >
              <Moon size={18} />
              <span>{lang === 'es' ? 'Captura un Sueño' : 'Capture a Dream'}</span>
            </button>
            
            {/* Secondary - Explore Your Patterns */}
            <button
              onClick={() => navigate('/insights')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium"
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.6)',
                fontSize: '15px'
              }}
              data-testid="reflection-view-patterns-btn"
            >
              <Sparkles size={18} />
              <span>{lang === 'es' ? 'Explora tus Patrones' : 'Explore Your Patterns'}</span>
            </button>
          </div>
        </motion.div>
        
        {/* Footer note - Warmer, poetic */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center mt-10 text-sm"
          style={{ 
            color: 'rgba(255,255,255,0.35)',
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic'
          }}
        >
          {lang === 'es' 
            ? 'Mañana trae otra página.'
            : 'Tomorrow brings another page.'}
        </motion.p>
      </main>
    </div>
  );
};

const HomeScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Onboarding V1: an authenticated member who has not completed onboarding is sent there.
  // The legacy welcome flow (NameEntryScreen → AwakeningOnboarding → FirstMeeting) is retired.
  const showWelcomeFlow = false;

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      checkOnboardingPending().then((pending) => {
        if (pending) navigate('/onboarding', { replace: true });
      });
    }
  }, [isAuthenticated, authLoading, navigate]);
  
  // P0.1: Proper loading states for dream data






  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = user?.name?.split(' ')[0] || null;
    
    if (hour >= 4 && hour < 12) {
      return { time: lang === 'es' ? 'Buenos días,' : 'Good morning,', name: firstName };
    }
    if (hour >= 12 && hour < 17) {
      return { time: lang === 'es' ? 'Buenas tardes,' : 'Good afternoon,', name: firstName };
    }
    if (hour >= 17 && hour < 21) {
      return { time: lang === 'es' ? 'Buenas noches,' : 'Good evening,', name: firstName };
    }
    return { time: lang === 'es' ? 'Buenas noches,' : 'Good evening,', name: firstName };
  };

  const greetingData = getGreeting();

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="home-screen">
      {/* ═══════════════════════════════════════════════════════════════════
          HOME SCREEN BACKGROUND - Production Asset (moon, sunrise, mountains, water reflection)
          Sits behind the entire Home Screen, extending underneath all sections
          ═══════════════════════════════════════════════════════════════════ */}
      {/* 5.1-D: the Home background asset was PROVEN entirely covered — hiding it produced a
          byte-identical screenshot at 390×844 and 1920×800 with animation frozen
          (`memory/PHASE_51_ASSET_REQUEST_EVIDENCE.json`) — so 1,591 KB is no longer fetched for a
          layer no member has ever seen. `ArrivalHome` paints Home's own background. The canonical
          orb asset is untouched. */}

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)}
        currentPath="/home"
        lang={lang}
      />



      {/* Legacy onboarding (NameEntryScreen → AwakeningOnboarding → FirstMeeting) is RETIRED.
          New members are routed to Onboarding V1 at /onboarding before reaching Home. */}

      {/* ═══════════════════════════════════════════════════════════════════
          HEADER - Controls only, logo moved to main content area per Brand Doctrine
          Phase 0: Logo increased 20% (42→50), responsive header height
          HIDDEN during onboarding to prevent any flash
          ═══════════════════════════════════════════════════════════════════ */}
      {!showWelcomeFlow && (
        <header 
          className="fixed top-0 left-0 right-0 z-40 flex items-center justify-end"
          style={{ 
            paddingTop: 'max(20px, calc(env(safe-area-inset-top, 0px) + 8px))', 
            paddingLeft: 'max(20px, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(20px, env(safe-area-inset-right, 0px))',
            height: 'calc(max(20px, calc(env(safe-area-inset-top, 0px) + 8px)) + 44px)'
          }}
        >
        {/* 5.1-A / redline: a member arriving must know where they are. Existing approved ARVEXIS
            treatment — text, no new asset — centred over a 44 px header row. */}
        <span
          className="absolute left-1/2 -translate-x-1/2 text-[22px] select-none whitespace-nowrap"
          style={{
            fontFamily: 'Outfit, Inter, system-ui, sans-serif',
            letterSpacing: '0.34em',
            textIndent: '0.34em',
            color: '#F4EFE7'
          }}
          data-testid="home-wordmark"
        >
          ARVEXIS
        </span>
        {/* Controls - right side only. No Profile destination and no product navigation (Phase 6). */}
        <div className="relative flex items-center gap-3">
          <button 
            className="w-11 h-11 rounded-full flex items-center justify-center border border-[#E8C58F] bg-transparent outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/75 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
            onClick={() => setIsMobileMenuOpen(true)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            <Menu size={20} className="text-[#E8C58F]" />
          </button>
        </div>
        </header>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MAIN CONTENT - Brand Doctrine: Logo → Breathing Room → Greeting → Content
          "The Home screen should feel like opening a luxury journal, not an app."
          HIDDEN during onboarding to prevent any flash
          ═══════════════════════════════════════════════════════════════════ */}
      {!showWelcomeFlow && (
        <>
          <UnverifiedEmailNotice user={user} lang={lang} />
          <ArrivalHome lang={lang} user={user} />
        </>
      )}
    </div>
  );
};

// ============================================================================
// PREMIUM LOG DREAM SCREEN — RC1 FINAL DESIGN POLISH
// Unified Record/Type experience with premium "beautiful journal" aesthetic
// ============================================================================
const LogDreamScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [selectedEmotions, setSelectedEmotions] = useState([]);
  const [customEmotion, setCustomEmotion] = useState(""); // For "Other..." option
  const [showCustomEmotionInput, setShowCustomEmotionInput] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('record'); // 'record' or 'type'
  const [recordingLanguage, setRecordingLanguage] = useState(lang === 'es' ? 'Spanish' : 'English');
  const [trustAcknowledged, setTrustAcknowledged] = useState(() => {
    return localStorage.getItem('arvexis_trust_acknowledged') === 'true';
  });
  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);
  const customEmotionRef = useRef(null);
  const submittingTimerRef = useRef(null);
  const MAX_CHARS = 4000;
  
  // ==========================================================================
  // FRONTLINE STATE MACHINE OVERLAY
  // States: IDLE → TRANSCRIBING → SYNTHESIS → REVEAL
  // Eliminates loading freezes with smooth overlay transitions
  // ==========================================================================
  const UI_STATES = {
    IDLE: 'idle',           // User composing dream
    TRANSCRIBING: 'transcribing', // Voice → text conversion
    SYNTHESIS: 'synthesis',  // AI processing dream
    REVEAL: 'reveal'        // Transition to reflection
  };
  
  const [uiState, setUiState] = useState(UI_STATES.IDLE);
  const [synthesisPhase, setSynthesisPhase] = useState(0);
  const [revealDreamId, setRevealDreamId] = useState(null);
  const [revealDreamData, setRevealDreamData] = useState(null);
  
  // Derived states for backwards compatibility
  const isSubmitting = uiState === UI_STATES.SYNTHESIS;
  const isTranscribing = uiState === UI_STATES.TRANSCRIBING;
  
  // Synthesis phase messages - Frame 1 cycling text (4 second intervals)
  const synthesisMessages = lang === 'es' 
    ? [
        "Leyendo tu sueño...",
        "Recordando tus reflexiones anteriores...",
        "Conectando temas recurrentes...",
        "Buscando lo que importa...",
        "Preparando tu reflexión..."
      ]
    : [
        "Reading your dream...",
        "Remembering your previous reflections...",
        "Connecting recurring themes...",
        "Looking for what matters...",
        "Preparing your reflection..."
      ];
  
  // Reassuring messages during generation (legacy alias)
  const reassuringMessages = synthesisMessages;
  
  // ==========================================================================
  // STATE MACHINE EFFECTS - Smooth overlay transitions
  // ==========================================================================
  
  // Advance synthesis phase message every 4 seconds (Frame 1 spec)
  useEffect(() => {
    if (uiState === UI_STATES.SYNTHESIS) {
      setSynthesisPhase(0);
      submittingTimerRef.current = setInterval(() => {
        setSynthesisPhase(prev => Math.min(prev + 1, synthesisMessages.length - 1));
      }, 4000);
    } else {
      if (submittingTimerRef.current) {
        clearInterval(submittingTimerRef.current);
      }
      setSynthesisPhase(0);
    }
    return () => {
      if (submittingTimerRef.current) {
        clearInterval(submittingTimerRef.current);
      }
    };
  }, [uiState]);
  
  // Handle REVEAL state transition - navigate after brief animation
  useEffect(() => {
    if (uiState === UI_STATES.REVEAL && revealDreamId && revealDreamData) {
      const revealTimer = setTimeout(() => {
        navigate(`/reflection/${revealDreamId}`, { 
          state: { dream: revealDreamData, fromNewDream: true } 
        });
      }, 800); // Brief reveal animation before navigation
      return () => clearTimeout(revealTimer);
    }
  }, [uiState, revealDreamId, revealDreamData, navigate]);

  // Clear dream handler
  const handleClearDream = () => {
    setTranscript("");
    setInterimTranscript("");
    setSelectedEmotions([]);
    setCustomEmotion("");
    setShowCustomEmotionInput(false);
    setShowClearConfirm(false);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // Toggle emotion selection (max 3)
  const toggleEmotion = (emotion) => {
    if (emotion === 'other') {
      // Toggle custom emotion input
      if (showCustomEmotionInput) {
        setShowCustomEmotionInput(false);
        setCustomEmotion("");
        setSelectedEmotions(prev => prev.filter(e => !e.startsWith('custom:')));
      } else {
        setShowCustomEmotionInput(true);
        setTimeout(() => customEmotionRef.current?.focus(), 100);
      }
      return;
    }
    
    setSelectedEmotions(prev => {
      if (prev.includes(emotion)) {
        return prev.filter(e => e !== emotion);
      }
      // Filter out any custom emotions if adding a predefined one and at max
      const predefinedEmotions = prev.filter(e => !e.startsWith('custom:'));
      if (predefinedEmotions.length >= 3) {
        return [...predefinedEmotions.slice(1), emotion];
      }
      return [...prev, emotion];
    });
  };
  
  // Handle custom emotion submission
  const handleCustomEmotionSubmit = () => {
    if (customEmotion.trim()) {
      const customValue = `custom:${customEmotion.trim()}`;
      setSelectedEmotions(prev => {
        // Remove any existing custom emotion
        const withoutCustom = prev.filter(e => !e.startsWith('custom:'));
        if (withoutCustom.length >= 3) {
          return [...withoutCustom.slice(1), customValue];
        }
        return [...withoutCustom, customValue];
      });
    }
  };

  // WHISPER TRANSCRIPTION: Use server-side Whisper for better bilingual support
  const [recordingSupported, setRecordingSupported] = useState(true);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Initialize audio recording capability
  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setRecordingSupported(false);
      setSpeechSupported(false);
    }
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Start voice visualization for breathing halo effect
      startVoiceVisualization(stream);
      
      // Determine best supported audio format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Stop voice visualization
        stopVoiceVisualization();
        
        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        
        // Send to Whisper for accurate transcription (handles EN/ES/Spanglish)
        await transcribeWithWhisper(audioBlob);
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsListening(true);
      
      // Haptic feedback where supported
      if ('vibrate' in navigator) {
        navigator.vibrate(10);
      }
      
    } catch (err) {
      console.error("Recording error:", err);
      if (err.name === 'NotAllowedError') {
        setRecordingSupported(false);
        setSpeechSupported(false);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsListening(false);
      setUiState(UI_STATES.TRANSCRIBING); // State machine: IDLE → TRANSCRIBING
    }
  };

  const transcribeWithWhisper = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      // Dream voice entry: the only path that legitimately receives dream conditioning.
      formData.append('context', 'dream');
      
      // Transcription now requires a session, and recording a dream can begin long after
      // the page was opened, so refresh once on 401 rather than losing the recording.
      const postTranscribe = () => axios.post(`${API}/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      let response;
      try {
        response = await postTranscribe();
      } catch (err) {
        if (err.response?.status === 401) {
          await axios.post(`${API}/auth/refresh`, {});
          response = await postTranscribe();
        } else {
          throw err;
        }
      }
      
      if (response.data.success && response.data.text) {
        // Set the transcript from Whisper (handles EN/ES/Spanglish accurately)
        const whisperText = response.data.text.trim();
        setTranscript(whisperText.slice(0, MAX_CHARS));
      }
    } catch (err) {
      console.error("Transcription error:", err);
      // Silently fail - user can still type their dream
    } finally {
      setUiState(UI_STATES.IDLE); // State machine: TRANSCRIBING → IDLE
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Update recording language display (informational only - Whisper auto-detects)
  useEffect(() => {
    setRecordingLanguage(lang === 'es' ? 'Español' : 'English');
  }, [lang]);

  const handleTrustAcknowledge = (checked) => {
    setTrustAcknowledged(checked);
    if (checked) {
      localStorage.setItem('arvexis_trust_acknowledged', 'true');
    }
  };

  const handleSubmit = async () => {
    // A feeling is optional metadata. The dream is what matters, and a member who has no word
    // for how they woke up is not blocked from keeping it. No selection is never turned into
    // "neutral" — Neutral means they chose Neutral.
    if (!transcript.trim() || !trustAcknowledged) return;
    
    // State machine: IDLE → SYNTHESIS
    setUiState(UI_STATES.SYNTHESIS);
    
    try {
      // Build emotions array, including custom emotion if present
      let emotionsToSubmit = [...selectedEmotions];
      if (customEmotion.trim() && !emotionsToSubmit.some(e => e.startsWith('custom:'))) {
        emotionsToSubmit.push(`custom:${customEmotion.trim()}`);
      }
      
      // Get primary emotion (first one, handling custom format). Absent when they chose none.
      const primaryEmotion = emotionsToSubmit[0]?.startsWith('custom:') 
        ? emotionsToSubmit[0].replace('custom:', '')
        : emotionsToSubmit[0];
      
      const response = await axios.post(`${API}/dreams`, {
        transcript,
        ...(primaryEmotion ? { emotion: primaryEmotion } : {}),
        emotions: emotionsToSubmit.map(e => e.startsWith('custom:') ? e.replace('custom:', '') : e),
        language: lang
      });
      
      // State machine: SYNTHESIS → REVEAL (triggers navigation via useEffect)
      setRevealDreamId(response.data.id);
      setRevealDreamData(response.data);
      setUiState(UI_STATES.REVEAL);
    } catch (err) {
      console.error("Submission error:", err);
      setUiState(UI_STATES.IDLE); // Reset on error
    }
  };

  const canSubmit = transcript.trim().length > 0 && trustAcknowledged;

  // ==========================================================================
  // Sprint 1.1: Listening Presence - Rotating phrases during recording
  // ==========================================================================
  const listeningPhrases = lang === 'es'
    ? [
        "Te escucho...",
        "Tómate tu tiempo.",
        "Estoy aquí.",
        "Cuéntame más...",
        "No hay prisa.",
        "Lo tengo.",
        "Continúa..."
      ]
    : [
        "I'm listening...",
        "Take your time.",
        "I'm following.",
        "Tell me more...",
        "There's no rush.",
        "I've got it.",
        "Keep going..."
      ];
  
  const [currentListeningPhrase, setCurrentListeningPhrase] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recordingTimerRef = useRef(null);
  
  // Rotate listening phrases every 5-7 seconds
  useEffect(() => {
    let phraseTimer;
    if (isListening) {
      const rotatePhrase = () => {
        setCurrentListeningPhrase(prev => (prev + 1) % listeningPhrases.length);
        // Random interval between 5-7 seconds
        phraseTimer = setTimeout(rotatePhrase, 5000 + Math.random() * 2000);
      };
      phraseTimer = setTimeout(rotatePhrase, 5000 + Math.random() * 2000);
    }
    return () => clearTimeout(phraseTimer);
  }, [isListening, listeningPhrases.length]);
  
  // Track recording duration
  useEffect(() => {
    if (isListening) {
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [isListening]);

  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // ==========================================================================
  // Sprint 1.1: Voice Ripple Intensity Classification
  // ==========================================================================
  const getRippleIntensity = () => {
    if (voiceIntensity < 0.15) return 'soft';
    if (voiceIntensity < 0.4) return 'medium';
    return 'strong';
  };

  // ==========================================================================
  // RC2: Listening State Messages - Personal, not scripted
  // ==========================================================================
  const listeningMessages = lang === 'es'
    ? [
        "Gracias.",
        "Gracias por confiar en mí.",
        "Te escuché.",
        "Estoy reflexionando sobre lo que compartiste.",
        "Dame un momento…",
        "Miremos debajo de la superficie.",
        "Cada detalle importa.",
        "Tu sueño merece una reflexión cuidadosa.",
        "Estoy conectando las piezas."
      ]
    : [
        "Thank you.",
        "Thank you for trusting me.",
        "I heard you.",
        "I'm reflecting on everything you shared.",
        "Give me a moment…",
        "Let's look beneath the surface.",
        "Every detail matters.",
        "Your dream deserves thoughtful reflection.",
        "I'm connecting the pieces."
      ];
  
  // Select a random listening message on each session
  const [listeningMessage] = useState(() => 
    listeningMessages[Math.floor(Math.random() * listeningMessages.length)]
  );

  // ==========================================================================
  // Sprint 1.1 FINAL: Voice-Reactive Waveform - True audio visualization
  // REVERTED: Simplified to calm breathing halo (no morphing blob)
  // ==========================================================================
  const [voiceIntensity, setVoiceIntensity] = useState(0);
  const [smoothedIntensity, setSmoothedIntensity] = useState(0);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);

  const startVoiceVisualization = (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      analyserRef.current = analyser;
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let lastIntensity = 0;
      
      const updateIntensity = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          
          // Calculate overall intensity from voice frequencies
          const voiceRange = dataArray.slice(2, 40);
          const sum = voiceRange.reduce((a, b) => a + b, 0);
          const rawIntensity = sum / voiceRange.length / 128;
          
          // Apply smoothing for natural movement
          const smoothed = lastIntensity + (rawIntensity - lastIntensity) * 0.3;
          lastIntensity = smoothed;
          const amplified = Math.min(smoothed * 2, 1);
          
          setVoiceIntensity(amplified);
          setSmoothedIntensity(prev => prev + (amplified - prev) * 0.15);
          
          animationFrameRef.current = requestAnimationFrame(updateIntensity);
        }
      };
      updateIntensity();
    } catch (err) {
      console.log('Voice visualization not supported:', err);
    }
  };

  const stopVoiceVisualization = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
    }
    analyserRef.current = null;
    audioContextRef.current = null;
    setVoiceIntensity(0);
    setSmoothedIntensity(0);
  };

  // ==========================================================================
  // REVERTED: Whisper-only transcription (no live browser speech recognition)
  // Accuracy is more important than immediacy.
  // Whisper handles English, Spanish, and Spanglish after pressing Done.
  // ==========================================================================

  return (
    <div
      className="log-dream-premium utility-surface utility-surface--atmospheric"
      data-testid="log-dream-screen"
    >
      {/* Phase 6 Dream visual closeout: the bespoke photographic plates are replaced by the one
          canonical atmosphere every other surface already uses. Presentation only. */}

      {/* Header */}
      <header className="log-dream-premium__header">
        <button 
          onClick={() => navigate('/home')}
          className="log-dream-premium__back-btn"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="log-dream-premium__logo">
          <SparkleIcon className="log-dream-premium__logo-icon" />
          <span className="log-dream-premium__logo-text">ARVEXIS</span>
        </div>
        
        <button 
          onClick={() => navigate('/dreams')}
          className="log-dream-premium__dreams-btn"
          data-testid="my-dreams-btn"
        >
          <BookOpen className="w-4 h-4" />
          <span>{t.myDreams}</span>
        </button>
      </header>

      {/* Main Content - Scrollable */}
      <main className="log-dream-premium__content">
        {/* RC2: Page Title - Premium typography */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="rc2-title">{t.logDreamTitle}</h1>
          <p className="rc2-subtitle">
            {t.logDreamSubtitle}
          </p>
        </motion.div>

        {/* Main Recording Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="log-dream-premium__main-card"
        >
          {/* Tab Switcher */}
          <div className="log-dream-premium__tabs">
            <button
              onClick={() => setActiveTab('record')}
              className={`log-dream-premium__tab ${activeTab === 'record' ? 'log-dream-premium__tab--active' : ''}`}
              data-testid="record-tab"
            >
              <Mic className="w-4 h-4" />
              <span>{t.recordTab}</span>
            </button>
            <button
              onClick={() => setActiveTab('type')}
              className={`log-dream-premium__tab ${activeTab === 'type' ? 'log-dream-premium__tab--active' : ''}`}
              data-testid="type-tab"
            >
              <PenLine className="w-4 h-4" />
              <span>{t.typeTab}</span>
            </button>
          </div>

          {/* Premium Recording Section - REVERTED to calm breathing design */}
          {activeTab === 'record' && (
            <div className="log-dream-premium__record-section">
              {/* Recording Artifact - Visual Hero */}
              <div className="rc2-recording-artifact">
                {/* Ambient particles */}
                <div className="rc2-artifact__particles">
                  <span className="rc2-artifact__particle" />
                  <span className="rc2-artifact__particle" />
                  <span className="rc2-artifact__particle" />
                  <span className="rc2-artifact__particle" />
                  <span className="rc2-artifact__particle" />
                  <span className="rc2-artifact__particle" />
                  <span className="rc2-artifact__particle" />
                </div>

                {/* Calm breathing halo - responds gently to voice intensity */}
                {isListening && (
                  <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
                    {/* Outer breathing ring */}
                    <div 
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: `${180 + smoothedIntensity * 30}px`,
                        height: `${180 + smoothedIntensity * 30}px`,
                        borderRadius: '50%',
                        border: `1.5px solid rgba(230, 192, 110, ${0.15 + voiceIntensity * 0.2})`,
                        transition: 'all 0.3s ease-out'
                      }}
                    />
                    {/* Middle breathing ring */}
                    <div 
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: `${200 + smoothedIntensity * 25}px`,
                        height: `${200 + smoothedIntensity * 25}px`,
                        borderRadius: '50%',
                        border: `1px solid rgba(230, 192, 110, ${0.1 + voiceIntensity * 0.15})`,
                        transition: 'all 0.35s ease-out'
                      }}
                    />
                    {/* Ambient glow */}
                    <div 
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '220px',
                        height: '220px',
                        borderRadius: '50%',
                        background: `radial-gradient(circle, rgba(230, 192, 110, ${0.08 + voiceIntensity * 0.1}) 0%, transparent 70%)`,
                        transition: 'all 0.4s ease-out'
                      }}
                    />
                  </div>
                )}

                {/* Main microphone artifact */}
                <button
                  onClick={toggleListening}
                  disabled={!speechSupported}
                  className={`rc2-artifact__main ${isListening ? 'rc2-artifact__main--recording' : ''}`}
                  data-testid="voice-input-btn"
                  aria-label={isListening ? 'Stop recording' : 'Start recording'}
                >
                  <div className="rc2-artifact__halo" />
                  <div className="rc2-artifact__ring-outer" />
                  <div className="rc2-artifact__inner">
                    {isListening ? (
                      <MicOff className="rc2-artifact__icon" size={36} />
                    ) : (
                      <Mic className="rc2-artifact__icon" size={36} />
                    )}
                  </div>
                </button>
              </div>

              {/* Listening Presence - Rotating phrases (KEPT) */}
              <div className="rc2-listening-presence mt-6 mb-4">
                {isListening ? (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentListeningPhrase}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.5 }}
                    >
                      <p className="rc2-listening-presence__phrase">
                        {listeningPhrases[currentListeningPhrase]}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                ) : (
                  <>
                    <h3 className="text-white text-lg font-medium mb-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                      {t.tapToRecord}
                    </h3>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)', maxWidth: '280px', margin: '0 auto', lineHeight: 1.6 }}>
                      {t.speakNaturally} {t.wellUnderstand}
                    </p>
                  </>
                )}
              </div>

              {/* Recording Status with Timer (KEPT) */}
              {isListening && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center justify-center gap-3 mt-4"
                >
                  <div className="rc2-recording-indicator">
                    <span className="rc2-recording-indicator__dot" />
                    <span className="rc2-recording-indicator__text">
                      {lang === 'es' ? 'Grabando' : 'Recording'}
                    </span>
                    <span className="rc2-recording-indicator__time">
                      {formatTime(recordingDuration)}
                    </span>
                  </div>
                  <button
                    onClick={toggleListening}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                    style={{ 
                      background: 'rgba(255,255,255,0.06)', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.8)'
                    }}
                    data-testid="stop-recording-btn"
                  >
                    {lang === 'es' ? 'Finalizar' : 'Done'}
                  </button>
                </motion.div>
              )}

              {/* Language indicator */}
              <div className="rc2-language-badge" style={{ margin: '16px auto 0' }}>
                <Globe className="rc2-language-badge__icon" />
                <span>{lang === 'es' ? 'Auto-detectar idioma' : 'Auto-detect language'}</span>
              </div>
              
              {/* REMOVED: Live transcript container (reverted to Whisper-only) */}
              
              {/* Transcribing State */}
              {isTranscribing && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-4 mt-6 py-4"
                >
                  <div className="rc2-artifact__main" style={{ width: '100px', height: '100px' }}>
                    <div className="rc2-artifact__halo" />
                    <div className="rc2-artifact__ring-outer" />
                    <div className="rc2-artifact__inner" style={{ width: '80px', height: '80px' }}>
                      <Loader2 className="w-8 h-8 text-[#E6C06E] animate-spin" />
                    </div>
                  </div>
                  <p className="text-sm font-medium text-[#D4A843]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}>
                    {lang === 'es' ? 'Procesando tus palabras...' : 'Processing your words...'}
                  </p>
                </motion.div>
              )}
            </div>
          )}

          {/* Divider with "or" */}
          <div className="log-dream-premium__divider">
            <div className="log-dream-premium__divider-line" />
            <span className="log-dream-premium__divider-text">{lang === 'es' ? 'o' : 'or'}</span>
            <div className="log-dream-premium__divider-line" />
          </div>

          {/* Type Section */}
          <div className={`log-dream-premium__type-section ${activeTab === 'type' ? 'log-dream-premium__type-section--active' : ''}`}>
            <div className="log-dream-premium__type-header">
              <PenLine className="w-4 h-4 text-[#D4A843]" />
              <span className="log-dream-premium__type-title">{t.typeYourDream}</span>
            </div>
            
            <div className="log-dream-premium__textarea-wrapper">
              <textarea
                ref={textareaRef}
                value={transcript}
                onChange={(e) => setTranscript(e.target.value.slice(0, MAX_CHARS))}
                placeholder={t.describeDream}
                className="log-dream-premium__textarea"
                data-testid="dream-textarea"
                onFocus={() => setActiveTab('type')}
              />
              <div className="log-dream-premium__textarea-footer">
                <span className="log-dream-premium__char-count">
                  {transcript.length}{t.characterCount}
                </span>
              </div>
            </div>
            
            {/* Clear Button - only show when there's content */}
            {transcript.trim() && (
              <button
                onClick={() => setShowClearConfirm(true)}
                className="log-dream-premium__clear-btn"
                data-testid="clear-dream-btn"
              >
                <Trash2 className="w-4 h-4" />
                <span>{t.clearDream}</span>
              </button>
            )}
          </div>
        </motion.div>

        {/* Emotion Picker - Sprint 1.1: Premium styling */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="log-dream-premium__emotions-section"
        >
          <div className="log-dream-premium__emotions-header">
            <span>{t.howDidYouFeel}</span>
            {selectedEmotions.length > 0 && (
              <span className="log-dream-premium__emotions-count">{selectedEmotions.filter(e => !e.startsWith('custom:')).length + (customEmotion.trim() ? 1 : 0)}/3</span>
            )}
          </div>
          <div className="log-dream-premium__emotions-grid">
            {EMOTIONS.map((emotion, i) => {
              const isSelected = emotion === 'other' 
                ? showCustomEmotionInput 
                : selectedEmotions.includes(emotion);
              const selectionIndex = selectedEmotions.indexOf(emotion);
              return (
                <motion.button
                  key={emotion}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.03 }}
                  onClick={() => toggleEmotion(emotion)}
                  className={`rc2-emotion-chip ${isSelected ? 'rc2-emotion-chip--selected' : ''}`}
                  data-testid={`emotion-${emotion}`}
                  style={{
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {t.emotions[emotion]}
                  {isSelected && selectionIndex === 0 && emotion !== 'other' && (
                    <span style={{ 
                      marginLeft: '6px', 
                      fontSize: '10px', 
                      opacity: 0.7,
                      background: 'rgba(230,192,110,0.2)',
                      padding: '2px 6px',
                      borderRadius: '8px'
                    }}>1</span>
                  )}
                </motion.button>
              );
            })}
          </div>
          
          {/* Custom Emotion Input */}
          {showCustomEmotionInput && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="log-dream-premium__custom-emotion"
            >
              <input
                ref={customEmotionRef}
                type="text"
                value={customEmotion}
                onChange={(e) => setCustomEmotion(e.target.value.slice(0, 50))}
                onBlur={handleCustomEmotionSubmit}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomEmotionSubmit()}
                placeholder={t.customEmotionPlaceholder}
                className="log-dream-premium__custom-emotion-input"
                data-testid="custom-emotion-input"
                maxLength={50}
              />
            </motion.div>
          )}
          
          {(selectedEmotions.length > 0 || customEmotion.trim()) && (
            <p className="log-dream-premium__emotions-summary">
              {selectedEmotions
                .map(e => e.startsWith('custom:') ? e.replace('custom:', '') : (t.emotions[e] || e))
                .join(' • ')}
            </p>
          )}
        </motion.div>

        {/* RC2: Reassurance Card - Editorial styling */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="rc2-card mb-4"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(230,192,110,0.1)' }}>
              <Sparkles className="w-5 h-5 text-[#E6C06E]" />
            </div>
            <div>
              <h4 className="rc2-card__title">{t.everyDetailMatters}</h4>
              <p className="rc2-card__desc">{t.everyDetailDesc}</p>
            </div>
          </div>
        </motion.div>

        {/* RC2: Reflection Preview Card - Editorial styling */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="rc2-card mb-6"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(139,92,246,0.1)' }}>
              <DiamondSparkle className="w-5 h-5 text-[#8B5CF6]" />
            </div>
            <div className="flex-1">
              <h4 className="rc2-card__title">{t.reflectionPreview}</h4>
              <p className="rc2-card__desc">{t.reflectionPreviewDesc}</p>
            </div>
          </div>
        </motion.div>

        {/* RC2: Trust Acknowledgment - Elevated presentation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="rc2-trust mb-6"
        >
          <button 
            onClick={() => handleTrustAcknowledge(!trustAcknowledged)}
            className={`rc2-trust__checkbox ${trustAcknowledged ? 'rc2-trust__checkbox--checked' : ''}`}
            data-testid="trust-checkbox"
          >
            {trustAcknowledged && <Check className="w-3.5 h-3.5 text-[#E6C06E]" />}
          </button>
          <div>
            <span className="rc2-trust__text">{t.trustAcknowledgment}</span>
            <button 
              onClick={() => navigate('/privacy')}
              className="rc2-trust__link ml-1"
              data-testid="learn-more-btn"
            >
              {t.learnMore}
            </button>
          </div>
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="log-dream-premium__submit-section"
        >
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || uiState !== UI_STATES.IDLE}
            className={`log-dream-premium__submit-btn ${canSubmit ? 'log-dream-premium__submit-btn--enabled' : ''}`}
            data-testid="generate-btn"
          >
            {uiState === UI_STATES.SYNTHESIS ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{synthesisMessages[synthesisPhase]}</span>
              </>
            ) : uiState === UI_STATES.TRANSCRIBING ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{lang === 'es' ? 'Transcribiendo...' : 'Transcribing...'}</span>
              </>
            ) : uiState === UI_STATES.REVEAL ? (
              <>
                <Sparkles className="w-5 h-5 animate-pulse" />
                <span>{lang === 'es' ? 'Listo!' : 'Ready!'}</span>
              </>
            ) : (
              <span>{t.generateReflection}</span>
            )}
          </button>
        </motion.div>
      </main>

      {/* =================================================================
          RC2: PREMIUM GENERATION EXPERIENCE
          Continuous, calm transition - Arvexis is reflecting, not loading.
          Includes Listening State for emotional moment of acknowledgment.
          ================================================================= */}
      {(uiState === UI_STATES.SYNTHESIS || uiState === UI_STATES.REVEAL) && (
        <PreparingReflection
          lang={lang}
          ready={uiState === UI_STATES.REVEAL && !!revealDreamId}
          onExit={() => navigate('/dreams')}
        />
      )}

      {/* Bottom navigation removed for V1: Home and the menu are the navigation.
          The header back control remains the way out of this screen. */}

      {/* Clear Dream Confirmation Modal */}
      <AnimatePresence>
        {showClearConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowClearConfirm(false)}
            className="log-dream-premium__modal-overlay"
            data-testid="clear-confirm-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="log-dream-premium__modal"
            >
              <h3 className="log-dream-premium__modal-title">{t.clearDreamConfirm}</h3>
              <p className="log-dream-premium__modal-desc">{t.clearDreamMessage}</p>
              <div className="log-dream-premium__modal-actions">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="log-dream-premium__modal-cancel"
                  data-testid="clear-cancel-btn"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={handleClearDream}
                  className="log-dream-premium__modal-confirm"
                  data-testid="clear-confirm-btn"
                >
                  {t.clearDream}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================================
// WELLNESS CHECK-IN SCREEN
// ============================================================================
// P0.6.1 — WELLNESS CHECK-IN WITH EMOTIONAL RESPONSE LAYER
// Vision: "Arvexis listened before she spoke."
// 
// The moment a user selects an emotion, Arvexis should pause before asking anything.
// The first responsibility is not to collect more information.
// The first responsibility is to acknowledge what the user just shared.
//
// Flow: Choose emotion → Arvexis gently responds → Optional reflection → Personal closing
// ============================================================================

// Elegant emotional states for check-in
// Ordered from lighter emotional states toward heavier ones for a human emotional journey
const WELLNESS_EMOTIONS = [
  { key: 'calm', label: { en: 'Calm', es: 'En calma' }, description: { en: 'At peace', es: 'En paz' } },
  { key: 'hopeful', label: { en: 'Hopeful', es: 'Con esperanza' }, description: { en: 'Looking forward', es: 'Mirando hacia adelante' } },
  { key: 'grateful', label: { en: 'Grateful', es: 'Agradecido' }, description: { en: 'Appreciating life', es: 'Apreciando la vida' } },
  { key: 'curious', label: { en: 'Curious', es: 'Curioso' }, description: { en: 'Open to discovery', es: 'Abierto al descubrimiento' } },
  { key: 'inspired', label: { en: 'Inspired', es: 'Inspirado' }, description: { en: 'Moved to create', es: 'Movido a crear' } },
  { key: 'reflective', label: { en: 'Reflective', es: 'Reflexivo' }, description: { en: 'Thinking deeply', es: 'Pensando profundamente' } },
  { key: 'restless', label: { en: 'Restless', es: 'Inquieto' }, description: { en: 'Needing movement', es: 'Necesitando movimiento' } },
  { key: 'overwhelmed', label: { en: 'Overwhelmed', es: 'Abrumado' }, description: { en: 'Carrying much', es: 'Cargando mucho' } },
  { key: 'melancholic', label: { en: 'Melancholic', es: 'Melancólico' }, description: { en: 'Feeling tender', es: 'Sintiéndome sensible' } },
  { key: 'uncertain', label: { en: 'Uncertain', es: 'Con incertidumbre' }, description: { en: 'Finding the way', es: 'Buscando el camino' } },
  { key: 'broken', label: { en: 'Broken', es: 'Roto' }, description: { en: 'Needing tenderness', es: 'Necesitando ternura' } },
  // "Other" option - allows users to express emotions not represented
  { key: 'other', label: { en: 'Other...', es: 'Otro...' }, description: { en: 'In your own words', es: 'Con tus propias palabras' }, isCustom: true },
];

// Handcrafted emotional responses - "I noticed."
// These communicate understanding before Arvexis asks anything in return
const EMOTIONAL_RESPONSES = {
  calm: {
    en: "Peace doesn't always arrive with great moments.\n\nSometimes it quietly settles into ordinary days.\n\nIf today feels calm, allow yourself to simply be here.",
    es: "La paz no siempre llega con grandes momentos.\n\nA veces se asienta silenciosamente en días comunes.\n\nSi hoy se siente tranquilo, permítete simplemente estar aquí."
  },
  hopeful: {
    en: "Hope rarely arrives with certainty.\n\nMore often, it appears as the quiet willingness to keep moving forward, even when the path is still unfolding.\n\nThat is enough for today.",
    es: "La esperanza rara vez llega con certeza.\n\nMás a menudo, aparece como la disposición silenciosa de seguir adelante, incluso cuando el camino aún se está desplegando.\n\nEso es suficiente para hoy."
  },
  grateful: {
    en: "Gratitude doesn't erase life's difficulties.\n\nIt simply reminds us that even challenging seasons can still hold moments worth remembering.",
    es: "La gratitud no borra las dificultades de la vida.\n\nSimplemente nos recuerda que incluso las estaciones difíciles pueden contener momentos que vale la pena recordar."
  },
  curious: {
    en: "Curiosity is one of the gentlest forms of courage.\n\nEvery question you ask yourself opens a new possibility.\n\nYou don't have to know the answers yet.",
    es: "La curiosidad es una de las formas más gentiles de valentía.\n\nCada pregunta que te haces abre una nueva posibilidad.\n\nNo tienes que saber las respuestas todavía."
  },
  inspired: {
    en: "Inspiration often arrives without explanation.\n\nIt simply asks to be followed, gently, without demanding certainty.\n\nTrust what moves you today.",
    es: "La inspiración a menudo llega sin explicación.\n\nSimplemente pide ser seguida, gentilmente, sin exigir certeza.\n\nConfía en lo que te mueve hoy."
  },
  reflective: {
    en: "Reflection is how we become friends with ourselves.\n\nTaking time to look inward is never wasted.\n\nWhat you discover today will become wisdom tomorrow.",
    es: "La reflexión es cómo nos hacemos amigos de nosotros mismos.\n\nTomar tiempo para mirar hacia adentro nunca es tiempo perdido.\n\nLo que descubras hoy se convertirá en sabiduría mañana."
  },
  restless: {
    en: "Restlessness often means something inside you is asking for attention.\n\nYou don't have to solve it all today.\n\nSimply noticing it is already a meaningful beginning.",
    es: "La inquietud a menudo significa que algo dentro de ti está pidiendo atención.\n\nNo tienes que resolverlo todo hoy.\n\nSimplemente notarlo ya es un comienzo significativo."
  },
  overwhelmed: {
    en: "When everything feels important, it's difficult to know where to begin.\n\nToday doesn't ask for perfection.\n\nIt only asks for one gentle step.",
    es: "Cuando todo parece importante, es difícil saber por dónde empezar.\n\nHoy no pide perfección.\n\nSolo pide un paso gentil."
  },
  melancholic: {
    en: "Some days naturally carry more weight than others.\n\nYou don't need to rush yourself back toward happiness.\n\nYour feelings deserve patience too.",
    es: "Algunos días naturalmente cargan más peso que otros.\n\nNo necesitas apresurarte de vuelta hacia la felicidad.\n\nTus sentimientos también merecen paciencia."
  },
  uncertain: {
    en: "Not knowing can feel uncomfortable.\n\nYet many meaningful journeys begin long before the destination becomes clear.",
    es: "No saber puede sentirse incómodo.\n\nSin embargo, muchos viajes significativos comienzan mucho antes de que el destino se aclare."
  },
  broken: {
    en: "I'm sorry today feels so heavy.\n\nFeeling broken doesn't mean you've stopped growing.\n\nSometimes we only notice the cracks because light has begun finding new ways to enter.\n\nYou don't have to carry today alone.",
    es: "Lamento que hoy se sienta tan pesado.\n\nSentirse roto no significa que hayas dejado de crecer.\n\nA veces solo notamos las grietas porque la luz ha comenzado a encontrar nuevas formas de entrar.\n\nNo tienes que cargar hoy solo."
  },
  other: {
    en: "Thank you for sharing what words can barely hold.\n\nSometimes the truest feelings don't fit neatly into any category.\n\nWhat matters is that you noticed, and now Arvexis will remember this moment with you.",
    es: "Gracias por compartir lo que las palabras apenas pueden contener.\n\nA veces los sentimientos más verdaderos no encajan perfectamente en ninguna categoría.\n\nLo que importa es que lo notaste, y ahora Arvexis recordará este momento contigo."
  }
};

// Dynamic closing messages based on emotional tone
const EMOTIONAL_CLOSINGS = {
  calm: {
    en: "Thank you for sharing this peaceful moment with me.\n\nMay it stay with you a little longer today.",
    es: "Gracias por compartir este momento de paz conmigo.\n\nQue permanezca contigo un poco más hoy."
  },
  hopeful: {
    en: "Hold on to this feeling.\n\nHope often begins quietly before it changes everything.",
    es: "Mantén este sentimiento.\n\nLa esperanza a menudo comienza en silencio antes de cambiarlo todo."
  },
  grateful: {
    en: "Thank you for pausing to notice what's good.\n\nGratitude grows stronger the more we return to it.",
    es: "Gracias por detenerte a notar lo bueno.\n\nLa gratitud se fortalece mientras más volvemos a ella."
  },
  curious: {
    en: "Keep asking questions.\n\nThe best discoveries often begin with simple curiosity.",
    es: "Sigue haciendo preguntas.\n\nLos mejores descubrimientos a menudo comienzan con simple curiosidad."
  },
  inspired: {
    en: "Follow where this feeling leads.\n\nInspiration is a gift that asks to be honored.",
    es: "Sigue hacia donde este sentimiento te lleva.\n\nLa inspiración es un regalo que pide ser honrado."
  },
  reflective: {
    en: "Thank you for taking this time with yourself.\n\nReflection is one of the kindest things we can do.",
    es: "Gracias por tomarte este tiempo contigo mismo.\n\nLa reflexión es una de las cosas más amables que podemos hacer."
  },
  restless: {
    en: "Thank you for being honest about where you are.\n\nRestlessness often carries the seeds of important change.",
    es: "Gracias por ser honesto sobre dónde estás.\n\nLa inquietud a menudo lleva las semillas de un cambio importante."
  },
  overwhelmed: {
    en: "Thank you for trusting me with this moment.\n\nYou don't have to carry everything all at once.",
    es: "Gracias por confiarme este momento.\n\nNo tienes que cargar todo de una vez."
  },
  melancholic: {
    en: "Thank you for not hiding this feeling.\n\nSome days ask us to move more slowly, and that's okay.",
    es: "Gracias por no ocultar este sentimiento.\n\nAlgunos días nos piden movernos más lento, y está bien."
  },
  uncertain: {
    en: "Thank you for staying present even when the path isn't clear.\n\nClarity often arrives one small step at a time.",
    es: "Gracias por permanecer presente incluso cuando el camino no está claro.\n\nLa claridad a menudo llega un pequeño paso a la vez."
  },
  broken: {
    en: "Thank you for letting me be here with you today.\n\nEven difficult days become part of the story you're writing.\n\nI'll remember this moment with you.",
    es: "Gracias por dejarme estar aquí contigo hoy.\n\nIncluso los días difíciles se convierten en parte de la historia que estás escribiendo.\n\nRecordaré este momento contigo."
  },
  other: {
    en: "Thank you for sharing something so personal.\n\nArvexis will hold this moment gently.",
    es: "Gracias por compartir algo tan personal.\n\nArvexis guardará este momento con cuidado."
  }
};

// Personal reflection prompts (more intimate than generic)
const REFLECTION_PROMPTS = [
  { en: "Would you like to leave a few words for your future self?", es: "¿Te gustaría dejar unas palabras para tu yo del futuro?" },
  { en: "Is there anything about today you'd like Arvexis to remember with you?", es: "¿Hay algo de hoy que te gustaría que Arvexis recuerde contigo?" },
  { en: "If this moment had a voice, what would it say?", es: "Si este momento tuviera voz, ¿qué diría?" }
];

const WellnessCheckInScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [customEmotion, setCustomEmotion] = useState('');
  const [reflection, setReflection] = useState('');
  const [step, setStep] = useState('emotion'); // 'emotion' | 'custom' | 'acknowledged' | 'reflection' | 'ai_response' | 'complete'
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const [aiResponse, setAiResponse] = useState(''); // Arvexis' response to user's words
  
  // Select a consistent reflection prompt for this session
  const [reflectionPromptIndex] = useState(() => Math.floor(Math.random() * REFLECTION_PROMPTS.length));
  const currentReflectionPrompt = REFLECTION_PROMPTS[reflectionPromptIndex];

  // Handle emotion selection - now goes to acknowledgment step first
  const handleEmotionSelect = (emotionKey, isCustom = false) => {
    setSelectedEmotion(emotionKey);
    if (isCustom) {
      setTimeout(() => setStep('custom'), 400);
    } else {
      // Go to acknowledgment step first - Arvexis responds before asking
      setTimeout(() => setStep('acknowledged'), 600);
    }
  };

  // Handle custom emotion submission - also goes to acknowledgment
  const handleCustomEmotionSubmit = () => {
    if (customEmotion.trim()) {
      setStep('acknowledged');
    }
  };

  // Continue from acknowledgment to reflection
  const handleContinueToReflection = () => {
    setStep('reflection');
  };

  // Generate Arvexis' response to the user's written reflection
  const generateAIResponse = async (userReflection, emotion, customEmotionText) => {
    setIsGeneratingResponse(true);
    
    try {
      const emotionLabel = emotion === 'other' ? customEmotionText : emotion;
      
      const response = await axios.post(`${API}/wellness/respond`, {
        emotion: emotionLabel,
        reflection: userReflection,
        language: lang
      });
      
      if (response.data?.response) {
        setAiResponse(response.data.response);
      } else {
        // Fallback if API fails - use a gentle default
        setAiResponse(lang === 'es' 
          ? 'Gracias por compartir esto conmigo. Tus palabras importan, y este momento quedará guardado en tu viaje.'
          : 'Thank you for sharing this with me. Your words matter, and this moment will be kept as part of your journey.');
      }
    } catch (err) {
      console.error('Failed to generate AI response:', err);
      // Graceful fallback
      setAiResponse(lang === 'es'
        ? 'Gracias por compartir esto conmigo. Tus palabras importan, y este momento quedará guardado en tu viaje.'
        : 'Thank you for sharing this with me. Your words matter, and this moment will be kept as part of your journey.');
    } finally {
      setIsGeneratingResponse(false);
    }
  };

  // Handle save - now checks if user wrote something to generate AI response
  const handleSave = async () => {
    if (!selectedEmotion) return;
    setIsSaving(true);
    
    const checkIn = {
      id: `wellness_${Date.now()}`,
      emotion: selectedEmotion,
      customEmotion: selectedEmotion === 'other' ? customEmotion.trim() : null,
      reflection: reflection.trim() || null,
      timestamp: new Date().toISOString(),
      date: new Date().toISOString().split('T')[0]
    };
    
    const existing = JSON.parse(localStorage.getItem('arvexis_wellness_checkins') || '[]');
    existing.unshift(checkIn);
    localStorage.setItem('arvexis_wellness_checkins', JSON.stringify(existing.slice(0, 100)));
    
    // If user wrote a reflection, generate AI response before closing
    if (reflection.trim()) {
      setIsSaving(false);
      setStep('ai_response');
      await generateAIResponse(reflection.trim(), selectedEmotion, customEmotion.trim());
    } else {
      // No reflection written - go directly to completion
      setTimeout(() => {
        setIsSaving(false);
        setStep('complete');
      }, 800);
    }
  };

  const handleSkipReflection = () => {
    // Skip means no reflection - save and go to complete
    setIsSaving(true);
    
    const checkIn = {
      id: `wellness_${Date.now()}`,
      emotion: selectedEmotion,
      customEmotion: selectedEmotion === 'other' ? customEmotion.trim() : null,
      reflection: null,
      timestamp: new Date().toISOString(),
      date: new Date().toISOString().split('T')[0]
    };
    
    const existing = JSON.parse(localStorage.getItem('arvexis_wellness_checkins') || '[]');
    existing.unshift(checkIn);
    localStorage.setItem('arvexis_wellness_checkins', JSON.stringify(existing.slice(0, 100)));
    
    setTimeout(() => {
      setIsSaving(false);
      setStep('complete');
    }, 800);
  };

  // Get the emotional response for the selected emotion
  const emotionalResponse = EMOTIONAL_RESPONSES[selectedEmotion] || EMOTIONAL_RESPONSES.other;
  const emotionalClosing = EMOTIONAL_CLOSINGS[selectedEmotion] || EMOTIONAL_CLOSINGS.other;

  // ═══════════════════════════════════════════════════════════════════
  // AI RESPONSE STATE — Arvexis responds to the user's written words
  // "The moment they'll remember" — This is where the magic happens
  // ═══════════════════════════════════════════════════════════════════
  if (step === 'ai_response') {
    return (
      <div 
        className="min-h-screen relative"
        style={{ background: 'linear-gradient(180deg, #0D0F18 0%, #12141F 50%, #0D0F18 100%)' }}
        data-testid="wellness-ai-response-screen"
      >
        <div 
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.04) 0%, transparent 60%)'
          }}
        />
        
        <header 
          className="fixed top-0 left-0 right-0 z-40"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', 
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '16px'
          }}
        >
          <button
            onClick={() => navigate('/home')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            data-testid="wellness-ai-response-close"
          >
            <ArrowLeft size={20} />
          </button>
        </header>
        
        <main 
          className="relative z-10 flex flex-col items-center justify-center min-h-screen px-8"
          style={{ paddingTop: '80px', paddingBottom: '40px' }}
        >
          {isGeneratingResponse ? (
            // Arvexis is thinking...
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <motion.div
                animate={{ scale: [1, 1.05, 1], opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="mb-6"
              >
                <ArvexisSymbol size={56} glow />
              </motion.div>
              <p 
                className="text-[15px]"
                style={{ 
                  color: 'rgba(255,255,255,0.5)',
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontStyle: 'italic'
                }}
              >
                {lang === 'es' ? 'Leyendo tus palabras...' : 'Reading your words...'}
              </p>
            </motion.div>
          ) : (
            // Arvexis' response to what the user wrote
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-md"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mb-8"
              >
                <ArvexisSymbol size={56} glow />
              </motion.div>
              
              {/* User's reflection - shown softly as context */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mb-6 px-4 py-3 rounded-xl"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
              >
                <p 
                  className="text-[14px]"
                  style={{ 
                    color: 'rgba(255,255,255,0.5)',
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontStyle: 'italic'
                  }}
                >
                  &ldquo;{reflection}&rdquo;
                </p>
              </motion.div>
              
              {/* Arvexis' personalized response */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[18px] sm:text-[20px] mb-10 whitespace-pre-line"
                style={{ 
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.85)',
                  lineHeight: 1.7,
                  fontStyle: 'italic'
                }}
              >
                {aiResponse}
              </motion.p>
              
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                onClick={() => setStep('complete')}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full"
                style={{
                  background: 'rgba(212,175,55,0.1)',
                  border: '1px solid rgba(212,175,55,0.25)',
                  color: '#E6C06E',
                  fontSize: '15px',
                  fontWeight: 500
                }}
                data-testid="wellness-ai-response-continue"
              >
                <span>{lang === 'es' ? 'Continuar' : 'Continue'}</span>
              </motion.button>
            </motion.div>
          )}
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // COMPLETION STATE — Dynamic personal closing based on emotion
  // ═══════════════════════════════════════════════════════════════════
  if (step === 'complete') {
    return (
      <div 
        className="min-h-screen relative"
        style={{ background: 'linear-gradient(180deg, #0D0F18 0%, #12141F 50%, #0D0F18 100%)' }}
        data-testid="wellness-complete-screen"
      >
        <div 
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.03) 0%, transparent 60%)'
          }}
        />
        
        <header 
          className="fixed top-0 left-0 right-0 z-40"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', 
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '16px'
          }}
        >
          <button
            onClick={() => navigate('/home')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            data-testid="wellness-complete-back"
          >
            <ArrowLeft size={20} />
          </button>
        </header>
        
        <main 
          className="relative z-10 flex flex-col items-center justify-center min-h-screen px-8"
          style={{ paddingTop: '80px', paddingBottom: '40px' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-sm"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-8"
            >
              <ArvexisSymbol size={56} glow />
            </motion.div>
            
            {/* Dynamic closing message based on emotion */}
            <p 
              className="text-[18px] sm:text-[20px] mb-10 whitespace-pre-line"
              style={{ 
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.85)',
                lineHeight: 1.6,
                fontStyle: 'italic'
              }}
            >
              {emotionalClosing[lang] || emotionalClosing.en}
            </p>
            
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              onClick={() => navigate('/home')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '15px',
                fontWeight: 500
              }}
              data-testid="wellness-return-home"
            >
              <Home size={18} />
              <span>{lang === 'es' ? 'Regresar a Inicio' : 'Return Home'}</span>
            </motion.button>
          </motion.div>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // ACKNOWLEDGED STATE — Arvexis responds before asking anything
  // "I noticed." — The Emotional Pause
  // ═══════════════════════════════════════════════════════════════════
  if (step === 'acknowledged') {
    const selectedEmotionData = WELLNESS_EMOTIONS.find(e => e.key === selectedEmotion);
    const displayEmotion = selectedEmotion === 'other' 
      ? customEmotion 
      : (selectedEmotionData?.label?.[lang] || selectedEmotionData?.label?.en);
    
    return (
      <div 
        className="min-h-screen relative"
        style={{ background: 'linear-gradient(180deg, #0D0F18 0%, #12141F 50%, #0D0F18 100%)' }}
        data-testid="wellness-acknowledged-screen"
      >
        <div 
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            background: 'radial-gradient(ellipse at center top, rgba(212,175,55,0.04) 0%, transparent 50%)'
          }}
        />
        
        <header 
          className="fixed top-0 left-0 right-0 z-40 flex items-center"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', 
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '16px',
            background: 'linear-gradient(180deg, rgba(13,15,24,0.95) 0%, transparent 100%)'
          }}
        >
          <button
            onClick={() => { setStep('emotion'); setSelectedEmotion(null); }}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            data-testid="wellness-acknowledged-back"
          >
            <ArrowLeft size={20} />
          </button>
        </header>
        
        <main 
          className="relative z-10 px-6 sm:px-8 max-w-lg mx-auto flex flex-col justify-center"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 100px)',
            paddingBottom: '40px',
            minHeight: '100vh'
          }}
        >
          {/* Arvexis Symbol - she's listening */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <ArvexisSymbol size={48} glow />
          </motion.div>
          
          {/* Selected emotion badge - small, understated */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center mb-8"
          >
            <span 
              className="px-4 py-2 rounded-full text-sm"
              style={{
                background: 'rgba(212,175,55,0.08)',
                border: '1px solid rgba(212,175,55,0.2)',
                color: 'rgba(230,192,110,0.8)',
                fontWeight: 500
              }}
            >
              {displayEmotion}
            </span>
          </motion.div>
          
          {/* Arvexis' handcrafted response - the heart of this experience */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-center mb-10"
          >
            <p 
              className="text-[18px] sm:text-[20px] whitespace-pre-line"
              style={{ 
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.85)',
                lineHeight: 1.7,
                fontStyle: 'italic'
              }}
            >
              {emotionalResponse[lang] || emotionalResponse.en}
            </p>
          </motion.div>
          
          {/* Continue button - gentle, not urgent */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col items-center gap-4"
          >
            <button
              onClick={handleContinueToReflection}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full"
              style={{
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.25)',
                color: '#E6C06E',
                fontSize: '15px',
                fontWeight: 500
              }}
              data-testid="wellness-continue-to-reflection"
            >
              <span>{lang === 'es' ? 'Continuar' : 'Continue'}</span>
              <ArrowRight size={16} />
            </button>
            
            {/* Option to save without writing */}
            <button
              onClick={handleSave}
              className="text-sm"
              style={{ color: 'rgba(255,255,255,0.4)' }}
              data-testid="wellness-save-without-writing"
            >
              {lang === 'es' ? 'Guardar sin escribir' : 'Save without writing'}
            </button>
          </motion.div>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // CUSTOM EMOTION STATE — "Other..." text input
  // ═══════════════════════════════════════════════════════════════════
  if (step === 'custom') {
    return (
      <div 
        className="min-h-screen relative"
        style={{ background: 'linear-gradient(180deg, #0D0F18 0%, #12141F 50%, #0D0F18 100%)' }}
        data-testid="wellness-custom-screen"
      >
        <div 
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            background: 'radial-gradient(ellipse at center top, rgba(212,175,55,0.04) 0%, transparent 50%)'
          }}
        />
        
        <header 
          className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', 
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '16px',
            background: 'linear-gradient(180deg, rgba(13,15,24,0.95) 0%, transparent 100%)'
          }}
        >
          <button
            onClick={() => { setStep('emotion'); setSelectedEmotion(null); }}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            data-testid="wellness-custom-back"
          >
            <ArrowLeft size={20} />
            <span className="text-sm">{lang === 'es' ? 'Atrás' : 'Back'}</span>
          </button>
        </header>
        
        <main 
          className="relative z-10 px-6 sm:px-8 max-w-lg mx-auto flex flex-col justify-center"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 100px)',
            paddingBottom: '40px',
            minHeight: '100vh'
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h2 
              className="text-[22px] sm:text-[26px] mb-3"
              style={{ 
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.9)',
                fontStyle: 'italic'
              }}
            >
              {lang === 'es'
                ? '¿Cómo describirías cómo te sientes hoy?'
                : 'How would you describe how you\'re feeling today?'}
            </h2>
            <p 
              className="text-sm"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              {lang === 'es' ? 'Con tus propias palabras' : 'In your own words'}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <input
              type="text"
              value={customEmotion}
              onChange={(e) => setCustomEmotion(e.target.value)}
              placeholder={lang === 'es' ? 'Escribe unas palabras...' : 'A few words...'}
              className="w-full text-center"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                padding: '20px',
                color: '#FFFFFF',
                fontSize: '18px',
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                outline: 'none'
              }}
              autoFocus
              data-testid="wellness-custom-input"
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <button
              onClick={handleCustomEmotionSubmit}
              disabled={!customEmotion.trim()}
              className="w-full py-4 rounded-full font-medium flex items-center justify-center gap-2"
              style={{
                background: customEmotion.trim() ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.04)',
                border: customEmotion.trim() ? '1px solid rgba(212,175,55,0.3)' : '1px solid rgba(255,255,255,0.08)',
                color: customEmotion.trim() ? '#E6C06E' : 'rgba(255,255,255,0.4)',
                fontSize: '15px',
                cursor: customEmotion.trim() ? 'pointer' : 'not-allowed'
              }}
              data-testid="wellness-custom-continue"
            >
              <ArrowRight size={18} />
              <span>{lang === 'es' ? 'Continuar' : 'Continue'}</span>
            </button>
          </motion.div>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // REFLECTION STATE — Optional writing prompt (after acknowledgment)
  // ═══════════════════════════════════════════════════════════════════
  if (step === 'reflection') {
    const selectedEmotionData = WELLNESS_EMOTIONS.find(e => e.key === selectedEmotion);
    const displayEmotion = selectedEmotion === 'other' 
      ? customEmotion 
      : (selectedEmotionData?.label?.[lang] || selectedEmotionData?.label?.en);
    
    return (
      <div 
        className="min-h-screen relative"
        style={{ background: 'linear-gradient(180deg, #0D0F18 0%, #12141F 50%, #0D0F18 100%)' }}
        data-testid="wellness-reflection-screen"
      >
        <div 
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            background: 'radial-gradient(ellipse at center top, rgba(212,175,55,0.04) 0%, transparent 50%)'
          }}
        />
        
        <header 
          className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', 
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '16px',
            background: 'linear-gradient(180deg, rgba(13,15,24,0.95) 0%, transparent 100%)'
          }}
        >
          <button
            onClick={() => setStep('acknowledged')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            data-testid="wellness-reflection-back"
          >
            <ArrowLeft size={20} />
            <span className="text-sm">{lang === 'es' ? 'Atrás' : 'Back'}</span>
          </button>
        </header>
        
        <main 
          className="relative z-10 px-6 sm:px-8 max-w-lg mx-auto"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 100px)',
            paddingBottom: '40px'
          }}
        >
          {/* Selected emotion badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 mb-6"
          >
            <span 
              className="px-4 py-2 rounded-full text-sm"
              style={{
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.25)',
                color: '#E6C06E',
                fontWeight: 500
              }}
            >
              {displayEmotion}
            </span>
          </motion.div>
          
          {/* Personal reflection prompt */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-8"
          >
            <h2 
              className="text-[20px] sm:text-[24px] mb-3"
              style={{ 
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.9)',
                fontStyle: 'italic'
              }}
            >
              {currentReflectionPrompt[lang] || currentReflectionPrompt.en}
            </h2>
            <p 
              className="text-sm"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              {lang === 'es' ? 'Completamente opcional' : 'Completely optional'}
            </p>
          </motion.div>
          
          {/* Reflection text area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder={lang === 'es' ? 'Unas palabras, si lo deseas...' : 'A few words, if you wish...'}
              rows={5}
              className="w-full resize-none"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                padding: '20px',
                color: '#FFFFFF',
                fontSize: '16px',
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                lineHeight: 1.7,
                outline: 'none'
              }}
              data-testid="wellness-reflection-input"
            />
          </motion.div>
          
          {/* Action buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col gap-3"
          >
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full py-4 rounded-full font-medium flex items-center justify-center gap-2"
              style={{
                background: reflection.trim() ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.06)',
                border: reflection.trim() ? '1px solid rgba(212,175,55,0.3)' : '1px solid rgba(255,255,255,0.1)',
                color: reflection.trim() ? '#E6C06E' : 'rgba(255,255,255,0.7)',
                fontSize: '15px'
              }}
              data-testid="wellness-save-reflection"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Check size={18} />
                  <span>{lang === 'es' ? 'Guardar' : 'Save'}</span>
                </>
              )}
            </button>
            
            {!reflection.trim() && (
              <button
                onClick={handleSkipReflection}
                disabled={isSaving}
                className="w-full py-3 text-sm"
                style={{ color: 'rgba(255,255,255,0.4)' }}
                data-testid="wellness-skip-reflection"
              >
                {lang === 'es' ? 'Continuar sin escribir' : 'Continue without writing'}
              </button>
            )}
          </motion.div>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // EMOTION SELECTION STATE — "How are you feeling today?"
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div 
      className="min-h-screen relative"
      style={{ background: 'linear-gradient(180deg, #0D0F18 0%, #12141F 50%, #0D0F18 100%)' }}
      data-testid="wellness-emotion-screen"
    >
      {/* Ambient glow */}
      <div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: 'radial-gradient(ellipse at center top, rgba(212,175,55,0.04) 0%, transparent 50%)'
        }}
      />
      
      {/* Header with Back button */}
      <header 
        className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between"
        style={{ 
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', 
          paddingLeft: '20px',
          paddingRight: '20px',
          paddingBottom: '16px',
          background: 'linear-gradient(180deg, rgba(13,15,24,0.95) 0%, transparent 100%)'
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          data-testid="wellness-back-btn"
        >
          <ArrowLeft size={20} />
          <span className="text-sm">{lang === 'es' ? 'Inicio' : 'Home'}</span>
        </button>
      </header>
      
      {/* Main content */}
      <main 
        className="relative z-10 px-6 sm:px-8 max-w-xl mx-auto"
        style={{ 
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 100px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 40px)'
        }}
      >
        {/* Gentle opening question */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <div className="mb-6">
            <ArvexisSymbol size={44} />
          </div>
          
          <h1 
            className="text-[28px] sm:text-[32px] mb-3"
            style={{ 
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 500,
              color: 'rgba(255,255,255,0.95)',
              lineHeight: 1.2
            }}
          >
            {lang === 'es' ? '¿Cómo te sientes hoy?' : 'How are you feeling today?'}
          </h1>
          
          <p 
            className="text-[15px]"
            style={{ 
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.5
            }}
          >
            {lang === 'es' 
              ? 'Elige lo que mejor refleje este momento.'
              : 'Choose what best reflects this moment.'}
          </p>
        </motion.div>
        
        {/* Elegant emotion cards grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8"
        >
          {WELLNESS_EMOTIONS.map((emotion, index) => (
            <motion.button
              key={emotion.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 + index * 0.03 }}
              onClick={() => handleEmotionSelect(emotion.key, emotion.isCustom)}
              className="relative p-4 rounded-2xl text-left transition-all duration-300"
              style={{
                background: selectedEmotion === emotion.key 
                  ? 'rgba(212,175,55,0.12)' 
                  : emotion.isCustom 
                    ? 'rgba(255,255,255,0.02)' 
                    : 'rgba(255,255,255,0.03)',
                border: selectedEmotion === emotion.key 
                  ? '1px solid rgba(212,175,55,0.4)' 
                  : emotion.isCustom
                    ? '1px dashed rgba(255,255,255,0.15)'
                    : '1px solid rgba(255,255,255,0.06)',
                transform: selectedEmotion === emotion.key ? 'scale(1.02)' : 'scale(1)'
              }}
              data-testid={`emotion-${emotion.key}`}
            >
              <span 
                className="block text-[16px] mb-1"
                style={{ 
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 600,
                  color: selectedEmotion === emotion.key 
                    ? '#E6C06E' 
                    : emotion.isCustom 
                      ? 'rgba(255,255,255,0.6)' 
                      : 'rgba(255,255,255,0.85)'
                }}
              >
                {emotion.label[lang] || emotion.label.en}
              </span>
              <span 
                className="block text-[12px]"
                style={{ 
                  color: selectedEmotion === emotion.key 
                    ? 'rgba(230,192,110,0.7)' 
                    : emotion.isCustom
                      ? 'rgba(255,255,255,0.35)'
                      : 'rgba(255,255,255,0.4)'
                }}
              >
                {emotion.description[lang] || emotion.description.en}
              </span>
            </motion.button>
          ))}
        </motion.div>
        
        {/* Subtle footer note about privacy */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs"
          style={{ color: 'rgba(255,255,255,0.3)' }}
        >
          {lang === 'es'
            ? 'Tus registros son privados y te ayudan a reconocer patrones con el tiempo.'
            : 'Your check-ins are private and help you recognize patterns over time.'}
        </motion.p>
      </main>
    </div>
  );
};

// ============================================================================
// P0.7 — GROWTH JOURNEY EXPERIENCE (Redesigned)
// Vision: "Reading the story of your own evolution"
// Emotional question: "Who am I becoming?"
// Replace statistics with narrative. Timeline, not achievements.
// Future-ready: Favorite Dreams, Personal Milestones, Life Chapters, Dream Seasons
// ============================================================================

// Helper to generate thoughtful narrative observations based on user data
const generateGrowthNarrative = (stats, dreamCount, lang) => {
  const narratives = [];
  
  if (dreamCount >= 1 && dreamCount < 5) {
    narratives.push({
      en: "You've begun capturing the stories your dreams tell. Each one adds a new thread to the tapestry of understanding yourself.",
      es: "Has comenzado a capturar las historias que cuentan tus sueños. Cada uno añade un nuevo hilo al tapiz de conocerte a ti mismo."
    });
  } else if (dreamCount >= 5 && dreamCount < 15) {
    narratives.push({
      en: "Patterns are beginning to emerge from your reflections. Your subconscious is revealing its language to you.",
      es: "Los patrones están comenzando a emerger de tus reflexiones. Tu subconsciente te está revelando su lenguaje."
    });
  } else if (dreamCount >= 15 && dreamCount < 30) {
    narratives.push({
      en: "Your dream journal has grown into something meaningful. Looking back, you can see how your inner world has been speaking to you.",
      es: "Tu diario de sueños se ha convertido en algo significativo. Mirando atrás, puedes ver cómo tu mundo interior te ha estado hablando."
    });
  } else if (dreamCount >= 30) {
    narratives.push({
      en: "You've cultivated a deep practice of self-reflection. The themes in your dreams tell a story of growth, change, and becoming.",
      es: "Has cultivado una práctica profunda de autorreflexión. Los temas en tus sueños cuentan una historia de crecimiento, cambio y transformación."
    });
  }
  
  // Add emotion-based narrative if available
  if (stats?.emotion_trends?.length > 0) {
    const topEmotions = stats.emotion_trends.slice(0, 3).map(e => e.emotion?.toLowerCase());
    if (topEmotions.includes('curious') || topEmotions.includes('hopeful')) {
      narratives.push({
        en: "Your recent dreams carry a sense of openness and possibility. There's a quiet curiosity moving through your reflections.",
        es: "Tus sueños recientes llevan un sentido de apertura y posibilidad. Hay una curiosidad tranquila moviéndose a través de tus reflexiones."
      });
    }
    if (topEmotions.includes('peaceful') || topEmotions.includes('calm')) {
      narratives.push({
        en: "A gentle sense of peace has been present in your dreams lately. Your inner world seems to be finding its stillness.",
        es: "Una sensación gentil de paz ha estado presente en tus sueños últimamente. Tu mundo interior parece estar encontrando su quietud."
      });
    }
  }
  
  // Add theme-based narrative if available
  if (stats?.themes_with_evidence?.length > 0) {
    const themes = stats.themes_with_evidence.map(t => t.theme?.toLowerCase() || '');
    if (themes.some(t => t.includes('journey') || t.includes('path') || t.includes('travel'))) {
      narratives.push({
        en: "Themes of journeys and paths appear in your dreams—perhaps reflecting a transition you're navigating in waking life.",
        es: "Temas de viajes y caminos aparecen en tus sueños—quizás reflejando una transición que estás navegando en la vida despierta."
      });
    }
    if (themes.some(t => t.includes('water') || t.includes('ocean') || t.includes('sea'))) {
      narratives.push({
        en: "Water appears frequently in your dreams. It often represents emotions, the unconscious, or a sense of flow and change.",
        es: "El agua aparece frecuentemente en tus sueños. A menudo representa emociones, el inconsciente, o un sentido de flujo y cambio."
      });
    }
  }
  
  return narratives;
};

// Timeline milestones - now reimagined as "Chapters" with curated artwork
const JOURNEY_CHAPTERS = [
  { 
    key: 'chapter_origin',
    threshold: 1,
    chapterNum: 'I',
    title: { en: 'The Beginning', es: 'El Comienzo' },
    subtitle: { en: 'Where the journey began', es: 'Donde comenzó el viaje' },
    narrative: { 
      en: 'Every story has a first word. Every journey, a first step. You chose to listen to the quiet voice within—the one that speaks in symbols and feelings. This was the moment you decided your dreams were worth remembering.',
      es: 'Cada historia tiene una primera palabra. Cada viaje, un primer paso. Elegiste escuchar la voz tranquila interior—la que habla en símbolos y sentimientos. Este fue el momento en que decidiste que tus sueños valían la pena recordar.'
    },
    theme: 'awakening',
    artworkUrl: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=1200&q=80'
  },
  { 
    key: 'chapter_patterns',
    threshold: 5,
    chapterNum: 'II',
    title: { en: 'Patterns Emerge', es: 'Los Patrones Emergen' },
    subtitle: { en: 'The subconscious reveals itself', es: 'El subconsciente se revela' },
    narrative: { 
      en: 'Five dreams recorded. Five doorways opened. Now you begin to see it—the threads connecting one night to another. Your mind has always been speaking; you\'re simply learning its language now.',
      es: 'Cinco sueños registrados. Cinco puertas abiertas. Ahora comienzas a verlo—los hilos conectando una noche con otra. Tu mente siempre ha estado hablando; simplemente estás aprendiendo su lenguaje ahora.'
    },
    theme: 'discovery',
    artworkUrl: 'https://images.unsplash.com/photo-1507166763745-bfe008fbb831?w=1200&q=80'
  },
  { 
    key: 'chapter_week',
    threshold: 7,
    chapterNum: 'III',
    title: { en: 'A Week of Listening', es: 'Una Semana Escuchando' },
    subtitle: { en: 'Presence becomes practice', es: 'La presencia se convierte en práctica' },
    narrative: { 
      en: 'Seven reflections. A week spent not just sleeping, but paying attention. The practice is taking root. You\'re cultivating something rare—a relationship with the part of yourself that dreams.',
      es: 'Siete reflexiones. Una semana no solo durmiendo, sino prestando atención. La práctica está echando raíces. Estás cultivando algo raro—una relación con la parte de ti que sueña.'
    },
    theme: 'practice',
    artworkUrl: 'https://images.unsplash.com/photo-1548362851-ea052637ad64?w=1200&q=80'
  },
  { 
    key: 'chapter_symbols',
    threshold: 15,
    chapterNum: 'IV',
    title: { en: 'The Recurring Symbols', es: 'Los Símbolos Recurrentes' },
    subtitle: { en: 'Personal mythology takes shape', es: 'La mitología personal toma forma' },
    narrative: { 
      en: 'By now, certain images have appeared more than once. A place. A feeling. A person or presence. These are not random—they are your personal symbols, the vocabulary of your unconscious mind speaking directly to you.',
      es: 'A estas alturas, ciertas imágenes han aparecido más de una vez. Un lugar. Un sentimiento. Una persona o presencia. Estos no son aleatorios—son tus símbolos personales, el vocabulario de tu mente inconsciente hablándote directamente.'
    },
    theme: 'symbols',
    artworkUrl: 'https://images.unsplash.com/photo-1601325059290-eac581b71510?w=1200&q=80'
  },
  { 
    key: 'chapter_month',
    threshold: 30,
    chapterNum: 'V',
    title: { en: 'A Season of Dreams', es: 'Una Temporada de Sueños' },
    subtitle: { en: 'Thirty nights remembered', es: 'Treinta noches recordadas' },
    narrative: { 
      en: 'Thirty dreams. An entire season of your inner life, captured. If you look back now, you\'ll see not just individual dreams, but a narrative—your narrative. The story of who you are becoming, told in symbols and feeling.',
      es: 'Treinta sueños. Una temporada entera de tu vida interior, capturada. Si miras atrás ahora, verás no solo sueños individuales, sino una narrativa—tu narrativa. La historia de quién te estás convirtiendo, contada en símbolos y sentimientos.'
    },
    theme: 'depth',
    artworkUrl: 'https://images.unsplash.com/photo-1507166763745-bfe008fbb831?w=1200&q=80'
  },
  { 
    key: 'chapter_awareness',
    threshold: 50,
    chapterNum: 'VI',
    title: { en: 'Deepening Awareness', es: 'Profundizando la Conciencia' },
    subtitle: { en: 'The inner landscape clarifies', es: 'El paisaje interior se clarifica' },
    narrative: { 
      en: 'Fifty reflections have passed through your hands like water through a stream. What once felt murky now has clarity. You understand your emotional landscape better than most people ever will. This is the gift of paying attention.',
      es: 'Cincuenta reflexiones han pasado por tus manos como agua por un arroyo. Lo que una vez se sentía turbio ahora tiene claridad. Entiendes tu paisaje emocional mejor de lo que la mayoría de las personas jamás lo hará. Este es el regalo de prestar atención.'
    },
    theme: 'clarity',
    artworkUrl: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=1200&q=80'
  },
  { 
    key: 'chapter_hundred',
    threshold: 100,
    chapterNum: 'VII',
    title: { en: 'One Hundred Dreams', es: 'Cien Sueños' },
    subtitle: { en: 'A profound archive of self', es: 'Un archivo profundo del ser' },
    narrative: { 
      en: 'One hundred dreams. One hundred conversations with yourself. You now possess something extraordinary: a living record of your inner world, spanning months or perhaps years. This is more than a journal—it is a mirror that shows not just who you are, but who you are becoming.',
      es: 'Cien sueños. Cien conversaciones contigo mismo. Ahora posees algo extraordinario: un registro vivo de tu mundo interior, abarcando meses o quizás años. Esto es más que un diario—es un espejo que muestra no solo quién eres, sino quién te estás convirtiendo.'
    },
    theme: 'mastery',
    artworkUrl: 'https://images.unsplash.com/photo-1548362851-ea052637ad64?w=1200&q=80'
  },
];

// Legacy milestones for backward compatibility
const JOURNEY_MILESTONES = JOURNEY_CHAPTERS.map(ch => ({
  key: ch.key,
  dreamThreshold: ch.threshold,
  title: ch.title,
  description: ch.subtitle
}));

// ═══════════════════════════════════════════════════════════════════════════════
// CURATED ARTWORK LIBRARY — Visual Storytelling for Daily Reflections
// Architecture designed for future Visual Reflections (AI-generated artwork)
// ═══════════════════════════════════════════════════════════════════════════════

const GrowthJourneyScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentEntries, setRecentEntries] = useState([]);
  const [firstDreamDate, setFirstDreamDate] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch insights for stats
        const insightsRes = await axios.get(`${API}/longitudinal-insights`);
        setStats(insightsRes.data);
        
        // Fetch recent entries and find first dream date
        const entriesRes = await axios.get(`${API}/dreams?limit=100`);
        const dreams = entriesRes.data || [];
        setRecentEntries(dreams.slice(0, 5));
        
        // Find earliest dream for timeline
        if (dreams.length > 0) {
          const sorted = [...dreams].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          setFirstDreamDate(sorted[0]?.created_at);
        }
      } catch (err) {
        console.error('Failed to fetch journey data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalReflections = stats?.dream_count || 0;
  const achievedChapters = JOURNEY_CHAPTERS.filter(ch => totalReflections >= ch.threshold);
  const currentChapter = achievedChapters[achievedChapters.length - 1] || null;
  const nextChapter = JOURNEY_CHAPTERS.find(ch => totalReflections < ch.threshold);

  // Calculate journey duration
  const journeyDuration = firstDreamDate 
    ? Math.ceil((new Date() - new Date(firstDreamDate)) / (1000 * 60 * 60 * 24))
    : 0;

  // Progress toward next chapter
  const progressToNext = nextChapter 
    ? Math.min((totalReflections / nextChapter.threshold) * 100, 100)
    : 100;

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: '#020617' }}
        data-testid="growth-journey-loading"
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.5, repeat: Infinity }}
        >
          <ArvexisSymbol size={48} />
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative"
      style={{ background: '#020617' }}
      data-testid="growth-journey-screen"
    >
      {/* SVG Noise Texture Overlay */}
      <div 
        className="fixed inset-0 pointer-events-none z-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />
      
      {/* Glassmorphic Header */}
      <header 
        className="fixed top-0 left-0 right-0 z-50"
        style={{ 
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', 
          paddingLeft: '24px',
          paddingRight: '24px',
          paddingBottom: '16px',
          background: 'rgba(2,6,23,0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.05)'
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-stone-400 hover:text-stone-100 transition-colors duration-300"
            data-testid="journey-back-btn"
          >
            <ArrowLeft size={18} />
            <span 
              className="text-xs font-medium tracking-widest uppercase"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {lang === 'es' ? 'Atrás' : 'Back'}
            </span>
          </button>
          
          <div className="flex items-center gap-3">
            <ArvexisSymbol size={28} />
          </div>
        </div>
      </header>
      
      {/* Main Editorial Content */}
      <main 
        className="relative z-10"
        style={{ 
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 120px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 100px)'
        }}
      >
        {/* Hero Section - Current Chapter or Welcome */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="relative min-h-[70vh] flex items-center mb-24 md:mb-32"
        >
          {/* Chapter Artwork - Full bleed background */}
          {currentChapter && (
            <div className="absolute inset-0 overflow-hidden">
              <motion.img
                initial={{ scale: 1.1, opacity: 0 }}
                animate={{ scale: 1, opacity: 0.4 }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
                src={currentChapter.artworkUrl}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover"
              />
              <div 
                className="absolute inset-0"
                style={{ 
                  background: 'linear-gradient(to top, #020617 0%, rgba(2,6,23,0.6) 50%, rgba(2,6,23,0.8) 100%)'
                }}
              />
            </div>
          )}
          
          <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-end">
              {/* Chapter Number - Large typography */}
              <div className="md:col-span-3">
                {currentChapter ? (
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="block text-7xl md:text-8xl lg:text-9xl font-light tracking-tighter"
                    style={{ 
                      fontFamily: '"Cormorant Garamond", serif',
                      color: 'rgba(255,255,255,0.05)'
                    }}
                  >
                    {currentChapter.chapterNum}
                  </motion.span>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    <Sparkles size={48} style={{ color: 'rgba(180,140,60,0.5)' }} />
                  </motion.div>
                )}
              </div>
              
              {/* Chapter Content */}
              <div className="md:col-span-9">
                {/* Label */}
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="text-xs font-medium tracking-[0.25em] uppercase mb-4"
                  style={{ 
                    fontFamily: 'Manrope, sans-serif',
                    color: 'rgba(180,140,60,0.8)'
                  }}
                >
                  {currentChapter 
                    ? (lang === 'es' ? 'CAPÍTULO ACTUAL' : 'CURRENT CHAPTER')
                    : (lang === 'es' ? 'TU VIAJE ESPERA' : 'YOUR JOURNEY AWAITS')
                  }
                </motion.p>
                
                {/* Title */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4"
                  style={{ 
                    fontFamily: '"Cormorant Garamond", serif',
                    fontWeight: 400,
                    color: 'rgba(255,255,255,0.95)',
                    lineHeight: 1.1
                  }}
                >
                  {currentChapter 
                    ? (currentChapter.title[lang] || currentChapter.title.en)
                    : (lang === 'es' ? 'Comienza Tu Historia' : 'Begin Your Story')
                  }
                </motion.h1>
                
                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className="text-lg mb-8"
                  style={{ 
                    fontFamily: '"Cormorant Garamond", serif',
                    fontStyle: 'italic',
                    color: 'rgba(255,255,255,0.5)'
                  }}
                >
                  {currentChapter 
                    ? (currentChapter.subtitle[lang] || currentChapter.subtitle.en)
                    : (lang === 'es' ? 'Cada sueño es una página' : 'Every dream is a page')
                  }
                </motion.p>
                
                {/* Narrative Text */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                  className="p-6 md:p-8 rounded-sm"
                  style={{
                    background: 'rgba(15,23,42,0.6)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.08)'
                  }}
                >
                  <p 
                    className="text-base md:text-lg leading-relaxed"
                    style={{ 
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 300,
                      color: 'rgba(214,211,209,0.85)'
                    }}
                  >
                    {currentChapter 
                      ? (currentChapter.narrative[lang] || currentChapter.narrative.en)
                      : (lang === 'es' 
                          ? 'Tu historia aún no ha sido escrita. Cada sueño que captures se convertirá en un capítulo—un fragmento de tu mundo interior preservado y reflexionado. El viaje comienza con una sola reflexión.'
                          : 'Your story has not yet been written. Every dream you capture will become a chapter—a fragment of your inner world preserved and reflected upon. The journey begins with a single reflection.'
                        )
                    }
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>
        
        {/* Journey Stats - Elegant Minimal */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 mb-24 md:mb-32"
        >
          <div 
            className="flex flex-wrap justify-center gap-12 md:gap-20 py-12"
            style={{
              borderTop: '1px solid rgba(255,255,255,0.06)',
              borderBottom: '1px solid rgba(255,255,255,0.06)'
            }}
          >
            {/* Dreams Count */}
            <div className="text-center">
              <p 
                className="text-4xl md:text-5xl mb-2"
                style={{ 
                  fontFamily: '"Cormorant Garamond", serif',
                  fontWeight: 300,
                  color: 'rgba(180,140,60,0.9)'
                }}
              >
                {totalReflections}
              </p>
              <p 
                className="text-xs tracking-[0.2em] uppercase"
                style={{ 
                  fontFamily: 'Manrope, sans-serif',
                  color: 'rgba(168,162,158,0.6)'
                }}
              >
                {lang === 'es' ? 'SUEÑOS' : 'DREAMS'}
              </p>
            </div>
            
            {/* Days */}
            {journeyDuration > 0 && (
              <div className="text-center">
                <p 
                  className="text-4xl md:text-5xl mb-2"
                  style={{ 
                    fontFamily: '"Cormorant Garamond", serif',
                    fontWeight: 300,
                    color: 'rgba(255,255,255,0.7)'
                  }}
                >
                  {journeyDuration}
                </p>
                <p 
                  className="text-xs tracking-[0.2em] uppercase"
                  style={{ 
                    fontFamily: 'Manrope, sans-serif',
                    color: 'rgba(168,162,158,0.6)'
                  }}
                >
                  {lang === 'es' ? 'DÍAS' : 'DAYS'}
                </p>
              </div>
            )}
            
            {/* Chapters */}
            <div className="text-center">
              <p 
                className="text-4xl md:text-5xl mb-2"
                style={{ 
                  fontFamily: '"Cormorant Garamond", serif',
                  fontWeight: 300,
                  color: 'rgba(255,255,255,0.7)'
                }}
              >
                {achievedChapters.length}
              </p>
              <p 
                className="text-xs tracking-[0.2em] uppercase"
                style={{ 
                  fontFamily: 'Manrope, sans-serif',
                  color: 'rgba(168,162,158,0.6)'
                }}
              >
                {lang === 'es' ? 'CAPÍTULOS' : 'CHAPTERS'}
              </p>
            </div>
            
            {/* Themes */}
            {stats?.themes_with_evidence?.length > 0 && (
              <div className="text-center">
                <p 
                  className="text-4xl md:text-5xl mb-2"
                  style={{ 
                    fontFamily: '"Cormorant Garamond", serif',
                    fontWeight: 300,
                    color: 'rgba(255,255,255,0.7)'
                  }}
                >
                  {stats.themes_with_evidence.length}
                </p>
                <p 
                  className="text-xs tracking-[0.2em] uppercase"
                  style={{ 
                    fontFamily: 'Manrope, sans-serif',
                    color: 'rgba(168,162,158,0.6)'
                  }}
                >
                  {lang === 'es' ? 'TEMAS' : 'THEMES'}
                </p>
              </div>
            )}
          </div>
        </motion.section>
        
        {/* Previous Chapters - Editorial Layout */}
        {achievedChapters.length > 1 && (
          <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 mb-24 md:mb-32">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-xs font-medium tracking-[0.25em] uppercase mb-12"
              style={{ 
                fontFamily: 'Manrope, sans-serif',
                color: 'rgba(168,162,158,0.5)'
              }}
            >
              {lang === 'es' ? 'CAPÍTULOS ANTERIORES' : 'PREVIOUS CHAPTERS'}
            </motion.p>
            
            <div className="space-y-16 md:space-y-24">
              {achievedChapters.slice(0, -1).reverse().map((chapter, idx) => (
                <motion.div
                  key={chapter.key}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.8, delay: idx * 0.1 }}
                  className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-center ${
                    idx % 2 === 0 ? '' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Artwork */}
                  <div className={`md:col-span-5 ${idx % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}>
                    <div className="relative aspect-[4/3] rounded-sm overflow-hidden group">
                      <img
                        src={chapter.artworkUrl}
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div 
                        className="absolute inset-0"
                        style={{ 
                          background: 'linear-gradient(to top, rgba(2,6,23,0.5) 0%, transparent 60%)'
                        }}
                      />
                      {/* Chapter Number Overlay */}
                      <span
                        className="absolute bottom-4 left-4 text-6xl font-light"
                        style={{ 
                          fontFamily: '"Cormorant Garamond", serif',
                          color: 'rgba(255,255,255,0.1)'
                        }}
                      >
                        {chapter.chapterNum}
                      </span>
                    </div>
                  </div>
                  
                  {/* Text Content */}
                  <div className={`md:col-span-7 ${idx % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
                    <p 
                      className="text-xs tracking-[0.2em] uppercase mb-3"
                      style={{ 
                        fontFamily: 'Manrope, sans-serif',
                        color: 'rgba(180,140,60,0.6)'
                      }}
                    >
                      {lang === 'es' ? 'CAPÍTULO' : 'CHAPTER'} {chapter.chapterNum}
                    </p>
                    <h3 
                      className="text-2xl md:text-3xl lg:text-4xl tracking-tight mb-3"
                      style={{ 
                        fontFamily: '"Cormorant Garamond", serif',
                        fontWeight: 400,
                        color: 'rgba(255,255,255,0.9)'
                      }}
                    >
                      {chapter.title[lang] || chapter.title.en}
                    </h3>
                    <p 
                      className="text-base mb-4"
                      style={{ 
                        fontFamily: '"Cormorant Garamond", serif',
                        fontStyle: 'italic',
                        color: 'rgba(255,255,255,0.4)'
                      }}
                    >
                      {chapter.subtitle[lang] || chapter.subtitle.en}
                    </p>
                    <p 
                      className="text-sm leading-relaxed"
                      style={{ 
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 300,
                        color: 'rgba(214,211,209,0.65)'
                      }}
                    >
                      {chapter.narrative[lang] || chapter.narrative.en}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}
        
        {/* Next Chapter Preview */}
        {nextChapter && (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 mb-24"
          >
            <div 
              className="p-8 md:p-12 rounded-sm"
              style={{
                background: 'rgba(15,23,42,0.4)',
                border: '1px solid rgba(255,255,255,0.06)'
              }}
            >
              <p 
                className="text-xs tracking-[0.2em] uppercase mb-4"
                style={{ 
                  fontFamily: 'Manrope, sans-serif',
                  color: 'rgba(168,162,158,0.5)'
                }}
              >
                {lang === 'es' ? 'PRÓXIMO CAPÍTULO' : 'NEXT CHAPTER'}
              </p>
              
              <h3 
                className="text-2xl md:text-3xl tracking-tight mb-2"
                style={{ 
                  fontFamily: '"Cormorant Garamond", serif',
                  fontWeight: 400,
                  color: 'rgba(255,255,255,0.6)'
                }}
              >
                {nextChapter.title[lang] || nextChapter.title.en}
              </h3>
              
              <p 
                className="text-sm mb-6"
                style={{ 
                  fontFamily: 'Manrope, sans-serif',
                  color: 'rgba(168,162,158,0.5)'
                }}
              >
                {lang === 'es' 
                  ? `${nextChapter.threshold - totalReflections} sueños más para desbloquear`
                  : `${nextChapter.threshold - totalReflections} more dreams to unlock`
                }
              </p>
              
              {/* Elegant Progress Indicator */}
              <div className="relative h-px w-full max-w-md bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressToNext}%` }}
                  transition={{ duration: 1, delay: 0.5, ease: 'easeOut' }}
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ background: 'rgba(180,140,60,0.6)' }}
                />
              </div>
            </div>
          </motion.section>
        )}
        
        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 text-center"
        >
          <button
            onClick={() => navigate('/log')}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-sm transition-all duration-300 hover:bg-amber-900/20"
            style={{
              background: 'rgba(180,140,60,0.1)',
              border: '1px solid rgba(180,140,60,0.3)',
              color: 'rgba(180,140,60,0.9)'
            }}
            data-testid="journey-new-reflection-btn"
          >
            <Moon size={18} />
            <span 
              className="text-sm font-medium tracking-wide"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {lang === 'es' ? 'Capturar un Sueño' : 'Capture a Dream'}
            </span>
          </button>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 text-sm"
            style={{ 
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              color: 'rgba(168,162,158,0.4)'
            }}
          >
            {lang === 'es'
              ? 'El viaje continúa con cada reflexión.'
              : 'The journey continues with each reflection.'}
          </motion.p>
        </motion.section>
      </main>
    </div>
  );
};

// ============================================================================
// PROFILE EDITOR WRAPPER (P0.5)
// ============================================================================
const ProfileEditorWrapper = ({ lang }) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  
  const handleUpdate = async (updatedUser) => {
    // Refresh user data in context
    if (refreshUser) {
      await refreshUser();
    }
  };
  
  const handleClose = () => {
    navigate('/profile');
  };
  
  return (
    <ProfileEditor 
      user={user}
      onUpdate={handleUpdate}
      onClose={handleClose}
      lang={lang}
    />
  );
};

// One Profile row. Same row anatomy the Profile card already used; no new visual values.
const ProfileRow = ({ icon: Icon, iconColor, iconBg, label, subtitle, onClick, testId, isLast }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: '16px 20px',
      background: 'transparent',
      border: 'none',
      borderBottom: isLast ? 'none' : '1px solid rgba(255, 255, 255, 0.04)',
      cursor: 'pointer',
      textAlign: 'left'
    }}
  >
    <div style={{
      width: 40,
      height: 40,
      borderRadius: '12px',
      background: iconBg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '14px',
      flexShrink: 0
    }}>
      <Icon size={18} style={{ color: iconColor }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ fontSize: '15px', fontWeight: 500, color: '#FFFFFF', marginBottom: '2px' }}>
        {label}
      </p>
      <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.4)' }}>
        {subtitle}
      </p>
    </div>
    <ChevronRight size={18} style={{ color: 'rgba(255, 255, 255, 0.25)' }} />
  </button>
);

// ============================================================================
// YOU SCREEN (Profile) - RC1 Premium Refinement
// "Who am I becoming?" - Personal Identity, warm journey statistics
// Implements Founder Experience Doctrine v2.0
// ============================================================================
const YouScreen = ({ lang, setLang, t }) => {
  const navigate = useNavigate();
  // The back control returns to the member's actual prior surface, falling back to Home only
  // when there is no in-app entry behind it. Same principle as every other shell back control.
  const goBack = useShellBack();
  const { user, refreshUser } = useAuth();
  const { isPro, isTrial, subscription } = useSubscription();
  const [stats, setStats] = useState({ 
    dreamsRecorded: 0, 
    dreamDays: 0,
    lastReflection: null 
  });
  // D2 — the relationship surfaces open in place, as they do from Settings.
  const [showSharedMemories, setShowSharedMemories] = useState(false);
  const [showMessageHistory, setShowMessageHistory] = useState(false);
  const [showSavedTalks, setShowSavedTalks] = useState(false);
  const [showCompanionChat, setShowCompanionChat] = useState(false);
  const [resumeSessionId, setResumeSessionId] = useState(null);
  // Phase 6 — the member's own picture.
  const [showPhotoSheet, setShowPhotoSheet] = useState(false);

  // Translations - Natural, emotionally appropriate
  const profileT = {
    en: {
      profile: 'Profile',
      memberSince: 'Member since',
      lastReflection: 'Last dream',
      yesterday: 'Yesterday',
      today: 'Today',
      daysAgo: 'days ago',
      premiumMember: 'Premium Member',
      trialMember: 'Premium Trial',
      freeMember: 'Free Member',
      renewsOn: 'Your subscription renews on',
      trialEnds: 'Your trial ends on',
      upgradeToUnlock: 'Upgrade to Premium',
      editProfile: 'Edit Profile',
      sharedMemories: 'Shared Memories',
      sharedMemoriesSub: 'What Arvexis remembers about you',
      messageHistory: 'Message History',
      messageHistorySub: 'Your written conversations with her',
      savedTalks: 'Saved Talks',
      savedTalksSub: 'Talks you chose to keep',
      dreamJournal: 'Dream Journal',
      privacyFooter: 'Private by default. You control what stays.'
    },
    es: {
      profile: 'Perfil',
      memberSince: 'Miembro desde',
      lastReflection: 'Último sueño',
      yesterday: 'Ayer',
      today: 'Hoy',
      daysAgo: 'días',
      premiumMember: 'Miembro Premium',
      trialMember: 'Prueba Premium',
      freeMember: 'Miembro',
      renewsOn: 'Tu suscripción se renueva el',
      trialEnds: 'Tu prueba termina el',
      upgradeToUnlock: 'Mejorar a Premium',
      editProfile: 'Editar Perfil',
      sharedMemories: 'Recuerdos Compartidos',
      sharedMemoriesSub: 'Lo que Arvexis recuerda de ti',
      messageHistory: 'Historial de Mensajes',
      messageHistorySub: 'Tus conversaciones escritas con ella',
      savedTalks: 'Charlas Guardadas',
      savedTalksSub: 'Charlas por voz que elegiste guardar',
      dreamJournal: 'Diario de Sueños',
      privacyFooter: 'Privado por defecto. Tú controlas lo que se conserva.'
    }
  }[lang];

  // D3 — the Dream Journal row's only figures, both from the authoritative
  // /profile/statistics aggregate. Nothing here is derived or inferred.
  const dreamJournalSummary = stats.dreamsRecorded === 0
    ? (lang === 'es' ? 'Aún no hay sueños registrados' : 'No dreams recorded yet')
    : (lang === 'es'
        ? `${stats.dreamsRecorded} ${stats.dreamsRecorded === 1 ? 'sueño' : 'sueños'} en ${stats.dreamDays} ${stats.dreamDays === 1 ? 'día' : 'días'}`
        : `${stats.dreamsRecorded} ${stats.dreamsRecorded === 1 ? 'dream' : 'dreams'} across ${stats.dreamDays} ${stats.dreamDays === 1 ? 'day' : 'days'}`);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // The two Profile statistics come from one member-scoped aggregate. Dream Days is
        // cumulative distinct dream dates resolved in the member's stored account timezone, so
        // it is complete at any dream count and identical on every device.
        const [totals, dreams] = await Promise.all([
          axios.get(`${API}/profile/statistics`),
          axios.get(`${API}/dreams`)
        ]);

        // "Last dream" stays as it was: the newest dream date, which /dreams already returns first.
        let lastReflection = null;
        if (Array.isArray(dreams.data) && dreams.data.length > 0 && dreams.data[0].created_at) {
          lastReflection = new Date(dreams.data[0].created_at);
        }

        setStats({
          dreamsRecorded: totals.data.dreams_recorded,
          dreamDays: totals.data.dream_days,
          lastReflection
        });
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      }
    };
    fetchStats();
  }, []);

  // Format last reflection date
  const formatLastReflection = () => {
    if (!stats.lastReflection) return null;
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const reflectionDate = stats.lastReflection.toDateString();
    
    if (reflectionDate === today) return profileT.today;
    if (reflectionDate === yesterday) return profileT.yesterday;
    
    const daysAgo = Math.floor((new Date() - stats.lastReflection) / 86400000);
    return `${daysAgo} ${profileT.daysAgo}`;
  };

  // Format member since date
  const formatMemberSince = () => {
    if (!user?.created_at) return 'December 2025';
    return new Date(user.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  // PR-1: the authoritative backend fields are `trial_end` and `subscription_end`
  // (SubscriptionInfo). `current_period_end` never existed, so this line never rendered.
  const formatRenewalDate = () => {
    const authoritative = isTrial ? subscription?.trial_end : subscription?.subscription_end;
    if (authoritative) {
      return new Date(authoritative).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
    return null;
  };

  return (
    <AppShellV2 currentPath="/profile" variant="you" lang={lang}>
      <div
        className="min-h-screen utility-surface profile-composed"
        style={{ display: 'flex', flexDirection: 'column' }}
        data-testid="profile-utility-surface"
      >
        
        {/* ════════════════════════════════════════════════════════════════
            HEADER - Global Navigation Standard
            Back button + Title + Settings
        ════════════════════════════════════════════════════════════════ */}
        <header 
          className="flex items-center justify-between px-5 account-mobile-header"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 47px) + 16px)', 
            paddingBottom: '12px'
          }}
        >
          <button 
            onClick={goBack}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'transparent', border: '1px solid rgba(232, 197, 143, 0.38)' }}
            data-testid="profile-back-btn"
          >
            <ChevronLeft size={20} style={{ color: 'rgba(232, 197, 143, 0.9)' }} />
          </button>

          <span
            className="account-wordmark"
            style={{ fontSize: '12px', letterSpacing: '0.42em', color: 'rgba(244, 239, 231, 0.8)' }}
            data-testid="profile-wordmark"
          >
            ARVEXIS
          </span>

          <button 
            onClick={() => navigate('/settings')}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255, 255, 255, 0.05)' }}
            data-testid="settings-btn"
          >
            <Settings size={20} style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </button>
        </header>

        {/* The desktop reference's editorial page title. Mobile keeps the wordmark header. */}
        <h1 className="account-desktop-title" data-testid="profile-desktop-title">
          {profileT.profile}
        </h1>

        <main
          className="account-col account-col--wide"
          style={{
            padding: '0 20px',
            paddingBottom: 'calc(var(--nav-height, 88px) + env(safe-area-inset-bottom, 0px) + 20px)',
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(18px, 3.4vh, 34px)',
            justifyContent: 'flex-start',
          }}
          data-testid="profile-main"
        >
          
          {/* ════════════════════════════════════════════════════════════════
              PERSONAL IDENTITY SECTION
              "This is your place." - Avatar hero, name anchor, member info
          ════════════════════════════════════════════════════════════════ */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-center"
            style={{ paddingTop: '16px', paddingBottom: '32px' }}
          >
            {/* Profile Avatar - Hero Element */}
            <div 
              className="mx-auto mb-5"
              style={{
                position: 'relative',
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'linear-gradient(145deg, rgba(212, 175, 55, 0.12) 0%, rgba(212, 175, 55, 0.04) 100%)',
                border: '2px solid rgba(212, 175, 55, 0.35)',
                boxShadow: '0 0 40px rgba(212, 175, 55, 0.12), inset 0 0 30px rgba(212, 175, 55, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setShowPhotoSheet(true)}
              data-testid="profile-avatar"
            >
              {/* Phase 6 — one avatar chain: their photo, their Google photo, their initials,
                  then the symbol. Tapping it is how a picture is set, replaced or removed. */}
              <MemberAvatar user={user} size={116} symbolSize={65} />
              <span
                style={{
                  position: 'absolute',
                  right: -2,
                  bottom: -2,
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: '#D4AF37',
                  border: '2px solid #0A0A0F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none'
                }}
                data-testid="profile-avatar-edit-badge"
              >
                <Camera size={16} style={{ color: '#0A0A0F' }} />
              </span>
            </div>
            
            {/* User Name - Visual Anchor — Cormorant Garamond for elegance */}
            <h2 style={{ 
              fontSize: '32px', 
              fontWeight: 400, 
              color: '#FFFFFF', 
              marginBottom: '8px',
              letterSpacing: '-0.3px',
              fontFamily: '"Cormorant Garamond", Georgia, serif'
            }}>
              {user?.name || user?.email?.split('@')[0] || 'Dreamer'}
            </h2>
            
            {/* Member Info */}
            <div style={{ 
              fontSize: '14px', 
              color: 'rgba(255, 255, 255, 0.45)',
              marginBottom: '16px',
              lineHeight: 1.6
            }}>
              <p>{profileT.memberSince} {formatMemberSince()}</p>
              {stats.lastReflection && (
                <p>{profileT.lastReflection} {formatLastReflection()}</p>
              )}
            </div>
            
            {/* Subscription Status */}
            <div style={{ marginBottom: '16px' }}>
              {isPro ? (
                <div>
                  <span 
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.08) 100%)',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      color: '#D4AF37',
                      fontSize: '13px',
                      fontWeight: 500
                    }}
                  >
                    <Crown size={14} />
                    {isTrial ? profileT.trialMember : profileT.premiumMember}
                  </span>
                  {formatRenewalDate() && (
                    <p style={{ 
                      fontSize: '12px', 
                      color: 'rgba(255, 255, 255, 0.35)',
                      marginTop: '8px'
                    }}>
                      {isTrial ? profileT.trialEnds : profileT.renewsOn} {formatRenewalDate()}
                    </p>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => navigate('/subscription')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '11px 22px',
                    borderRadius: '999px',
                    background: 'rgba(232, 197, 143, 0.06)',
                    border: '1px solid rgba(232, 197, 143, 0.55)',
                    color: '#E8C58F',
                    fontSize: '15px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                  data-testid="upgrade-to-pro-btn"
                >
                  <Crown size={16} />
                  {profileT.upgradeToUnlock}
                </button>
              )}
            </div>
            
            {/* Edit Profile Button */}
            <button
              onClick={() => navigate('/profile/edit')}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#FFFFFF',
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '24px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              data-testid="edit-profile-btn"
            >
              {profileT.editProfile}
            </button>
          </motion.section>

          {/* ════════════════════════════════════════════════════════════════
              RELATIONSHIP + DREAM JOURNAL
              D2 — the three relationship surfaces open here as the very same components
              Settings already mounts. No new routes, no new components, no backend change.
              No counts are shown: no authoritative count exists for Message History or
              Saved Talks (both read limit-capped lists), so a number here would be false.
              D3 — one compact Dream Journal row, carrying the already-authoritative
              /profile/statistics figures and nothing derived.
          ════════════════════════════════════════════════════════════════ */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            style={{ marginBottom: '28px' }}
          >
            <div
              data-testid="profile-relationship-card"
              style={{
                background: 'rgba(18, 18, 30, 0.58)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '28px',
                overflow: 'hidden',
                boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)'
              }}
            >
              <ProfileRow
                icon={Heart}
                iconColor="#F0A8B4"
                iconBg="rgba(240, 168, 180, 0.12)"
                label={profileT.sharedMemories}
                subtitle={profileT.sharedMemoriesSub}
                onClick={() => setShowSharedMemories(true)}
                testId="profile-shared-memories-row"
              />
              <ProfileRow
                icon={MessageCircle}
                iconColor="#60A5FA"
                iconBg="rgba(96, 165, 250, 0.12)"
                label={profileT.messageHistory}
                subtitle={profileT.messageHistorySub}
                onClick={() => setShowMessageHistory(true)}
                testId="profile-message-history-row"
              />
              <ProfileRow
                icon={Mic}
                iconColor="#5EC8B8"
                iconBg="rgba(94, 200, 184, 0.12)"
                label={profileT.savedTalks}
                subtitle={profileT.savedTalksSub}
                onClick={() => setShowSavedTalks(true)}
                testId="profile-saved-talks-row"
              />
              <ProfileRow
                icon={BookOpen}
                iconColor="#A78BFA"
                iconBg="rgba(138, 99, 210, 0.12)"
                label={profileT.dreamJournal}
                subtitle={dreamJournalSummary}
                onClick={() => navigate('/dreams')}
                testId="profile-dream-journal-row"
                isLast
              />
            </div>
          </motion.section>

          {/* D4 — quiet privacy footer. A statement, not a destination. */}
          <p
            style={{ 
              fontSize: '12px', 
              color: 'rgba(255, 255, 255, 0.28)', 
              textAlign: 'center',
              marginTop: 'auto',
              paddingTop: 'clamp(12px, 2.6vh, 28px)',
              lineHeight: 1.6
            }}
            data-testid="profile-privacy-footer"
          >
            {profileT.privacyFooter}
          </p>
        </main>

        {/* Phase 6 — setting, replacing or removing the member's own picture. */}
        {showPhotoSheet && (
          <ProfilePhotoSheet
            lang={lang}
            hasPhoto={!!user?.profile_photo?.photo_id}
            onSaved={async () => { await refreshUser?.(); }}
            onRemoved={async () => { await refreshUser?.(); }}
            onClose={() => setShowPhotoSheet(false)}
          />
        )}

        {/* D2 — the same relationship surfaces Settings mounts, opened from Profile.
            The components themselves are untouched. */}
        <AnimatePresence>
          {showSharedMemories && (
            <SharedMemories
              lang={lang}
              onClose={() => setShowSharedMemories(false)}
            />
          )}
          {showMessageHistory && (
            <PastReflections
              lang={lang}
              onClose={() => setShowMessageHistory(false)}
              onResumeSession={(sessionId) => {
                setResumeSessionId(sessionId);
                setShowMessageHistory(false);
                setShowCompanionChat(true);
              }}
            />
          )}
          {showSavedTalks && (
            <ConversationHistory
              lang={lang}
              onClose={() => setShowSavedTalks(false)}
            />
          )}
        </AnimatePresence>

        {/* Resuming a written conversation behaves exactly as it does from Settings. */}
        <AnimatePresence>
          {showCompanionChat && (
            <div
              className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100]"
              data-testid="profile-companion-chat-modal"
            >
              <div className="h-full flex flex-col">
                {/* Phase 6: leaving an opened conversation returns the member to Message
                    History, not to a dead end. The browser gesture is no longer the only way. */}
                <button
                  onClick={() => {
                    setShowCompanionChat(false);
                    setResumeSessionId(null);
                    setShowMessageHistory(true);
                  }}
                  className="absolute z-20 rounded-full flex items-center justify-center"
                  style={{ top: 'calc(env(safe-area-inset-top, 12px) + 10px)', left: 16,
                           width: 44, height: 44, background: 'rgba(5, 8, 14, 0.6)',
                           border: '1px solid rgba(232, 197, 143, 0.38)' }}
                  aria-label={lang === 'es' ? 'Volver al historial' : 'Back to Message History'}
                  data-testid="profile-companion-chat-close"
                >
                  <ChevronLeft className="w-5 h-5" style={{ color: 'rgba(232, 197, 143, 0.9)' }} />
                </button>

                <CompanionChat
                  surface="daily_companion"
                  lang={lang}
                  existingSessionId={resumeSessionId}
                  initialMessage={
                    resumeSessionId
                      ? (lang === 'es'
                          ? 'Bienvenida de vuelta. ¿Dónde nos quedamos?'
                          : 'Welcome back. Where were we?')
                      : null
                  }
                  onClose={() => {
                    setShowCompanionChat(false);
                    setResumeSessionId(null);
                  }}
                />
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </AppShellV2>
  );
};

// ============================================================================
// SETTINGS SCREEN - Premium UI v2.0
// Gold-ringed avatar, grouped section cards, language toggle pill
// Implements Sprint 002 Section D - Settings Audit
// ============================================================================
const SettingsScreen = ({ lang, setLang, t, theme, setTheme, timeFormat, setTimeFormat }) => {
  const navigate = useNavigate();
  const goBack = useShellBack();
  const { user, logout, refreshUser } = useAuth();
  const { subscription, isPro, isTrial } = useSubscription();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  // One canonical Arvexis voice: one selection, every spoken surface.
  const [showVoiceAudition, setShowVoiceAudition] = useState(false);
  // 6D — which voice Arvexis is actually using, and whether the member ever chose it. Read from
  // the same canonical contract the audition surface reads. Nothing here decides a voice.
  const [voiceStatus, setVoiceStatus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [isExportingData, setIsExportingData] = useState(false);
  const [composingMessage, setComposingMessage] = useState('');
  const [showComposingOverlay, setShowComposingOverlay] = useState(false);
  const [showDataStorageModal, setShowDataStorageModal] = useState(false);
  // Phase 6B — the member's signed-in devices, and the password/provider proof deletion now needs.
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  // Signed-in devices is presented over this page, so the page beneath is contained
  // while it is open, and the surface hands the foreground back if the member leaves
  // the document and Safari restores it — the same shared behaviour as the others.
  useFullScreenSurface(showDevicesModal, () => setShowDevicesModal(false));
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReauthMethod, setDeleteReauthMethod] = useState(null);
  // Phase 6B (STOP-2) — a fresh Google proof, held only in this tab's memory until the member
  // confirms the deletion they began. Never persisted, never reused.
  const [googleDeletionProof, setGoogleDeletionProof] = useState(null);
  const [showExportChoices, setShowExportChoices] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [showSharedMemories, setShowSharedMemories] = useState(false);
  // 3.9 — the specific row a memory status asked us to show.
  const [memoryFocusItemId, setMemoryFocusItemId] = useState(null);
  const [showPastReflections, setShowPastReflections] = useState(false);
  // Founder-authorized usage metering. The row and the surface only exist once an allowance is
  // actually configured — an unconfigured allowance shows nothing rather than a placeholder.
  const [showUsagePeriod, setShowUsagePeriod] = useState(false);
  const [usageConfigured, setUsageConfigured] = useState(false);
  const [resumeSessionId, setResumeSessionId] = useState(null);
  const [showCompanionChat, setShowCompanionChat] = useState(false);
  const [showSavedTalks, setShowSavedTalks] = useState(false);
  const [showMemorySummary, setShowMemorySummary] = useState(false);
  const settingsLocation = useLocation();

  // The menu's relationship rows arrive here asking for one specific surface.
  useEffect(() => {
    const open = settingsLocation.state?.open;
    if (open === 'shared-memories') {
      setMemoryFocusItemId(settingsLocation.state?.focusItemId || null);
      setShowSharedMemories(true);
    }
    if (open === 'message-history') setShowPastReflections(true);
    if (open === 'saved-talks') setShowSavedTalks(true);
  }, [settingsLocation.state]);

  // Is there an allowance in effect at all? One read of the same canonical contract both
  // usage presentations use. Nothing here computes or caches a limit.
  useEffect(() => {
    let alive = true;
    fetch(`${API}/usage/summary`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (alive) setUsageConfigured(!!data?.any_allowance_configured); })
      .catch(() => { /* metering unavailable: the row simply does not appear */ });
    return () => { alive = false; };
  }, []);

  // Marketing emails — the member's own consent, given or withdrawn here at any time. Absence of
  // consent is no consent: this reads the record and defaults nothing on.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [marketingSaving, setMarketingSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/marketing/consent`)
      .then((r) => { if (alive) setMarketingOptIn(Boolean(r.data?.opt_in)); })
      .catch(() => { /* unreadable: the row stays off rather than claiming consent */ });
    return () => { alive = false; };
  }, []);

  const toggleMarketing = async () => {
    const next = !marketingOptIn;
    setMarketingSaving(true);
    setMarketingOptIn(next);
    try {
      await axios.patch(`${API}/marketing/consent`, { enabled: next });
    } catch {
      setMarketingOptIn(!next);
    } finally {
      setMarketingSaving(false);
    }
  };

  // 6D — the voice Arvexis is using right now, stated rather than silently substituted. The
  // server is the only authority: `in_use` is resolve_voice()'s answer, `awaiting_selection` says
  // whether the member has ever chosen. The label comes from the server's own catalogue, so no
  // voice name is ever hardcoded here. If this read fails the row keeps its original copy.
  useEffect(() => {
    let alive = true;
    fetch(`${API}/voice/candidates`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !data?.in_use) return;
        const match = (data.candidates || []).find((c) => c.id === data.in_use);
        setVoiceStatus({
          inUseLabel: match?.label || data.in_use,
          awaiting_selection: !!data.awaiting_selection,
        });
      })
      .catch(() => { /* unavailable: the row keeps its original copy */ });
    return () => { alive = false; };
  }, [showVoiceAudition]);

  // Phase 6B (STOP-2) — returning from the Google proof hop. The deletion the member started is
  // reopened with their proof in hand; they still have to confirm it. The marker is taken off the
  // URL immediately, so a refresh or a Back/Forward step cannot replay a spent flow.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('deletion_proof') !== '1') return;
    window.history.replaceState(null, '', '/settings');
    const proof = takeGoogleDeletionProof();
    setDeleteReauthMethod('google');
    setShowDeleteConfirm(true);
    if (proof) {
      setGoogleDeletionProof(proof);
      setDeleteError(null);
    } else {
      setGoogleDeletionProof(null);
      setDeleteError(
        lang === 'es'
          ? 'Esa confirmación no se completó. No se ha eliminado nada.'
          : 'That confirmation did not complete. Nothing has been deleted.'
      );
    }
  }, []);
  
  // Notification preferences state
  const [notifications, setNotifications] = useState(() => {
    const saved = localStorage.getItem('arvexis_notifications');
    return saved ? JSON.parse(saved) : {
      dreamReminders: true,
      weeklyInsights: true,
      milestoneAlerts: true,
      dreamInsightsUpdates: true  // Enabled by default - users can opt out
    };
  });
  

  // Save notification preferences
  useEffect(() => {
    localStorage.setItem('arvexis_notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Member-facing export (export_version 2.0). A deliberate presentation layer, not a database
  // dump: internal prompt/orchestration content, experiment mechanics, provider telemetry and
  // storage details are excluded, and the exclusions are explained in plain language inside the
  // file itself (about_this_file).
  const exportMemberData = async () => {
    setIsExportingData(true);
    try {
      const response = await axios.get(`${API}/export/member-data`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      // The URL used to be revoked at once, which in Safari cancels the download before it starts.
      await deliverDownload(blob, `arvexis-my-data-${new Date().toISOString().split('T')[0]}.json`,
        'application/json');
    } catch (error) {
      console.error('Export failed:', error);
      setExportError(lang === 'es' ? 'No se pudo exportar' : 'Could not export your data');
    } finally {
      setIsExportingData(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    // Explicit sign-out returns the member to the public welcome, not to a sign-in form.
    navigate('/', { replace: true });
  };

  // Phase 6B (ruling 9a) — the member's language is account state now, not a device setting.
  // The interface changes immediately; the account is told, so every device and their export agree.
  const selectLanguage = async (next) => {
    if (next === lang) return;
    setLang(next);
    try {
      await axios.put(`${API}/auth/profile`, { preferred_language: next });
      if (refreshUser) await refreshUser();
    } catch (err) {
      console.error('Language preference did not save:', err);
    }
  };

  // Phase 6 Settings ruling — the member's time format is account state, exactly as their
  // language is. Independent of language: choosing one never rewrites the other. Presentation
  // only; no stored timestamp, timezone or chronology is touched.
  const selectTimeFormat = async (next) => {
    if (next === timeFormat) return;
    setTimeFormat(next);
    try {
      await axios.put(`${API}/auth/profile`, { time_format: next });
      if (refreshUser) await refreshUser();
    } catch (err) {
      console.error('Time format preference did not save:', err);
    }
  };

  // Help & Support opens email
  const handleHelp = () => {
    const subject = encodeURIComponent('Arvexis Support Request');
    const body = encodeURIComponent(
      'Hello Arvexis Support,\n\n' +
      'I have a question or need assistance with:\n\n' +
      '[Please describe your issue here]\n\n' +
      '---\nSent from Arvexis App'
    );
    window.open(`mailto:support@arvexisapp.com?subject=${subject}&body=${body}`, '_blank');
  };

  // Contact Support (general inquiries)
  const handleContactSupport = () => {
    const subject = encodeURIComponent('Arvexis Inquiry');
    const body = encodeURIComponent(
      'Hello Arvexis Team,\n\n' +
      '[Your message here]\n\n' +
      '---\nSent from Arvexis App'
    );
    window.open(`mailto:hello@arvexisapp.com?subject=${subject}&body=${body}`, '_blank');
  };

  // Account Deletion with two-step confirmation
  const [deleteError, setDeleteError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Phase 6B — which proof this account owes, decided from the account record (the server has the
  // final say and returns `required` if this is wrong).
  const deletionProofMethod = deleteReauthMethod || user?.auth_method || 'password';
  // Request-time deletion confirmation: server-returned maximum removal deadline.
  const [deletionDeadline, setDeletionDeadline] = useState(null);
  
  // "no later than" deadline — maximum removal date, never an exact or guaranteed date.
  const formatDeletionDeadline = (iso) => {
    const d = iso ? new Date(iso) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return new Intl.DateTimeFormat(lang === 'es' ? 'es-ES' : 'en-GB', {
      day: 'numeric', month: 'long', year: 'numeric'
    }).format(d);
  };

  const handleDeleteAccount = async () => {
    // Phase 6B (STOP-2) — a Google account cannot be closed without a fresh provider proof.
    // Without one, this asks for it and sends no deletion request at all.
    if (deletionProofMethod === 'google' && !googleDeletionProof) {
      setDeleteError(
        lang === 'es'
          ? 'Confirma que eres tú con Google antes de cerrar tu cuenta.'
          : 'Please confirm it is you with Google before we close your account.'
      );
      return;
    }
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      // Phase 6B (ruling 4a) — proof travels with the request. The server verifies before it
      // writes anything, so a refused proof deletes nothing.
      const proof = {};
      if (deleteReauthMethod === 'apple') {
        proof.apple_id_token = await appleDeletionProof();
      } else if (deletionProofMethod === 'google') {
        proof.google_credential = googleDeletionProof;  // Oct 25: verified Google ID token
      } else {
        proof.password = deletePassword;
      }
      const res = await axios.delete(`${API}/auth/me`, { data: proof });
      setDeletionDeadline(res?.data?.scheduled_deletion || null);
      setDeletePassword('');
      setGoogleDeletionProof(null);
      setDeleteLoading(false);
    } catch (err) {
      // The proof is single-use: a refused attempt spends it, and a new one must be taken.
      setGoogleDeletionProof(null);
      const code = err?.response?.data?.detail?.error_code;
      const required = err?.response?.data?.detail?.required;
      if (required) setDeleteReauthMethod(required);
      const messages = {
        en: {
          password_incorrect: 'That password is not right. Nothing has been deleted.',
          proof_required: 'Please confirm it is you before we close your account.',
          proof_not_recent: 'That confirmation has expired. Please try again.',
          identity_mismatch: 'That sign-in belongs to a different account. Nothing has been deleted.',
          provider_not_linked: 'That sign-in method is not linked to this account.',
          provider_unavailable: 'We could not reach your sign-in provider. Nothing has been deleted.',
          rate_limited: 'Too many attempts. Please try again in 15 minutes.',
        },
        es: {
          password_incorrect: 'Esa contraseña no es correcta. No se ha eliminado nada.',
          proof_required: 'Confirma que eres tú antes de cerrar tu cuenta.',
          proof_not_recent: 'Esa confirmación ha caducado. Inténtalo de nuevo.',
          identity_mismatch: 'Ese inicio de sesión pertenece a otra cuenta. No se ha eliminado nada.',
          provider_not_linked: 'Ese método de inicio de sesión no está vinculado a esta cuenta.',
          provider_unavailable: 'No pudimos contactar con tu proveedor. No se ha eliminado nada.',
          rate_limited: 'Demasiados intentos. Inténtalo de nuevo en 15 minutos.',
        },
      };
      const table = messages[lang] || messages.en;
      setDeleteError(
        (err?.response?.status === 429 ? table.rate_limited : table[code])
        || (lang === 'es'
          ? 'Error al eliminar la cuenta. No se ha eliminado nada.'
          : 'Could not close your account. Nothing has been deleted.')
      );
      setDeleteLoading(false);
    }
  };

  // A fresh Apple proof, taken in-page through the Apple SDK the sign-in flow already loads.
  // Google cannot be proved in-page: its proof comes back through the provider hop, captured by
  // the deletion-intent branch at the top of this file.
  const appleDeletionProof = async () => {
    const response = await window.AppleID.auth.signIn();
    return response?.authorization?.id_token;
  };

  // Export Data - Full data export (JSON + PDF options)
  const handleExportData = async (format) => {
    setExportLoading(true);
    setExportError(null);
    
    try {
      // Fetch all dreams for export
      const response = await axios.get(`${API}/dreams?limit=1000`);
      const dreams = response.data || [];
      
      if (dreams.length === 0) {
        setExportError(lang === 'es' ? 'No hay datos para exportar.' : 'No data to export.');
        setExportLoading(false);
        return;
      }

      const userName = user?.name || user?.email?.split('@')[0] || '';
      const exportDate = new Date().toISOString().split('T')[0];
      
      if (format === 'json') {
        // Export as JSON
        const exportData = {
          exportDate: new Date().toISOString(),
          user: {
            email: user?.email,
            name: user?.name,
            createdAt: user?.created_at
          },
          totalDreams: dreams.length,
          dreams: dreams.map(d => ({
            id: d.id,
            title: d.title,
            transcript: d.transcript,
            emotion: d.emotion,
            emotional_summary: d.emotional_summary,
            symbols: d.symbols,
            themes: d.themes,
            integrated_interpretation: d.integrated_interpretation,
            reflection_questions: d.reflection_questions,
            grounding_insight: d.grounding_insight,
            created_at: d.created_at,
            language: d.language
          }))
        };
        
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        // Same file, delivered without the instant revoke that cancelled Safari's download.
        await deliverDownload(blob, `arvexis-dreams-${exportDate}.json`, 'application/json');
        setShowExportModal(false);
      } else if (format === 'pdf') {
        // Use Premium Dream Journal Generator V2 (Sprint 3)
        setShowExportModal(false);
        setShowComposingOverlay(true);
        setComposingMessage(lang === 'es' ? 'Preparando tu diario...' : 'Preparing your journal...');
        
        try {
          const { downloadDreamJournalPDF } = await import('./utils/dreamJournalGeneratorV2');
          
          // For multiple dreams, generate one PDF per dream
          // In future, could combine into single multi-dream PDF
          for (const dream of dreams) {
            await downloadDreamJournalPDF(dream, userName, lang, ({ step, progress }) => {
              const messages = {
                preparing: lang === 'es' ? 'Preparando tu diario...' : 'Preparing your journal...',
                loading: lang === 'es' ? 'Cargando imágenes...' : 'Loading images...',
                rendering: lang === 'es' ? 'Creando páginas...' : 'Creating pages...',
                complete: lang === 'es' ? '¡Completo!' : 'Complete!'
              };
              setComposingMessage(messages[step] || messages.rendering);
            });
          }
        } catch (pdfErr) {
          console.error('PDF generation failed:', pdfErr);
          setExportError(lang === 'es' ? 'Error al crear el diario PDF.' : 'Failed to create PDF journal.');
        } finally {
          setShowComposingOverlay(false);
          setComposingMessage('');
        }
      }
    } catch (err) {
      console.error('Export failed:', err);
      setExportError(err.message || (lang === 'es' ? 'Error al exportar. Intenta de nuevo.' : 'Export failed. Please try again.'));
      setShowComposingOverlay(false);
    } finally {
      setExportLoading(false);
    }
  };

  // Toggle notification setting
  const toggleNotification = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle share functionality
  const handleShare = async () => {
    const shareData = {
      title: 'Arvexis Dream Journal',
      text: lang === 'es' 
        ? 'Descubre el significado de tus sueños con Arvexis' 
        : 'Discover the meaning of your dreams with Arvexis',
      url: 'https://arvexisapp.com'
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to copy link
          await navigator.clipboard.writeText(shareData.url);
          setToastMessage(lang === 'es' ? 'Enlace copiado al portapapeles' : 'Link copied to clipboard');
          setTimeout(() => setToastMessage(null), 2500);
        }
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      await navigator.clipboard.writeText(shareData.url);
      setToastMessage(lang === 'es' ? 'Enlace copiado al portapapeles' : 'Link copied to clipboard');
      setTimeout(() => setToastMessage(null), 2500);
    }
  };

  // Share Arvexis — member-initiated invitation. Nothing is tracked: no referral code, no reward,
  // no member identity, and not one word of a conversation or memory leaves the device. The link
  // is the public site, the same one anybody could type.
  const ARVEXIS_INVITATION = (
    'A little something worth sharing. \u{1F49C}\n\n' +
    'Meet Arvexis \u2014 a private AI companion that gets to know you over time. ' +
    'A space to talk, reflect, and feel heard.\n\n' +
    'https://arvexisapp.com'
  );

  const handleShareArvexis = async () => {
    const say = (en, es) => {
      setToastMessage(lang === 'es' ? es : en);
      setTimeout(() => setToastMessage(null), 2800);
    };
    // The approved message already ends with the link, so `url` is deliberately not passed —
    // some share targets would otherwise show the same link twice.
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Arvexis', text: ARVEXIS_INVITATION });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;   // they changed their mind. That is allowed.
      }
    }
    try {
      await navigator.clipboard.writeText(ARVEXIS_INVITATION);
      say('Invitation copied — paste it wherever you like',
          'Invitación copiada — pégala donde quieras');
    } catch (err) {
      say('Share arvexisapp.com with whoever you have in mind',
          'Comparte arvexisapp.com con quien tengas en mente');
    }
  };


  return (
    <AppShellV2 currentPath="/settings" variant="settings" showNav={false}>
      <div className="min-h-screen utility-surface utility-surface--atmospheric" data-testid="settings-utility-surface">
        {/* Header with Back Button */}
        <header 
          className="flex items-center gap-4 px-5 account-mobile-header"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 47px) + 12px)', 
            paddingBottom: '12px'
          }}
        >
          <button 
            onClick={goBack}
            className="btn-circle"
            style={{ background: 'transparent', border: '1px solid rgba(244,239,231,0.12)' }}
            data-testid="settings-back-btn"
          >
            <ChevronLeft className="w-5 h-5" style={{ color: 'rgba(244,239,231,0.8)' }} />
          </button>
          <span
            style={{ fontSize: '12px', letterSpacing: '0.42em', color: 'rgba(244,239,231,0.8)', margin: '0 auto', paddingRight: '44px' }}
            data-testid="settings-wordmark"
          >
            ARVEXIS
          </span>
        </header>

        {/* Phase 6 Settings density: the same bounded content stage its own child surfaces
            (Profile, Devices, Subscription, Change Password) already use, so a row's label and
            its control belong to one composition instead of sitting at opposite edges of a
            desktop display. Below 1024px `account-col` is full width, so the phone keeps the
            measurements it already had. Groups, rows, controls, state and behaviour unchanged. */}
        <div className="utility-head account-col account-col--wide" data-testid="settings-head">
          <p className="utility-head__eyebrow">{lang === 'es' ? 'CONFIGURACIÓN' : 'SETTINGS'}</p>
          <h1 className="utility-head__title">{t.settingsTitle}</h1>
          <p className="utility-head__sub">
            {lang === 'es' ? 'Cómo funcionan tu cuenta y Arvexis.' : 'How your account and Arvexis work.'}
          </p>
          <div className="utility-head__rule" />
        </div>

        <main className="account-col account-col--wide" style={{ padding: '0 20px', paddingBottom: '40px' }}>
          {/* Profile Card - Navigates to Profile page */}
          {user && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="profile-card"
              onClick={() => navigate('/profile')}
              style={{ cursor: 'pointer' }}
              data-testid="profile-card"
            >
              {/* Gold-ringed Avatar — same authoritative precedence as Profile: their photo,
                  their Google photo, their initials, then the Arvexis monogram. */}
              <div className="profile-card__avatar" style={{ overflow: 'hidden' }}
                   data-testid="settings-member-avatar">
                <MemberAvatar user={user} size={60} symbolSize={30} />
              </div>
              <div className="profile-card__info">
                <h2 className="profile-card__name">{user.name || 'Reflector'}</h2>
                <p className="profile-card__email">{user.email}</p>
                {isPro && (
                  <div className="gold-badge" style={{ marginTop: '4px' }}>
                    <Crown className="gold-badge__icon" />
                    <span className="gold-badge__text">{isTrial ? 'PRO TRIAL' : 'PRO'}</span>
                  </div>
                )}
              </div>
              <ChevronRight className="profile-card__chevron" />
            </motion.div>
          )}

          {/* Account Section */}
          <div className="section-label" style={{ marginTop: '20px' }}>
            {lang === 'es' ? 'CUENTA' : 'ACCOUNT'}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card-surface"
            style={{ padding: 0, overflow: 'hidden' }}
          >
            {/* Subscription */}
            <div 
              className="settings-row settings-row--gold"
              onClick={() => navigate('/subscription')}
              data-testid="manage-subscription-link"
            >
              <div className="settings-row__icon">
                <Crown />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{isPro ? t.manageSubscription : (lang === 'es' ? 'Elegir una membresía' : 'Choose a membership')}</span>
                <span className="settings-row__subtitle">{isPro ? (isTrial ? t.trialActiveMessage : t.subscriptionActive) : (lang === 'es' ? 'Ver planes y facturación' : 'See plans and billing')}</span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </div>
            
            {/* Change Password - Only show if user has password-based auth */}
            {user?.auth_method !== 'google' && user?.auth_method !== 'apple' && (
              <div 
                className="settings-row"
                onClick={() => navigate('/change-password')}
                data-testid="change-password-link"
              >
                <div className="settings-row__icon">
                  <Lock className="w-5 h-5" />
                </div>
                <div className="settings-row__content">
                  <span className="settings-row__label">{t.changePassword}</span>
                  <span className="settings-row__subtitle">{lang === 'es' ? 'Actualiza tu contraseña' : 'Update your password'}</span>
                </div>
                <ChevronRight className="settings-row__chevron" />
              </div>
            )}
          </motion.div>

          {/* Arvexis Section - Phase F */}
          <div className="section-label" style={{ marginTop: '20px' }}>
            ARVEXIS
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.11 }}
            className="glass-card-surface"
            style={{ padding: 0, overflow: 'hidden' }}
          >
            {/* Shared Memories */}
            <div 
              className="settings-row"
              onClick={() => setShowSharedMemories(true)}
              data-testid="shared-memories-link"
            >
              <div className="settings-row__icon">
                <Heart className="w-5 h-5" />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{lang === 'es' ? 'Recuerdos Compartidos' : 'Shared Memories'}</span>
                <span className="settings-row__subtitle">{lang === 'es' ? 'Lo que Arvexis recuerda de ti' : 'What Arvexis remembers about you'}</span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </div>
            
            {/* Message History — Message/chat sessions. Deliberately NOT called
                "Conversations": saved Talk history is a separate surface below. */}
            <div 
              className="settings-row"
              onClick={() => setShowPastReflections(true)}
              data-testid="message-history-link"
            >
              <div className="settings-row__icon">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{lang === 'es' ? 'Historial de Mensajes' : 'Message History'}</span>
                <span className="settings-row__subtitle">{lang === 'es' ? 'Tus conversaciones escritas con ella' : 'Your written conversations with her'}</span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </div>

            {/* Usage this period — the canonical detailed capacity surface. Hidden entirely
                while no allowance is configured: there is no truthful figure to show yet. */}
            {usageConfigured && (
              <div
                className="settings-row"
                onClick={() => setShowUsagePeriod(true)}
                data-testid="usage-period-link"
              >
                <div className="settings-row__icon">
                  <BarChart3 className="w-5 h-5" />
                </div>
                <div className="settings-row__content">
                  <span className="settings-row__label">{lang === 'es' ? 'Uso de este período' : 'Usage this period'}</span>
                  <span className="settings-row__subtitle">{lang === 'es' ? 'Talk y Message, y cuándo se reinician' : 'Talk and Message, and when they reset'}</span>
                </div>
                <ChevronRight className="settings-row__chevron" />
              </div>
            )}

            {/* D6 — the standalone "What Arvexis Remembers" destination is retired. Shared
                Memories above is the canonical member-facing memory surface. The MemorySummary
                narrative layer, /api/memory/summary and all memory data and logic are preserved
                untouched for the later Memory Intent + Provenance review; only this duplicate
                entrance is no longer member-facing. */}

            {/* Saved Talks — transcripts kept from voice sessions. Previously reachable only
                from an icon inside Chat, which is where its delete control was hiding. */}
            <div 
              className="settings-row"
              onClick={() => setShowSavedTalks(true)}
              data-testid="saved-talks-link"
            >
              <div className="settings-row__icon">
                <Mic className="w-5 h-5" />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{lang === 'es' ? 'Charlas Guardadas' : 'Saved Talks'}</span>
                <span className="settings-row__subtitle">{lang === 'es' ? 'Charlas por voz que elegiste guardar' : 'Talks you chose to keep'}</span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </div>
          </motion.div>

          {/* Preferences Section */}
          <div className="section-label" style={{ marginTop: '20px' }}>
            {lang === 'es' ? 'PREFERENCIAS' : 'PREFERENCES'}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="glass-card-surface"
            style={{ padding: 0, overflow: 'hidden' }}
          >
            {/* Require Face ID — a local privacy gate in front of an already-signed-in session */}
            <BiometricLockSetting lang={lang} />
            
            {/* Language Selection */}
            <div 
              className="settings-row"
              data-testid="language-setting"
            >
              <div className="settings-row__icon">
                <Globe className="w-5 h-5" />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{t.language}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => selectLanguage('en')}
                  className={`min-h-[44px] px-4 rounded-lg text-sm font-medium transition-all ${
                    lang === 'en' 
                      ? 'bg-[#E8C58F]/10 text-[#E8C58F] border border-[#E8C58F]/34'
                      : 'bg-[#F4EFE7]/[0.03] text-[#F4EFE7]/56 border border-transparent hover:bg-[#F4EFE7]/[0.06]'
                  }`}
                  data-testid="lang-en-btn"
                >
                  English
                </button>
                <button
                  onClick={() => selectLanguage('es')}
                  className={`min-h-[44px] px-4 rounded-lg text-sm font-medium transition-all ${
                    lang === 'es' 
                      ? 'bg-[#E8C58F]/10 text-[#E8C58F] border border-[#E8C58F]/34'
                      : 'bg-[#F4EFE7]/[0.03] text-[#F4EFE7]/56 border border-transparent hover:bg-[#F4EFE7]/[0.06]'
                  }`}
                  data-testid="lang-es-btn"
                >
                  Español
                </button>
              </div>
            </div>
            
            {/* Time Format — the member's own clock, applied wherever a time is shown to them. */}
            <div 
              className="settings-row"
              data-testid="time-format-setting"
            >
              <div className="settings-row__icon">
                <Clock className="w-5 h-5" />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">
                  {lang === 'es' ? 'Formato de hora' : 'Time Format'}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => selectTimeFormat('12h')}
                  className={`min-h-[44px] px-4 rounded-lg text-sm font-medium transition-all ${
                    timeFormat !== '24h'
                      ? 'bg-[#E8C58F]/10 text-[#E8C58F] border border-[#E8C58F]/34'
                      : 'bg-[#F4EFE7]/[0.03] text-[#F4EFE7]/56 border border-transparent hover:bg-[#F4EFE7]/[0.06]'
                  }`}
                  data-testid="time-12h-btn"
                >
                  {lang === 'es' ? '12 horas' : '12-hour'}
                </button>
                <button
                  onClick={() => selectTimeFormat('24h')}
                  className={`min-h-[44px] px-4 rounded-lg text-sm font-medium transition-all ${
                    timeFormat === '24h'
                      ? 'bg-[#E8C58F]/10 text-[#E8C58F] border border-[#E8C58F]/34'
                      : 'bg-[#F4EFE7]/[0.03] text-[#F4EFE7]/56 border border-transparent hover:bg-[#F4EFE7]/[0.06]'
                  }`}
                  data-testid="time-24h-btn"
                >
                  {lang === 'es' ? '24 horas' : '24-hour'}
                </button>
              </div>
            </div>
            
            {/* Theme — hidden for V1: Dark is the only supported theme, so there is no member
                choice to make. The theme infrastructure is intentionally left in place. */}
          </motion.div>
            {/* Privacy Section */}
          <div className="section-label" style={{ marginTop: '20px' }}>
            {t.privacy}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className="glass-card-surface"
            style={{ padding: 0, overflow: 'hidden' }}
          >
            {/* Data Storage — hidden from the member-facing surface (Phase 6 Settings ruling).
                A presentation decision only: the modal, the export and the deletion behaviour
                behind it are untouched and still governed exactly as before. */}

            {/* Phase 6B — Signed-in devices. Every row here is a real session that can really be
                ended; nothing on this surface is cosmetic. */}
            <button 
              className="settings-row settings-row--teal"
              onClick={() => setShowDevicesModal(true)}
              data-testid="signed-in-devices-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon">
                <Smartphone />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">
                  {lang === 'es' ? 'Dispositivos con sesión' : 'Signed-in devices'}
                </span>
                <span className="settings-row__subtitle">
                  {lang === 'es' ? 'Ver y cerrar sesiones' : 'See and end your sessions'}
                </span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </button>
            
            {/* R10: Usage Analytics and Crash Reports toggles removed for V1.
                They wrote to localStorage and were read by no code — member-facing privacy
                controls must not imply behaviour the backend does not honour. */}

            {/* Marketing emails — optional and member-controlled. This is Arvexis news only:
                account, security, billing and service messages are separate and continue. */}
            <button
              className="settings-row"
              onClick={toggleMarketing}
              disabled={marketingSaving}
              aria-pressed={marketingOptIn}
              data-testid="marketing-emails-toggle"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon">
                <Mail className="w-5 h-5" />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">
                  {lang === 'es' ? 'Correos de marketing' : 'Marketing emails'}
                </span>
                <span className="settings-row__subtitle" data-testid="marketing-emails-subtitle">
                  {lang === 'es'
                    ? 'Noticias, novedades del producto y ofertas de Arvexis ocasionales. Puedes desactivarlo cuando quieras.'
                    : 'Occasional Arvexis news, product updates, and offers. You can turn this off at any time.'}
                </span>
              </div>
              <span
                aria-hidden="true"
                style={{
                  flex: '0 0 auto', width: 44, height: 24, borderRadius: 999, padding: 2,
                  display: 'flex', alignItems: 'center',
                  justifyContent: marketingOptIn ? 'flex-end' : 'flex-start',
                  background: marketingOptIn ? 'rgba(232, 197, 143, 0.28)' : 'rgba(255, 255, 255, 0.10)',
                  border: `1px solid ${marketingOptIn ? 'rgba(232, 197, 143, 0.55)' : 'rgba(244, 239, 231, 0.14)'}`,
                  transition: 'background-color 180ms ease, border-color 180ms ease',
                }}
              >
                <span style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: marketingOptIn ? '#E8C58F' : 'rgba(244, 239, 231, 0.55)',
                }} />
              </span>
            </button>

            {/* One door to the member's own data: read it, keep it as a PDF, or take the
                structured file. The separate "My record, to read" row is gone — a member does
                not have two kinds of personal data. */}

            {/* Arvexis's voice — one selection, consumed by Talk, Message and Dreams alike.
                Not a per-surface voice control: there is only one Arvexis. */}
            <button
              className="settings-row"
              onClick={() => setShowVoiceAudition(true)}
              data-testid="arvexis-voice-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon">
                <Volume2 />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">
                  {lang === 'es' ? 'La voz de Arvexis' : "Arvexis's Voice"}
                </span>
                {/* 6D: the member is told which voice Arvexis is using, and whether that is
                    their own choice or the one Arvexis fell back to. Read from
                    GET /api/voice/candidates (selected / in_use / awaiting_selection) — this
                    surface states the server's resolution, it never names a voice itself. */}
                <span className="settings-row__subtitle" data-testid="arvexis-voice-subtitle">
                  {voiceStatus?.awaiting_selection
                    ? (lang === 'es'
                        ? `Arvexis habla con ${voiceStatus.inUseLabel} hasta que elijas. Toca para escuchar y elegir.`
                        : `Arvexis is speaking with ${voiceStatus.inUseLabel} until you choose. Tap to listen and choose.`)
                    : voiceStatus?.inUseLabel
                      ? (lang === 'es'
                          ? `Ahora usa ${voiceStatus.inUseLabel}. Toca para escuchar y cambiarla.`
                          : `Using ${voiceStatus.inUseLabel}. Tap to listen and change it.`)
                      : (lang === 'es'
                          ? 'Escucha las voces y elige una para Talk, Message y Dreams'
                          : 'Listen to the voices and choose one for Talk, Message and Dreams')}
                </span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </button>

            {/* Export My Data — registry-backed member export.
                Per-dream export (Dream → Export PDF / Share) remains the keepsake path. */}
            <button
              className="settings-row settings-row--purple"
              onClick={() => setShowExportChoices(true)}
              data-testid="export-my-data-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon">
                <Download />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">
                  {lang === 'es' ? 'Exportar mis datos' : 'Export My Data'}
                </span>
                <span className="settings-row__subtitle">
                  {lang === 'es'
                    ? 'Lee tu registro, guárdalo en PDF o descarga el archivo'
                    : 'Read your record, save it as a PDF, or download the file'}
                </span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </button>
          </motion.div>

          {/* Share Arvexis — one row, one tap, no flow in between. */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.17 }}
            className="glass-card-surface"
            style={{ padding: 0, overflow: 'hidden', marginTop: '20px' }}
          >
            <button
              className="settings-row settings-row--teal"
              onClick={handleShareArvexis}
              data-testid="share-arvexis-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon">
                <Share2 />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">
                  {lang === 'es' ? 'Comparte Arvexis' : 'Share Arvexis'}
                </span>
                <span className="settings-row__subtitle">
                  {lang === 'es'
                    ? 'Envía una invitación a alguien que la apreciaría'
                    : 'Send an invitation to someone who would appreciate it'}
                </span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </button>
          </motion.div>

          {/* Legal Section */}
          <div className="section-label" style={{ marginTop: '20px' }}>
            {lang === 'es' ? 'LEGAL' : 'LEGAL'}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="glass-card-surface"
            style={{ padding: 0, overflow: 'hidden' }}
          >
            <a href="/terms" className="settings-row settings-row--blue" data-testid="terms-link">
              <div className="settings-row__icon">
                <FileText />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{t.terms}</span>
              </div>
              <ChevronRight className="settings-row__chevron" style={{ width: '14px', height: '14px' }} />
            </a>
            
            <a href="/privacy" className="settings-row settings-row--pink" data-testid="privacy-link">
              <div className="settings-row__icon">
                <Shield />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{t.privacyPolicy}</span>
              </div>
              <ChevronRight className="settings-row__chevron" style={{ width: '14px', height: '14px' }} />
            </a>
            
            <a href="/cookies" className="settings-row settings-row--purple" data-testid="cookies-link">
              <div className="settings-row__icon">
                <FileText />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{t.cookiePolicy}</span>
              </div>
              <ChevronRight className="settings-row__chevron" style={{ width: '14px', height: '14px' }} />
            </a>

            <a href="/copyright" className="settings-row settings-row--blue" data-testid="copyright-link">
              <div className="settings-row__icon">
                <FileText />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{t.copyrightPolicy}</span>
              </div>
              <ChevronRight className="settings-row__chevron" style={{ width: '14px', height: '14px' }} />
            </a>
          </motion.div>

          {/* Support Section */}
          <div className="section-label" style={{ marginTop: '20px' }}>
            {lang === 'es' ? 'SOPORTE' : 'SUPPORT'}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card-surface"
            style={{ padding: 0, overflow: 'hidden' }}
          >
            {/* Help Center */}
            <button 
              className="settings-row"
              onClick={() => navigate('/help-center')}
              data-testid="help-center-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon">
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{lang === 'es' ? 'Centro de Ayuda' : 'Help Center'}</span>
                <span className="settings-row__subtitle">{lang === 'es' ? 'Preguntas frecuentes y guías' : 'FAQs & guides'}</span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </button>
            
            <FeedbackSettingsRow />

            {/* Contact Support */}
            <button 
              className="settings-row settings-row--teal"
              onClick={handleHelp}
              data-testid="contact-support-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon">
                <HelpCircle />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{lang === 'es' ? 'Contactar Soporte' : 'Contact Support'}</span>
                <span className="settings-row__subtitle">support@arvexisapp.com</span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </button>
            
            {/* General Inquiries */}
            <button 
              className="settings-row"
              onClick={handleContactSupport}
              data-testid="general-inquiries-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon">
                <Mail className="w-5 h-5" />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{lang === 'es' ? 'Consultas Generales' : 'General Inquiries'}</span>
                <span className="settings-row__subtitle">hello@arvexisapp.com</span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </button>
            
            {/* About Arvexis - Navigate to full page */}
            <button 
              className="settings-row"
              onClick={() => navigate('/about-arvexis')}
              data-testid="about-arvexis-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon">
                <Info className="w-5 h-5" />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label">{lang === 'es' ? 'Acerca de Arvexis' : 'About Arvexis'}</span>
                <span className="settings-row__subtitle">Version 1.0</span>
              </div>
              <ChevronRight className="settings-row__chevron" />
            </button>
          </motion.div>

          {/* Danger Zone Section */}
          <div className="section-label" style={{ marginTop: '20px', color: 'rgba(239, 68, 68, 0.7)' }}>
            {lang === 'es' ? 'ZONA DE PELIGRO' : 'DANGER ZONE'}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            className="glass-card-surface"
            style={{ padding: 0, overflow: 'hidden' }}
          >
            {/* Delete Account */}
            <button 
              className="settings-row"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="delete-account-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <div className="settings-row__icon" style={{ color: 'rgba(239, 68, 68, 0.8)' }}>
                <Trash2 />
              </div>
              <div className="settings-row__content">
                <span className="settings-row__label" style={{ color: 'rgba(239, 68, 68, 0.9)' }}>
                  {lang === 'es' ? 'Eliminar Cuenta' : 'Delete Account'}
                </span>
                <span className="settings-row__subtitle">{lang === 'es' ? 'Eliminar permanentemente todos los datos' : 'Permanently remove all your data'}</span>
              </div>
              <ChevronRight className="settings-row__chevron" style={{ color: 'rgba(239, 68, 68, 0.5)' }} />
            </button>
          </motion.div>

          {/* Sign Out */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.24 }}
            style={{ marginTop: '32px' }}
          >
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="settings-row settings-row--red glass-card-surface"
              style={{ width: '100%', justifyContent: 'center', borderRadius: 'var(--radius-card)' }}
              data-testid="logout-btn"
            >
              <div className="settings-row__icon">
                <LogOut />
              </div>
              <span style={{ color: 'var(--red)', fontWeight: 500 }}>{t.signOut}</span>
            </button>
          </motion.div>

          {/* INTERNAL — discoverability only. This section exists solely because the server's
              ordinary authentication response confirmed active Console access for this identity.
              Showing or hiding it decides nothing: every Console endpoint re-reads the access
              store and refuses anyone it does not recognise. */}
          {user?.console_access && (
            <>
              <div className="section-label" style={{ marginTop: '32px' }}>
                INTERNAL
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.26 }}
                className="glass-card-surface"
                style={{ padding: 0, overflow: 'hidden' }}
              >
                <button
                  className="settings-row"
                  onClick={() => navigate('/founder-console')}
                  data-testid="founder-console-entry"
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <div className="settings-row__icon">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <div className="settings-row__content">
                    <span className="settings-row__label">Founder Console</span>
                    <span className="settings-row__subtitle">Product intelligence and operational health</span>
                  </div>
                  <ChevronRight className="settings-row__chevron" />
                </button>
              </motion.div>
            </>
          )}

          {/* Version */}
          <p style={{ 
            fontSize: '12px', 
            color: 'rgba(255, 255, 255, 0.35)', 
            textAlign: 'center',
            marginTop: '24px'
          }} data-testid="version-label">
            Arvexis Version 1.0 • https://arvexisapp.com
          </p>
        </main>

        {/* Sign Out — the one approved confirmation. Current device only. */}
        <AnimatePresence>
          {showLogoutConfirm && (
            <SignOutConfirm
              lang={lang}
              prefix="settings"
              onCancel={() => setShowLogoutConfirm(false)}
              onConfirm={handleLogout}
            />
          )}
        </AnimatePresence>

        {/* Create Journal Modal - Sprint 002.2 Simplified */}
        <AnimatePresence>
          {showExportModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ backgroundColor: 'rgba(10, 10, 15, 0.92)' }}
              onClick={() => !exportLoading && setShowExportModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="glass-card-dark"
                style={{ padding: '28px 24px', maxWidth: '340px', width: '100%' }}
                onClick={(e) => e.stopPropagation()}
                data-testid="export-modal"
              >
                <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px', textAlign: 'center' }}>
                  {t.yourJournal}
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginBottom: '24px', textAlign: 'center' }}>
                  {t.createKeepsakeSubtitle}
                </p>
                
                {exportError && (
                  <div style={{ 
                    padding: '12px', 
                    background: 'rgba(224, 80, 48, 0.15)', 
                    border: '1px solid rgba(224, 80, 48, 0.3)',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <p style={{ fontSize: '13px', color: '#E05030' }}>{exportError}</p>
                  </div>
                )}
                
                {/* Create Reflection Journal PDF - Primary Export Option */}
                <button
                  onClick={() => handleExportData('pdf')}
                  disabled={exportLoading}
                  className="btn-gold"
                  style={{ 
                    width: '100%', 
                    height: '52px',
                    marginBottom: '12px',
                    opacity: exportLoading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  data-testid="export-pdf-btn"
                >
                  <FileText size={18} style={{ color: '#0F172A' }} />
                  <span className="btn-gold__text" style={{ fontSize: '15px' }}>
                    {lang === 'es' ? 'Crear Diario de Sueños' : 'Create Dream Journal'}
                  </span>
                </button>
                
                {/* Share Button */}
                <button
                  onClick={handleShare}
                  disabled={exportLoading}
                  className="btn-glass"
                  style={{ 
                    width: '100%', 
                    height: '48px',
                    marginBottom: '12px',
                    opacity: exportLoading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  data-testid="share-btn"
                >
                  <ExternalLink size={18} />
                  <span>{t.shareDream}</span>
                </button>
                
                {/* Export JSON - Data Export Option */}
                <button
                  onClick={() => handleExportData('json')}
                  disabled={exportLoading}
                  className="btn-glass"
                  style={{ 
                    width: '100%', 
                    height: '48px',
                    opacity: exportLoading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  data-testid="export-json-btn"
                >
                  <Download size={18} />
                  <span>
                    {lang === 'es' ? 'Exportar Sueños (JSON)' : 'Export Dreams (JSON)'}
                  </span>
                </button>
                
                <button
                  onClick={() => setShowExportModal(false)}
                  disabled={exportLoading}
                  className="btn-glass"
                  style={{ width: '100%', height: '44px', marginTop: '16px' }}
                  data-testid="export-cancel-btn"
                >
                  {t.cancel}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* One canonical Arvexis voice — audition and selection */}
        <AnimatePresence>
          {showVoiceAudition && (
            <ArvexisVoiceAudition
              lang={lang}
              onClose={() => setShowVoiceAudition(false)}
            />
          )}
        </AnimatePresence>

        {/* My data — one entry point: read the record, keep it as a PDF, or take the file */}
        <AnimatePresence>
          {showExportChoices && (
            <ExportMyDataModal
              api={API}
              lang={lang}
              onClose={() => setShowExportChoices(false)}
              isDownloadingJson={isExportingData}
              onDownloadJson={exportMemberData}
            />
          )}
        </AnimatePresence>

        {/* Data Storage Modal */}
        <AnimatePresence>
          {showDataStorageModal && (            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ backgroundColor: 'rgba(10, 10, 15, 0.92)' }}
              onClick={() => setShowDataStorageModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="utility-modal"
                style={{ padding: '28px 24px', maxWidth: '380px', width: '100%' }}
                onClick={(e) => e.stopPropagation()}
                data-testid="data-storage-modal"
              >
                <div className="utility-modal__medallion">
                  <Shield size={26} strokeWidth={1.4} />
                </div>

                <h3 className="utility-modal__title">{t.dataStorageTitle}</h3>

                <p className="utility-modal__sub">{t.dataStorageDescription}</p>
                
                <div style={{ 
                  background: 'rgba(255,255,255,0.04)', 
                  borderRadius: '12px', 
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Check size={16} style={{ color: '#E8C58F' }} />
                      <span style={{ fontSize: '13.5px', color: 'rgba(244,239,231,0.82)' }}>{t.dataStorageExportable}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Check size={16} style={{ color: '#E8C58F' }} />
                      <span style={{ fontSize: '13.5px', color: 'rgba(244,239,231,0.82)' }}>{t.dataStorageDeletable}</span>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setShowDataStorageModal(false)}
                  className="btn-glass"
                  style={{ width: '100%', height: '44px' }}
                  data-testid="data-storage-close"
                >
                  {lang === 'es' ? 'Entendido' : 'Got it'}
                </button>
              </motion.div>
            </motion.div>
          )}

          {/* Phase 6 — Signed-in devices on the approved account surface. Same entry point,
              same endpoints, same actions, same session contract. */}
          {showDevicesModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              // the foreground is handed back at once: fading out cross-faded the still-mounted page beneath through this surface
              className="utility-overlay z-[150] utility-surface utility-surface--atmospheric"
              data-testid="signed-in-devices-modal"
            >
              <DreamsShell lang={lang} activePath="/settings">
                <header
                  className="flex items-center gap-4 px-5 account-mobile-header"
                  style={{
                    paddingTop: 'calc(env(safe-area-inset-top, 47px) + 12px)',
                    paddingBottom: '12px'
                  }}
                >
                  <button
                    onClick={() => setShowDevicesModal(false)}
                    className="rounded-full flex items-center justify-center"
                    style={{ width: 44, height: 44, background: 'transparent',
                             border: '1px solid rgba(232, 197, 143, 0.38)' }}
                    aria-label={lang === 'es' ? 'Volver' : 'Back'}
                    data-testid="signed-in-devices-close"
                  >
                    <ChevronLeft className="w-5 h-5" style={{ color: 'rgba(232, 197, 143, 0.9)' }} />
                  </button>
                  <span
                    className="account-wordmark"
                    style={{ fontSize: '12px', letterSpacing: '0.42em',
                             color: 'rgba(244,239,231,0.8)', margin: '0 auto', paddingRight: '44px' }}
                    data-testid="devices-wordmark"
                  >
                    ARVEXIS
                  </span>
                </header>

                <div className="utility-head account-col account-col--wide" data-testid="devices-head">
                  <p className="utility-head__eyebrow">{lang === 'es' ? 'CONFIGURACIÓN' : 'SETTINGS'}</p>
                  <h1 className="utility-head__title">
                    {lang === 'es' ? 'Dispositivos con sesión' : 'Signed-in Devices'}
                  </h1>
                  <p className="utility-head__sub">
                    {lang === 'es'
                      ? 'Dónde tienes sesión abierta ahora. Al finalizar un dispositivo, se cierra su sesión.'
                      : 'Where you are signed in right now. Ending a device signs it out.'}
                  </p>
                </div>

                <main
                  className="account-col account-col--wide"
                  style={{ padding: '6px 20px 48px' }}
                  data-testid="devices-main"
                >
                  <SignedInDevices
                    lang={lang}
                    onCurrentDeviceEnded={() => { setShowDevicesModal(false); navigate('/login'); }}
                  />
                </main>
              </DreamsShell>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Composing Overlay - Keepsake creation experience */}
        <AnimatePresence>
          {showComposingOverlay && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex flex-col items-center justify-center"
              style={{ backgroundColor: 'rgba(10, 8, 16, 0.98)' }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="text-center"
              >
                {/* Animated icon */}
                <motion.div
                  animate={{ 
                    rotate: [0, 360],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{ 
                    rotate: { duration: 8, repeat: Infinity, ease: 'linear' },
                    scale: { duration: 2, repeat: Infinity, ease: 'easeInOut' }
                  }}
                  style={{ marginBottom: '32px' }}
                >
                  <ArvexisSymbol size={48} glow breathing />
                </motion.div>
                
                {/* Composing message - Localized */}
                <motion.p
                  key={composingMessage}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  style={{ 
                    fontSize: '18px', 
                    color: 'rgba(255,255,255,0.8)',
                    fontWeight: 400,
                    letterSpacing: '0.3px'
                  }}
                >
                  {composingMessage || t.creatingKeepsake}
                </motion.p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Toast Message (for success notifications) */}
        <AnimatePresence>
          {toastMessage && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="fixed bottom-24 left-4 right-4 z-50"
            >
              <div 
                className="glass-card-surface mx-auto max-w-sm"
                style={{ padding: '16px 20px', textAlign: 'center' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Check size={18} style={{ color: 'var(--teal)' }} />
                  <p style={{ fontSize: '15px', fontWeight: 500, color: '#FFFFFF' }}>
                    {toastMessage}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Coming Soon Toast - kept for future features */}
        <AnimatePresence>
          {showComingSoon && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="fixed bottom-24 left-4 right-4 z-50"
            >
              <div 
                className="glass-card-surface mx-auto max-w-sm"
                style={{ padding: '16px 20px', textAlign: 'center' }}
              >
                <p style={{ fontSize: '15px', fontWeight: 500, color: '#FFFFFF', marginBottom: '4px' }}>
                  {showComingSoon} {lang === 'es' ? 'próximamente.' : 'is coming soon.'}
                </p>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)' }}>
                  {lang === 'es' ? 'Lo estamos construyendo con cuidado.' : 'We are building this thoughtfully.'}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Account Confirmation Modal (Section D.4 - Two-step) */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ backgroundColor: 'rgba(10, 10, 15, 0.92)' }}
              onClick={() => !deleteLoading && !deletionDeadline && setShowDeleteConfirm(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="glass-card-dark"
                style={{ padding: '24px', maxWidth: '340px', width: '100%' }}
                onClick={(e) => e.stopPropagation()}
                data-testid="delete-account-modal"
              >
                {deletionDeadline ? (
                  <div data-testid="delete-account-closed-confirmation">
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px' }}>
                      {lang === 'es' ? 'Tu cuenta está cerrada' : 'Your account is closed'}
                    </h3>
                    <p
                      style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginBottom: '20px', lineHeight: 1.55 }}
                      data-testid="delete-account-closed-deadline"
                    >
                      {lang === 'es'
                        ? `Has cerrado sesión y ya no se puede acceder a tu cuenta. Tus datos se eliminarán de forma permanente de nuestros sistemas activos a más tardar el ${formatDeletionDeadline(deletionDeadline)}. Las copias de respaldo pueden conservarse durante un período de retención limitado conforme a nuestro proceso de respaldos.`
                        : `You have been signed out and your account can no longer be accessed. Your data will be permanently removed from our active systems no later than ${formatDeletionDeadline(deletionDeadline)}. Backup copies may remain for a limited retention period under our backup retention process.`}
                    </p>
                    <button
                      onClick={async () => {
                        localStorage.clear();
                        await logout();
                        navigate('/');
                      }}
                      className="btn-glass"
                      style={{ width: '100%', height: '44px' }}
                      data-testid="delete-account-closed-done"
                    >
                      {lang === 'es' ? 'Listo' : 'Done'}
                    </button>
                  </div>
                ) : (
                <>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px' }}>
                  {lang === 'es' ? '¿Eliminar tu cuenta?' : 'Delete your account?'}
                </h3>
                <p
                  style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginBottom: '16px', lineHeight: 1.55 }}
                  data-testid="delete-account-deadline-notice"
                >
                  {lang === 'es'
                    ? `Cerrar tu cuenta es permanente y no se puede deshacer. Tu cuenta quedará inaccesible de inmediato y tus datos se eliminarán de forma permanente de nuestros sistemas activos a más tardar el ${formatDeletionDeadline(null)}. Las copias de respaldo pueden conservarse durante un período de retención limitado conforme a nuestro proceso de respaldos.`
                    : `Closing your account is permanent and cannot be undone. Your account will become inaccessible immediately, and your data will be permanently removed from our active systems no later than ${formatDeletionDeadline(null)}. Backup copies may remain for a limited retention period under our backup retention process.`}
                </p>
                
                {deleteError && (
                  <div style={{ 
                    padding: '12px', 
                    background: 'rgba(239, 68, 68, 0.15)', 
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <p style={{ fontSize: '13px', color: '#EF4444' }} data-testid="delete-account-error">{deleteError}</p>
                  </div>
                )}

                {/* Phase 6B (ruling 4a) — closing an account is the most irreversible thing here,
                    so it asks the member to prove it is them, the same way they signed in. */}
                {(deleteReauthMethod === 'apple' || user?.auth_method === 'apple') ? (
                  <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginBottom: '14px' }}>
                    {lang === 'es'
                      ? 'Te pediremos confirmar con Apple antes de cerrar tu cuenta.'
                      : 'You will be asked to confirm with Apple before your account closes.'}
                  </p>
                ) : deletionProofMethod === 'google' ? (
                  /* Phase 6B (STOP-2) — a Google account proves itself through Google. The proof
                     is only taken because the member began this deletion, and it closes nothing
                     on its own: they come back here and confirm. */
                  googleDeletionProof ? (
                    <p
                      style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginBottom: '14px' }}
                      data-testid="delete-account-google-confirmed"
                    >
                      {lang === 'es'
                        ? 'Confirmado con Google. Pulsa abajo para cerrar tu cuenta.'
                        : 'Confirmed with Google. Press below to close your account.'}
                    </p>
                  ) : (
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '10px' }}>
                        {lang === 'es'
                          ? 'Confirma con Google que eres tú. No se elimina nada hasta que vuelvas y lo confirmes.'
                          : 'Confirm with Google that it is you. Nothing is deleted until you come back and confirm.'}
                      </p>
                      <GoogleCredentialButton
                        lang={lang}
                        text="continue_with"
                        testId="delete-account-google-proof"
                        onCredential={(credential) => { setGoogleDeletionProof(credential); setDeleteError(null); }}
                        onError={() => setDeleteError(lang === 'es' ? 'Esa confirmación no se completó. No se ha eliminado nada.' : 'That confirmation did not complete. Nothing was deleted.')}
                        renderNative={(go) => (
                          <button onClick={go} disabled={deleteLoading} className="btn-glass" style={{ width: '100%', height: '44px' }} data-testid="delete-account-google-proof">
                            {lang === 'es' ? 'Confirmar con Google' : 'Confirm with Google'}
                          </button>
                        )}
                      />
                    </div>
                  )
                ) : (
                  <div style={{ marginBottom: '16px' }}>
                    <label
                      htmlFor="delete-account-password"
                      style={{ display: 'block', fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}
                    >
                      {lang === 'es' ? 'Confirma tu contraseña' : 'Confirm your password'}
                    </label>
                    <input
                      id="delete-account-password"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      autoComplete="current-password"
                      style={{
                        width: '100%',
                        minHeight: '44px',
                        padding: '0 14px',
                        borderRadius: '10px',
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.14)',
                        color: '#FFFFFF',
                        fontSize: '14px'
                      }}
                      data-testid="delete-account-password"
                    />
                  </div>
                )}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading}
                    style={{ 
                      width: '100%', 
                      height: '44px',
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '12px',
                      color: '#EF4444',
                      fontWeight: 500,
                      cursor: deleteLoading ? 'not-allowed' : 'pointer',
                      opacity: deleteLoading ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    data-testid="delete-account-confirm"
                  >
                    {deleteLoading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {lang === 'es' ? 'Eliminando...' : 'Deleting...'}
                      </>
                    ) : (
                      lang === 'es' ? 'Sí, eliminar mi cuenta' : 'Yes, delete my account'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteError(null);
                      setGoogleDeletionProof(null);
                    }}
                    disabled={deleteLoading}
                    className="btn-glass"
                    style={{ width: '100%', height: '44px' }}
                    data-testid="delete-account-cancel"
                  >
                    {lang === 'es' ? 'Mantener mi cuenta' : 'Keep my account'}
                  </button>
                </div>
                </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Shared Memories Modal - Phase F */}
        <AnimatePresence>
          {showSharedMemories && (
            <SharedMemories 
              lang={lang} 
              onClose={() => {
                setShowSharedMemories(false);
                setMemoryFocusItemId(null);
              }}
              focusItemId={memoryFocusItemId}
            />
          )}
        </AnimatePresence>

        {/* Usage this period — canonical detailed capacity surface */}
        <AnimatePresence>
          {showUsagePeriod && (
            <UsageThisPeriod lang={lang} onClose={() => setShowUsagePeriod(false)} />
          )}
        </AnimatePresence>

        {/* Past Reflections Modal - Phase F */}
        <AnimatePresence>
          {showPastReflections && (
            <PastReflections 
              lang={lang} 
              onClose={() => setShowPastReflections(false)}
              onResumeSession={(sessionId) => {
                setResumeSessionId(sessionId);
                setShowPastReflections(false);
                setShowCompanionChat(true);
              }}
            />
          )}
        </AnimatePresence>

        {/* Saved Talks Modal — V1: transcripts get a stable home in Settings */}
        <AnimatePresence>
          {showMemorySummary && (
            <MemorySummary lang={lang} onClose={() => setShowMemorySummary(false)} />
          )}
          {showSavedTalks && (
            <ConversationHistory
              lang={lang}
              onClose={() => setShowSavedTalks(false)}
            />
          )}
        </AnimatePresence>

        {/* Companion Chat Modal - Phase F (Resume Session) */}
        <AnimatePresence>
          {showCompanionChat && (
            <div 
              className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100]"
              data-testid="companion-chat-modal"
            >
              <div className="h-full flex flex-col">
                {/* Phase 6: back returns to Message History, the surface they came from. */}
                <button
                  onClick={() => {
                    setShowCompanionChat(false);
                    setResumeSessionId(null);
                    setShowPastReflections(true);
                  }}
                  className="absolute z-20 rounded-full flex items-center justify-center"
                  style={{ top: 'calc(env(safe-area-inset-top, 12px) + 10px)', left: 16,
                           width: 44, height: 44, background: 'rgba(5, 8, 14, 0.6)',
                           border: '1px solid rgba(232, 197, 143, 0.38)' }}
                  aria-label={lang === 'es' ? 'Volver al historial' : 'Back to Message History'}
                  data-testid="companion-chat-close"
                >
                  <ChevronLeft className="w-5 h-5" style={{ color: 'rgba(232, 197, 143, 0.9)' }} />
                </button>
                
                <CompanionChat
                  surface="daily_companion"
                  lang={lang}
                  existingSessionId={resumeSessionId}
                  initialMessage={
                    resumeSessionId 
                      ? (lang === 'es' 
                          ? 'Bienvenida de vuelta. ¿Dónde nos quedamos?' 
                          : 'Welcome back. Where were we?')
                      : null
                  }
                  onClose={() => {
                    setShowCompanionChat(false);
                    setResumeSessionId(null);
                  }}
                />
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </AppShellV2>
  );
};

// ============================================================================
// GROUP 3/4 UTILITY-SHELL PRIMITIVES
// Tokens reused verbatim from the accepted Group 2 authenticated shell
// (Saved Talks / Message History / Shared Memories). No new values introduced.
// ============================================================================
const SHELL_BG = "radial-gradient(120% 80% at 18% 30%, rgba(85,205,225,0.10) 0%, rgba(85,205,225,0) 55%),radial-gradient(90% 70% at 34% 52%, rgba(181,106,216,0.09) 0%, rgba(181,106,216,0) 60%),radial-gradient(70% 50% at 26% 62%, rgba(232,197,143,0.07) 0%, rgba(232,197,143,0) 65%),#030A12";
const SHELL_SURFACE = 'rgba(7, 20, 33, 0.78)';
const SHELL_BORDER = 'rgba(128, 149, 166, 0.28)';
const SHELL_DIVIDER = 'rgba(128, 149, 166, 0.25)';
const TEXT_PRIMARY = '#F4EFE7';
const TEXT_SECONDARY = '#A8B3BF';
const TEXT_TERTIARY = '#8095A6';
const CHAMPAGNE = '#E8C58F';
const FONT_DISPLAY = "'Cormorant Garamond', Georgia, serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";
const FONT_WORDMARK = 'Outfit, Inter, system-ui, sans-serif';

// A back control must never be a bare history step: on a cold or standalone entry there is no
// in-app entry behind it and `navigate(-1)` leaves the app. Return to the originating surface
// when there is one, and fall back to authenticated Home when there is not.
const useShellBack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return () => {
    const idx = window.history.state?.idx;
    const noInAppHistory = location.key === 'default' || idx === undefined || idx <= 0;
    if (noInAppHistory) {
      navigate('/home', { replace: true });
    } else {
      navigate(-1);
    }
  };
};

const ShellHeader = ({ onBack, backTestId, backLabel }) => (
  <>
    <div
      className="flex items-center gap-4 px-6 min-[1200px]:px-8"
      style={{ paddingTop: 'max(20px, calc(env(safe-area-inset-top, 0px) + 8px))', paddingBottom: '14px' }}
    >
      <button
        onClick={onBack}
        className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center border transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
        style={{ borderColor: 'rgba(232, 197, 143, 0.45)', color: TEXT_PRIMARY, background: 'transparent' }}
        aria-label={backLabel}
        data-testid={backTestId}
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={1.6} />
      </button>
      <span
        className="text-[16px] select-none"
        style={{ fontFamily: FONT_WORDMARK, letterSpacing: '0.34em', textIndent: '0.34em', color: TEXT_PRIMARY }}
        data-testid="shell-wordmark"
      >
        ARVEXIS
      </span>
    </div>
    <div className="px-6 min-[1200px]:px-8">
      <div style={{ height: '1px', background: SHELL_DIVIDER }} />
    </div>
  </>
);

const ShellEyebrow = ({ children, tone = TEXT_SECONDARY, testId }) => (
  <div
    className="mb-3 text-[11px] font-semibold uppercase"
    style={{ fontFamily: FONT_BODY, letterSpacing: '0.22em', color: tone }}
    data-testid={testId}
  >
    {children}
  </div>
);

const ShellCard = ({ children, className = '', testId }) => (
  <div
    className={`rounded-[18px] overflow-hidden ${className}`}
    style={{ background: SHELL_SURFACE, border: `1px solid ${SHELL_BORDER}` }}
    data-testid={testId}
  >
    {children}
  </div>
);

const UtilityRow = ({
  as: Tag = 'button',
  icon: Icon,
  iconTone = TEXT_SECONDARY,
  label,
  sub,
  badge,
  divider = false,
  ...rest
}) => (
  <Tag
    className="w-full flex items-center gap-3.5 px-4 min-h-[62px] text-left transition-colors hover:bg-white/[0.03] outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-inset"
    style={{ borderBottom: divider ? `1px solid ${SHELL_DIVIDER}` : 'none', textDecoration: 'none' }}
    {...rest}
  >
    <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} style={{ color: iconTone }} />
    <span className="flex-1 min-w-0 flex flex-col gap-[2px] py-3" style={{ fontFamily: FONT_BODY }}>
      <span className="text-[15px] min-[1200px]:text-[16px]" style={{ color: TEXT_PRIMARY }}>{label}</span>
      {sub && <span className="text-[13px]" style={{ color: TEXT_SECONDARY }}>{sub}</span>}
    </span>
    {badge && (
      <span
        className="shrink-0 text-[10px] font-semibold uppercase"
        style={{ fontFamily: FONT_BODY, letterSpacing: '0.18em', color: TEXT_SECONDARY }}
      >
        {badge}
      </span>
    )}
    <ChevronRight className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} style={{ color: TEXT_TERTIARY }} />
  </Tag>
);
// ============================================================================
// About Arvexis Screen - Priority 2: Why Arvexis exists
// Per Founder Direction: "Rather than describing features, explain why Arvexis exists."
// ============================================================================
// ABOUT SCREEN — Minimal Settings Surface (V1 Consolidation)
// 
// FOUNDER DIRECTION (December 2025):
// "The Settings 'About Arvexis' surface should not maintain an independent 
//  legacy description of Arvexis. Make /about the single canonical full 
//  About narrative. Keep this screen intentionally minimal."
// 
// This screen provides:
// - Logo and tagline
// - Version info
// - Link to canonical /about page
// - Legal links (Privacy, Terms)
// - Copyright
// 
// The Learn more row is retired: the approved Visit Arvexis Website external destination
// supersedes it. Authenticated members are never navigated into public marketing chrome.
// ============================================================================
// About Arvexis — member-facing narrative. Copy is Founder-authorized and rendered verbatim from
// content/aboutArvexisContent.js. Quiet by intent: the member is already talking to Arvexis.
const AboutParagraph = ({ children, dim }) => (
  <p
    className="mt-5 text-[16px] leading-[27px] min-[1200px]:text-[17px] min-[1200px]:leading-[29px]"
    style={{ fontFamily: FONT_BODY, color: dim ? TEXT_TERTIARY : TEXT_SECONDARY }}
  >
    {children}
  </p>
);

const AboutScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const goBack = useShellBack();
  const about = memberAbout(lang);

  return (
    <AppShellV2 currentPath="/about-arvexis" variant="settings" showNav={false}>
      <div className="min-h-screen" style={{ background: SHELL_BG }} data-testid="about-arvexis-screen">
        <ShellHeader
          onBack={goBack}
          backTestId="about-back-btn"
          backLabel={lang === 'es' ? 'Atrás' : 'Back'}
        />

        <main className="px-6 min-[1200px]:px-8" style={{ paddingTop: '28px', paddingBottom: '56px' }}>
          <ShellEyebrow tone={CHAMPAGNE} testId="about-eyebrow">
            {lang === 'es' ? 'ACERCA DE' : 'ABOUT'}
          </ShellEyebrow>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <h1
              className="text-[34px] leading-[40px] min-[1200px]:text-[46px] min-[1200px]:leading-[54px]"
              style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, color: TEXT_PRIMARY }}
              data-testid="about-title"
            >
              {lang === 'es' ? 'Acerca de Arvexis' : 'About Arvexis'}
            </h1>
          </motion.div>

          {/* Member-facing narrative — Founder-authorized copy, verbatim. Quiet, second-person,
              not the public manifesto. Locked V1 remains the public editorial source. */}
          <div data-testid="about-editorial" className="max-w-[40rem]">
            <div className="mt-8" data-testid="about-member-narrative">
              {about.body.map((p, i) => <AboutParagraph key={i}>{p}</AboutParagraph>)}
            </div>

            <div
              className="mt-8 pl-5"
              style={{ borderLeft: `1px solid ${SHELL_DIVIDER}` }}
              data-testid="about-member-control"
            >
              {about.control.map((p, i) => (
                <p
                  key={i}
                  className={`text-[16px] leading-[27px] ${i === 0 ? '' : 'mt-3'}`}
                  style={{
                    fontFamily: FONT_BODY,
                    color: i === 0 ? TEXT_PRIMARY : TEXT_SECONDARY,
                  }}
                >
                  {p}
                </p>
              ))}
            </div>

            {about.rest.map((p, i) => <AboutParagraph key={i}>{p}</AboutParagraph>)}

            <p
              className="mt-10 text-[19px] leading-[28px] min-[1200px]:text-[21px] min-[1200px]:leading-[31px]"
              style={{ fontFamily: FONT_DISPLAY, color: TEXT_PRIMARY }}
              data-testid="about-member-pull"
            >
              {about.pull}
            </p>

            <div
              className="mt-10 pt-8"
              style={{ borderTop: `1px solid ${SHELL_DIVIDER}` }}
              data-testid="about-closing"
            >
              {about.closing.map((line, i) => (
                <p
                  key={i}
                  className={`text-[16px] leading-[26px] ${i === 0 ? '' : 'mt-2'}`}
                  style={{
                    fontFamily: FONT_BODY,
                    color: i === about.closing.length - 1 ? CHAMPAGNE : TEXT_SECONDARY,
                  }}
                >
                  {line}
                </p>
              ))}
            </div>
          </div>

          {/* Version — hairline rules, no card */}
          <div
            className="mt-9"
            style={{ borderTop: `1px solid ${SHELL_DIVIDER}`, borderBottom: `1px solid ${SHELL_DIVIDER}` }}
          >
            <div className="flex items-center justify-between py-4" style={{ fontFamily: FONT_BODY }}>
              <span className="text-[15px]" style={{ color: TEXT_SECONDARY }} data-testid="about-version-label">
                {lang === 'es' ? 'Versión' : 'Version'}
              </span>
              <span className="text-[15px]" style={{ color: TEXT_PRIMARY }} data-testid="about-version-value">1.0</span>
            </div>
          </div>

          {/* Legal — Terms of Service → Privacy Policy → Cookie Policy */}
          <div className="mt-8">
            <ShellEyebrow testId="about-legal-eyebrow">{t.legal}</ShellEyebrow>
            <ShellCard testId="about-legal-card">
              <UtilityRow as="a" href="/terms" icon={FileText} label={t.terms} divider data-testid="about-terms-link" />
              <UtilityRow as="a" href="/privacy" icon={Shield} label={t.privacyPolicy} divider data-testid="about-privacy-link" />
              <UtilityRow as="a" href="/cookies" icon={Cookie} label={t.cookiePolicy} divider data-testid="about-cookie-link" />
              <UtilityRow as="a" href="/copyright" icon={FileText} label={t.copyrightPolicy} data-testid="about-copyright-link" />
            </ShellCard>
          </div>

          {/* External — restrained utility border, champagne icon is the only gold accent */}
          <div className="mt-8">
            <ShellEyebrow testId="about-external-eyebrow">{lang === 'es' ? 'EXTERNO' : 'EXTERNAL'}</ShellEyebrow>
            <ShellCard testId="about-external-card">
              <UtilityRow
                as="a"
                href="https://arvexisapp.com"
                target="_blank"
                rel="noopener noreferrer"
                icon={ExternalLink}
                iconTone={CHAMPAGNE}
                label={lang === 'es' ? 'Visitar Sitio Web' : 'Visit Arvexis Website'}
                sub="arvexisapp.com"
                data-testid="about-website-link"
              />
            </ShellCard>
          </div>

          <p
            className="mt-10 text-center text-[13px]"
            style={{ fontFamily: FONT_BODY, color: TEXT_TERTIARY }}
            data-testid="about-copyright"
          >
            © 2026 Arvexis
          </p>
        </main>
      </div>
    </AppShellV2>
  );
};

// Help Center Screen
const HelpCenterScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const goBack = useShellBack();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      question: lang === 'es' ? '¿Cómo registro un sueño?' : 'How do I record a dream?',
      answer: lang === 'es' 
        ? 'Toca el botón del micrófono en la pantalla principal y habla sobre tu sueño. Arvexis escuchará, transcribirá y ofrecerá reflexiones gentiles sobre lo que compartiste.'
        : 'Tap the microphone button on the home screen and speak about your dream. Arvexis will listen, transcribe, and offer gentle reflections on what you shared.'
    },
    {
      question: lang === 'es' ? '¿Puedo escribir en lugar de hablar?' : 'Can I type instead of speaking?',
      answer: lang === 'es'
        ? 'Por supuesto. Después de tocar el botón del micrófono, verás una opción para escribir tu sueño en su lugar. Algunas personas prefieren la intimidad de las palabras escritas.'
        : 'Of course. After tapping the microphone button, you\'ll see an option to type your dream instead. Some people prefer the intimacy of written words.'
    },
    {
      question: lang === 'es' ? '¿Cómo se generan las interpretaciones?' : 'How are interpretations generated?',
      answer: lang === 'es'
        ? 'Arvexis combina psicología jungiana, análisis de símbolos universales e inteligencia artificial avanzada. Son perspectivas generadas por IA, no determinaciones médicas, psicológicas ni de hechos. Tú eres la autoridad sobre tus sueños.'
        : 'Arvexis combines Jungian psychology, universal symbol analysis, and advanced AI. They are AI-generated perspectives, not medical, psychological or factual determinations. You are the authority on your Dreams.'
    },
    {
      question: lang === 'es' ? '¿Arvexis me recuerda?' : 'Does Arvexis remember me?',
      answer: lang === 'es'
        ? 'Sí. Arvexis recuerda lo que decides compartir en Sueños, Mensajes y Talk. Puedes verlo en Recuerdos compartidos, corregirlo o pedirle que lo olvide. Recordar no es aprender: no aprende de tus conversaciones en tiempo real y no se usan para entrenar modelos de IA.'
        : "Yes. Arvexis remembers what you choose to share across Dreams, Message and Talk. You can see it in Shared Memories, correct it, or ask her to forget it. Remembering is not learning: she doesn't learn from your conversations in real time, and they aren't used to train AI models."
    },
    {
      question: lang === 'es' ? '¿Puedo eliminar mis sueños?' : 'Can I delete my dreams?',
      answer: lang === 'es'
        ? 'Sí. Puedes eliminar cualquier sueño individual abriéndolo y tocando el icono de papelera. Para eliminar todo, ve a Configuración > Zona de Peligro > Eliminar Cuenta. La eliminación de la cuenta se completa tras 30 días. Las copias de seguridad hechas antes se conservan según el calendario automático de nuestro proveedor de alojamiento y caducan según ese calendario; estamos confirmando ese plazo con el proveedor.'
        : 'Yes. You can delete any individual dream by opening it and tapping the trash icon. To delete everything, go to Settings > Danger Zone > Delete Account. Account deletion completes after 30 days. Backup copies made before then are kept on our hosting provider’s automatic schedule and expire on that schedule; we are confirming that period with the provider.'
    },
    {
      question: lang === 'es' ? '¿Puedo exportar mis datos?' : 'Can I export my data?',
      answer: lang === 'es'
        ? 'Sí. Abre cualquier sueño y toca Guardar o Compartir para crear una Tarjeta del Sueño: una imagen de tu reflexión que puedes guardar en tu dispositivo o compartir. Para todo tu registro, ve a Configuración > Mis datos, donde puedes leerlo, guardarlo en PDF o descargar el archivo.'
        : 'Yes. Open any dream and tap Save or Share to create a Dream Card — an image of your reflection you can keep on your device or share. For your whole record, go to Settings > My data, where you can read it, save it as a PDF, or download the file.'
    },
    {
      question: lang === 'es' ? '¿Mi información es privada?' : 'Is my information private?',
      answer: lang === 'es'
        ? 'Sí. Tus sueños y conversaciones son privados por defecto, nunca se venden ni se usan para publicidad. Puedes ver, corregir, olvidar, exportar o eliminar lo que Arvexis guarda. La información eliminada desaparece de nuestras copias de seguridad a medida que caducan según el calendario automático de nuestro proveedor de alojamiento; estamos confirmando ese plazo con el proveedor.'
        : 'Yes. Your Dreams and conversations are private by default, never sold and never used for advertising. You can see, correct, forget, export or delete what Arvexis keeps. Deleted information leaves our backups as they expire on our hosting provider’s automatic schedule; we are confirming that period with the provider.'
    },
    {
      question: lang === 'es' ? '¿Qué membresías hay?' : 'What memberships are there?',
      answer: lang === 'es'
        ? 'Las cuentas nuevas empiezan con Meet ARVEXIS: 7 días gratis, una sola vez, sin tarjeta, y nunca se convierte en un pago. Incluye una experiencia de Pasar a la siguiente sala. Después puedes elegir Essential ($14.99/mes) o Premium ($29.99/mes). Premium tiene más espacio que Essential y es la membresía con Pasar a la siguiente sala. Las búsquedas de información forman parte de tus Mensajes y de Talk, sin un contador aparte. Las cantidades exactas están en Comparar todas las funciones, en la pantalla de Suscripción, y lo que te queda está en Uso. Es la misma Arvexis, con la misma memoria, privacidad, Olvidar y apoyo en crisis en cualquier membresía.'
        : "New accounts start with Meet ARVEXIS: 7 days free, once, with no card, and it never turns into a charge. It includes one Step Into The Next Room experience. After that you can choose Essential ($14.99/month) or Premium ($29.99/month). Premium has more room than Essential and is the membership with Step Into The Next Room. Looking things up is part of your Messages and Talk, with no separate counter. The exact allowances are under Compare all features on the Subscription screen, and what you have left is under Usage. She is the same Arvexis, with the same memory, privacy, Forget and crisis support, on every membership."
    },
    {
      question: lang === 'es' ? '¿Qué son los complementos?' : 'What are add-ons?',
      answer: lang === 'es'
        ? 'Si eres miembro de Essential o Premium y se te acaba una cantidad mensual, puedes esperar a que se renueve, comprar un complemento (por ejemplo 100 Mensajes por $5.99) o pasar a Premium. Un complemento es una compra única: se usa solo después de tu cantidad mensual y nunca caduca. Si tu membresía termina, los complementos no usados se conservan para cuando vuelvas a tener Essential o Premium.'
        : "If you're an Essential or Premium member and a monthly allowance runs out, you can wait for it to refresh, buy an add-on (for example 100 Messages for $5.99), or move to Premium. An add-on is a one-time purchase: it's used only after your monthly allowance and never expires. If your membership ends, unused add-ons are kept for when you have Essential or Premium again."
    },
    {
      question: lang === 'es' ? '¿Cómo cancelo mi membresía?' : 'How do I cancel my membership?',
      answer: lang === 'es'
        ? 'En la web: Configuración > Suscripción > Gestionar suscripción. Tu membresía continúa hasta el final del periodo pagado y no se renueva después. En iPhone: Ajustes de tu ID de Apple > Suscripciones. Todo lo tuyo se conserva.'
        : 'On the web: Settings > Subscription > Manage subscription. Your membership continues to the end of the paid period and is not renewed after that. On iPhone: your Apple ID settings > Subscriptions. Everything of yours is kept.'
    },
    {
      question: lang === 'es' ? '¿Qué es Talk?' : 'What is Talk?',
      answer: lang === 'es'
        ? 'Talk es una conversación de voz en directo con Arvexis. Tu voz va directamente a nuestro proveedor de IA de voz y Arvexis no guarda el audio. Después de un Talk eliges si guardar la transcripción.'
        : "Talk is a live voice conversation with Arvexis. Your voice goes directly to our voice AI provider, and Arvexis doesn't keep the audio. After a Talk you choose whether to save the transcript."
    },
    {
      question: lang === 'es' ? '¿Puedo hacer que Arvexis olvide algo?' : 'Can I make Arvexis forget something?',
      answer: lang === 'es'
        ? 'Sí. Pídeselo («por favor, olvida…») o usa Recuerdos compartidos. Elimina ese detalle, y lo que lo revelaría, de tu historial.'
        : 'Yes. Ask her ("please forget…") or use Shared Memories. She removes the detail, and anything that would reveal it, from your history.'
    },
    {
      question: lang === 'es' ? '¿Arvexis busca cosas en internet?' : 'Does Arvexis look things up online?',
      answer: lang === 'es'
        ? 'Solo para información actual. Envía una versión abreviada de tu pregunta, sin tus datos personales, y para «cerca de mí» solo tu ciudad, con tu permiso.'
        : 'Only for current information. She sends a shortened question without your personal details, and for "near me" only your city, with your permission.'
    },
  ];

  const handleContactSupport = () => {
    const subject = encodeURIComponent('Arvexis Support Request');
    const body = encodeURIComponent(
      'Hello Arvexis Support,\n\n' +
      'I need help with:\n\n' +
      '\n\n' +
      'Thank you,\n' +
      '[Your name]'
    );
    window.open(`mailto:support@arvexisapp.com?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <AppShellV2 currentPath="/help-center" variant="settings" showNav={false}>
      <div className="min-h-screen" style={{ background: SHELL_BG }} data-testid="help-center-screen">
        <ShellHeader
          onBack={goBack}
          backTestId="help-back-btn"
          backLabel={lang === 'es' ? 'Atrás' : 'Back'}
        />

        <main className="px-6 min-[1200px]:px-8" style={{ paddingTop: '28px', paddingBottom: '56px' }}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <h1
              className="text-[34px] leading-[40px] min-[1200px]:text-[46px] min-[1200px]:leading-[54px]"
              style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, color: TEXT_PRIMARY }}
              data-testid="help-title"
            >
              {lang === 'es' ? 'Centro de Ayuda' : 'Help Center'}
            </h1>
            <p
              className="mt-3 text-[15px] leading-[22px] min-[1200px]:text-[17px]"
              style={{ fontFamily: FONT_BODY, color: TEXT_SECONDARY }}
              data-testid="help-subtitle"
            >
              {lang === 'es'
                ? 'Estamos aquí para ayudarte. Encuentra respuestas a las preguntas más comunes o contáctanos directamente.'
                : 'We\'re here to help. Find answers to common questions below, or reach out to us directly.'}
            </p>
          </motion.div>

          {/* Direct Contact — heading, labels and mailto behaviour unchanged */}
          <div className="mt-8">
            <ShellCard className="px-4 pt-4 pb-4">
              <ShellEyebrow testId="help-contact-heading">
                {lang === 'es' ? 'Contacto Directo' : 'Direct Contact'}
              </ShellEyebrow>
              <div className="flex gap-3">
                <button
                  onClick={handleContactSupport}
                  className="flex-1 h-12 rounded-[18px] flex items-center justify-center gap-2 border transition-colors hover:bg-white/[0.03] outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
                  style={{ borderColor: SHELL_BORDER, fontFamily: FONT_BODY, color: TEXT_PRIMARY }}
                  data-testid="help-contact-support"
                >
                  <Headphones className="w-[18px] h-[18px]" strokeWidth={1.6} style={{ color: CHAMPAGNE }} />
                  <span className="text-[15px]">{lang === 'es' ? 'Soporte' : 'Support'}</span>
                </button>
                <a
                  href="mailto:hello@arvexisapp.com"
                  className="flex-1 h-12 rounded-[18px] flex items-center justify-center gap-2 border transition-colors hover:bg-white/[0.03] outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
                  style={{ borderColor: SHELL_BORDER, fontFamily: FONT_BODY, color: TEXT_PRIMARY, textDecoration: 'none' }}
                  data-testid="help-general-inquiry"
                >
                  <Mail className="w-[18px] h-[18px]" strokeWidth={1.6} style={{ color: CHAMPAGNE }} />
                  <span className="text-[15px]">{lang === 'es' ? 'Consultas' : 'Inquiries'}</span>
                </a>
              </div>
            </ShellCard>
          </div>

          {/* FAQs — wording and order unchanged */}
          <div className="mt-8">
            <ShellEyebrow testId="help-faq-eyebrow">
              {lang === 'es' ? 'PREGUNTAS FRECUENTES' : 'FREQUENTLY ASKED QUESTIONS'}
            </ShellEyebrow>
            <ShellCard>
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  style={{ borderBottom: index < faqs.length - 1 ? `1px solid ${SHELL_DIVIDER}` : 'none' }}
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full flex items-center gap-3.5 px-4 min-h-[62px] text-left transition-colors hover:bg-white/[0.03] outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-inset"
                    data-testid={`faq-${index}`}
                  >
                    <HelpCircle className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} style={{ color: TEXT_SECONDARY }} />
                    <span
                      className="flex-1 min-w-0 py-3 text-[15px] min-[1200px]:text-[16px]"
                      style={{ fontFamily: FONT_BODY, color: TEXT_PRIMARY }}
                    >
                      {faq.question}
                    </span>
                    <ChevronDown
                      className="w-[18px] h-[18px] shrink-0 transition-transform duration-200"
                      strokeWidth={1.6}
                      style={{
                        color: TEXT_TERTIARY,
                        transform: expandedFaq === index ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}
                    />
                  </button>
                  <AnimatePresence>
                    {expandedFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <p
                          className="text-[14px] leading-[22px]"
                          style={{ padding: '0 16px 16px 50px', fontFamily: FONT_BODY, color: TEXT_SECONDARY }}
                        >
                          {faq.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </ShellCard>
          </div>

          {/* Resources — Terms of Service → Privacy Policy → Cookie Policy → external */}
          <div className="mt-8">
            <ShellEyebrow testId="help-resources-eyebrow">
              {lang === 'es' ? 'RECURSOS' : 'RESOURCES'}
            </ShellEyebrow>
            <ShellCard testId="help-resources-card">
              <UtilityRow as="a" href="/terms" icon={FileText} label={t.terms} divider data-testid="help-terms" />
              <UtilityRow as="a" href="/privacy" icon={Shield} label={t.privacyPolicy} divider data-testid="help-privacy" />
              <UtilityRow as="a" href="/cookies" icon={Cookie} label={t.cookiePolicy} divider data-testid="help-cookies" />
              <UtilityRow as="a" href="/copyright" icon={FileText} label={t.copyrightPolicy} divider data-testid="help-copyright" />
              <UtilityRow
                as="a"
                href="https://arvexisapp.com"
                target="_blank"
                rel="noopener noreferrer"
                icon={ExternalLink}
                iconTone={CHAMPAGNE}
                label={lang === 'es' ? 'Visitar Sitio Web' : 'Visit Website'}
                sub="arvexisapp.com"
                badge={lang === 'es' ? 'EXTERNO' : 'EXTERNAL'}
                data-testid="help-website"
              />
            </ShellCard>
          </div>
        </main>
      </div>
    </AppShellV2>
  );
};

// Insights Screen (Pattern Intelligence)
const InsightsScreen = ({ lang, t }) => {
  const navigate = useNavigate();
  const { isPro, canStartTrial, startTrial, createCheckout } = useSubscription();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('observations'); // observations, symbols, emotions

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const response = await axios.get(`${API}/longitudinal-insights`);
        setInsights(response.data);
      } catch (err) {
        console.error("Failed to fetch insights:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInsights();
  }, []);

  const handleStartTrial = async () => {
    const result = await startTrial();
    if (result.success) {
      const response = await axios.get(`${API}/longitudinal-insights`);
      setInsights(response.data);
    }
  };

  const handleUpgrade = async () => {
    const checkout = await createCheckout();
    if (checkout?.blocked === 'ios') {
      alert(iosPurchaseNotice(lang));
      return;
    }
    if (checkout?.url) {
      window.location.href = checkout.url;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // TRUTH REPAIR (Founder direction, September 2026): the previous
  // generateThemeNarrative / generateEmotionNarrative helpers were engineer-written first-person
  // prose in her voice, keyed by theme name with a real count interpolated. Removed. Anything
  // shown here is now only what is stored for this member. Where evidence is thin, we show
  // nothing rather than invent an observation.
  const themeFact = (theme, count) => {
    if (!theme || !count) return null;
    return lang === 'es'
      ? `«${theme}» aparece en ${count} de tus ${insights.dream_count} sueños registrados.`
      : `"${theme}" appears in ${count} of your ${insights.dream_count} recorded dreams.`;
  };

  const emotionFact = (emotions) => {
    const top = emotions && emotions.length > 0 ? emotions[0] : null;
    if (!top?.emotion || !top?.count) return null;
    return lang === 'es'
      ? `${top.emotion} es el tono emocional que registraste con más frecuencia: ${top.count} de ${insights.dream_count} sueños.`
      : `${top.emotion} is the emotional tone you recorded most often — ${top.count} of ${insights.dream_count} dreams.`;
  };


  // Get teaser message based on language
  const teaserMessage = lang === 'es' ? insights?.teaser_message_es : insights?.teaser_message_en;

  return (
    <AppShellV2 currentPath="/patterns" variant="dark" lang={lang}>
      <div className="min-h-screen" style={{ background: 'var(--bg-insights)' }}>
        {/* P0 Phase 2: Redesigned Header - "Who am I becoming?" */}
        <header 
          className="px-5"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 47px) + 16px)', 
            paddingBottom: '24px'
          }}
        >
          {/* Back navigation row */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              data-testid="patterns-back-btn"
            >
              <ArrowLeft size={20} />
              <span 
                className="text-sm font-medium"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                {lang === 'es' ? 'Atrás' : 'Back'}
              </span>
            </button>
          </div>
          
          {/* P0 Phase 2: New title - observational, invites reflection */}
          <div style={{ marginBottom: '8px' }}>
            <span style={{ 
              fontSize: '11px', 
              fontWeight: 600, 
              color: '#D4A843', 
              letterSpacing: '1.5px',
              textTransform: 'uppercase'
            }}>
              {lang === 'es' ? 'PATRONES' : 'PATTERNS'}
            </span>
          </div>
          <h1 
            style={{ 
              fontSize: '28px', 
              fontWeight: 400, 
              color: '#FFFFFF', 
              letterSpacing: '-0.3px',
              marginBottom: '16px',
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic'
            }}
          >
            {lang === 'es' 
              ? 'Patrones en tus sueños'
              : 'Patterns in your dreams'}
          </h1>
          
          {/* Factual framing — nothing here is interpretation */}
          <p 
            style={{ 
              fontSize: '15px', 
              color: 'rgba(255,255,255,0.65)', 
              lineHeight: 1.7,
              maxWidth: '420px'
            }}
          >
            {lang === 'es' 
              ? 'Estos son recuentos de los temas, símbolos y emociones que registraste con tus sueños. Nada aquí es interpretación.'
              : 'These are counts of the themes, symbols and emotions you recorded with your dreams. Nothing here is interpretation.'}
          </p>
        </header>

        {/* P0 Phase 2: Simplified Tab Bar - Observations, Symbols, Emotions */}
        <div className="tab-bar" style={{ margin: '0 20px 16px' }}>
          <button
            onClick={() => setActiveSection('observations')}
            className={`tab-bar__item ${activeSection === 'observations' ? 'active' : ''}`}
            data-testid="tab-observations"
          >
            {lang === 'es' ? 'Temas' : 'Themes'}
          </button>
          <button
            onClick={() => setActiveSection('symbols')}
            className={`tab-bar__item ${activeSection === 'symbols' ? 'active' : ''}`}
            data-testid="tab-symbols"
          >
            {lang === 'es' ? 'Símbolos' : 'Symbols'}
          </button>
          <button
            onClick={() => setActiveSection('emotions')}
            className={`tab-bar__item ${activeSection === 'emotions' ? 'active' : ''}`}
            data-testid="tab-emotions"
          >
            {lang === 'es' ? 'Emociones' : 'Emotions'}
          </button>
        </div>

        <main style={{ padding: '0 20px', paddingBottom: '100px' }} data-testid="insights-page">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--teal)' }} />
            </div>
          ) : !insights || insights.dream_count < 10 ? (
            /* P0 Phase 2: Redesigned anticipatory state - warmer, more personal */
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
              data-testid="no-insights-state"
            >
              <div 
                style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 24px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(212, 168, 67, 0.12), rgba(212, 168, 67, 0.04))',
                  border: '1px solid rgba(212, 168, 67, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Sparkles style={{ width: '32px', height: '32px', color: 'var(--gold)', opacity: 0.7 }} />
              </div>
              <h2 style={{ 
                fontSize: '22px', 
                fontWeight: 400, 
                color: '#FFFFFF', 
                marginBottom: '16px',
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic'
              }}>
                {lang === 'es' 
                  ? 'Tu historia aún se está escribiendo.'
                  : 'Your story is still being written.'}
              </h2>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.6)', 
                fontSize: '15px', 
                lineHeight: 1.7, 
                maxWidth: '300px', 
                margin: '0 auto 24px', 
                textAlign: 'center' 
              }}>
                {lang === 'es' 
                  ? `Con ${insights?.dream_count || 0} sueños capturados, Arvexis está comenzando a escuchar. Después de 10 reflexiones, podrá compartir lo que ha notado gentilmente.`
                  : `With ${insights?.dream_count || 0} dreams captured, Arvexis is beginning to listen. After 10 reflections, she'll be able to share what she's gently noticed.`}
              </p>
              <button
                onClick={() => navigate('/log')}
                className="btn-gold"
                style={{ width: 'auto', padding: '0 32px' }}
                data-testid="log-dream-from-insights"
              >
                <span className="btn-gold__text">
                  {lang === 'es' ? 'Registra un sueño' : 'Record a Dream'}
                </span>
              </button>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {/* TRUTH REPAIR: the backend still_learning_message is invented first-person prose
                  ("I've noticed some things… I'm watching them take shape"). Per Founder direction
                  we show nothing rather than invent an observation — the factual counts below are
                  the whole story. Backend is unchanged; the message is simply not rendered. */}
              {false && insights.still_learning && (
                <div 
                  className="glass-card-surface"
                  style={{ 
                    padding: '20px 24px',
                    marginBottom: '20px',
                    background: 'linear-gradient(135deg, rgba(212, 168, 67, 0.08), rgba(212, 168, 67, 0.02))',
                    borderLeft: '3px solid rgba(212, 168, 67, 0.4)'
                  }}
                  data-testid="still-learning-message"
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: 'rgba(212, 168, 67, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Sparkles size={18} style={{ color: '#D4A843', opacity: 0.8 }} />
                    </div>
                    <div>
                      <p style={{ 
                        fontSize: '15px', 
                        color: 'rgba(255, 255, 255, 0.8)', 
                        lineHeight: 1.7,
                        fontFamily: 'Georgia, serif'
                      }}>
                        {lang === 'es' 
                          ? `${insights.dream_count} sueños registrados. Todavía nada se repite lo suficiente para contar como un patrón.`
                          : `${insights.dream_count} dreams recorded. Nothing repeats often enough yet to count as a pattern.`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* TRUTH REPAIR: the backend narrative blocks (symbol_narratives /
                  theme_narratives from pattern_narrative_service.py) are evidence-gated but still
                  contain engineer-written first-person interpretation in her voice — e.g.
                  "Something about X seems to matter to your inner world." Unlinked for V1 pending
                  Founder review. pattern_narrative_service.py is UNCHANGED; restoring this block
                  is a one-line revert of the `false &&` below. */}
              {false && !insights.still_learning && insights.narratives && isPro && (
                <>
                  {/* Symbol Narratives */}
                  {insights.narratives.symbol_narratives && insights.narratives.symbol_narratives.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <span style={{ 
                          fontSize: '11px', 
                          fontWeight: 600, 
                          color: '#D4A843', 
                          letterSpacing: '1.5px',
                          textTransform: 'uppercase'
                        }}>
                          {lang === 'es' ? 'NARRATIVAS DE SÍMBOLOS' : 'SYMBOL NARRATIVES'}
                        </span>
                      </div>
                      {insights.narratives.symbol_narratives.map((narrative, idx) => (
                        <div 
                          key={idx}
                          className="glass-card-surface"
                          style={{ 
                            padding: '20px 24px',
                            marginBottom: '12px',
                            borderLeft: `3px solid ${
                              narrative.confidence === 'confident' ? 'rgba(212, 168, 67, 0.8)' :
                              narrative.confidence === 'moderate' ? 'rgba(212, 168, 67, 0.6)' :
                              'rgba(212, 168, 67, 0.4)'
                            }`
                          }}
                          data-testid={`symbol-narrative-${idx}`}
                        >
                          <p style={{ 
                            fontSize: '15px', 
                            color: 'rgba(255, 255, 255, 0.85)', 
                            lineHeight: 1.75,
                            fontFamily: 'Georgia, serif'
                          }}>
                            {lang === 'es' ? narrative.narrative_es : narrative.narrative_en}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Theme Narratives */}
                  {insights.narratives.theme_narratives && insights.narratives.theme_narratives.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <span style={{ 
                          fontSize: '11px', 
                          fontWeight: 600, 
                          color: '#D4A843', 
                          letterSpacing: '1.5px',
                          textTransform: 'uppercase'
                        }}>
                          {lang === 'es' ? 'NARRATIVAS DE TEMAS' : 'THEME NARRATIVES'}
                        </span>
                      </div>
                      {insights.narratives.theme_narratives.map((narrative, idx) => (
                        <div 
                          key={idx}
                          className="glass-card-surface"
                          style={{ 
                            padding: '20px 24px',
                            marginBottom: '12px',
                            borderLeft: `3px solid ${
                              narrative.confidence === 'confident' ? 'rgba(100, 200, 150, 0.8)' :
                              narrative.confidence === 'moderate' ? 'rgba(100, 200, 150, 0.6)' :
                              'rgba(100, 200, 150, 0.4)'
                            }`
                          }}
                          data-testid={`theme-narrative-${idx}`}
                        >
                          <p style={{ 
                            fontSize: '15px', 
                            color: 'rgba(255, 255, 255, 0.85)', 
                            lineHeight: 1.75,
                            fontFamily: 'Georgia, serif'
                          }}>
                            {lang === 'es' ? narrative.narrative_es : narrative.narrative_en}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* PATTERNS TAB — factual theme counts only */}
              {activeSection === 'observations' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Most frequent theme — rendered only when evidence exists */}
                  {insights.themes_with_evidence && insights.themes_with_evidence.length > 0 && (
                  <div 
                    className="glass-card-surface"
                    style={{ 
                      padding: '24px',
                      borderLeft: '3px solid rgba(212, 168, 67, 0.6)'
                    }}
                    data-testid="insights-primary-observation"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                      <SparkleIcon size={14} color="#D4A843" />
                      <span style={{ 
                        fontSize: '11px', 
                        fontWeight: 600, 
                        color: '#D4A843', 
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>
                        {lang === 'es' ? 'TEMA MÁS FRECUENTE' : 'MOST FREQUENT THEME'}
                      </span>
                    </div>
                    
                    {/* Factual only — counts drawn from what this member recorded */}
                    {insights.themes_with_evidence && insights.themes_with_evidence.length > 0 && (
                      <p style={{ 
                        fontSize: '16px', 
                        color: 'rgba(255, 255, 255, 0.85)', 
                        lineHeight: 1.8,
                        fontFamily: 'Georgia, serif'
                      }}>
                        {themeFact(
                          insights.themes_with_evidence[0].theme,
                          insights.themes_with_evidence[0].count
                        )}
                      </p>
                    )}
                    
                    <p style={{ 
                      fontSize: '13px', 
                      color: 'rgba(255, 255, 255, 0.4)', 
                      marginTop: '16px',
                      fontStyle: 'italic'
                    }}>
                      {lang === 'es' 
                        ? `Basado en ${insights.dream_count} sueños`
                        : `Based on ${insights.dream_count} dreams`}
                    </p>
                  </div>
                  )}

                  {/* Secondary Observation - if there's a second theme */}
                  {insights.themes_with_evidence && insights.themes_with_evidence.length > 1 && (
                    <div 
                      className="glass-card-surface"
                      style={{ 
                        padding: '20px 24px',
                        borderLeft: '3px solid rgba(96, 192, 160, 0.5)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <LeafIcon size={14} color="#60C0A0" />
                        <span style={{ 
                          fontSize: '11px', 
                          fontWeight: 600, 
                          color: '#60C0A0', 
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}>
                          {lang === 'es' ? 'TAMBIÉN FRECUENTE' : 'ALSO FREQUENT'}
                        </span>
                      </div>
                      
                      <p style={{ 
                        fontSize: '15px', 
                        color: 'rgba(255, 255, 255, 0.75)', 
                        lineHeight: 1.7,
                        fontFamily: 'Georgia, serif'
                      }}>
                        {themeFact(
                          insights.themes_with_evidence[1].theme,
                          insights.themes_with_evidence[1].count
                        )}
                      </p>
                    </div>
                  )}

                  {/* Emotional tone — factual, and only when the member recorded emotions */}
                  {emotionFact(insights.emotion_trends) && (
                    <div 
                      className="glass-card-surface"
                      style={{ 
                        padding: '20px 24px',
                        borderLeft: '3px solid rgba(160, 128, 240, 0.5)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <Heart size={14} color="#A080F0" />
                        <span style={{ 
                          fontSize: '11px', 
                          fontWeight: 600, 
                          color: '#A080F0', 
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}>
                          {lang === 'es' ? 'TONOS EMOCIONALES' : 'EMOTIONAL TONES'}
                        </span>
                      </div>
                      
                      <p style={{ 
                        fontSize: '15px', 
                        color: 'rgba(255, 255, 255, 0.75)', 
                        lineHeight: 1.7,
                        fontFamily: 'Georgia, serif'
                      }}>
                        {emotionFact(insights.emotion_trends)}
                      </p>
                    </div>
                  )}

                  {/* What this is, and is not */}
                  <div style={{ 
                    padding: '20px 24px',
                    background: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <p style={{ 
                      fontSize: '14px', 
                      color: 'rgba(255, 255, 255, 0.5)', 
                      lineHeight: 1.6,
                      fontStyle: 'italic'
                    }}>
                      {lang === 'es'
                        ? 'Son recuentos, no conclusiones. Lo que significan—si significan algo—lo decides tú.'
                        : 'These are counts, not conclusions. What they mean — if anything — is yours to decide.'}
                    </p>
                  </div>
                </div>
              )}

              {/* P0 Phase 2: SYMBOLS TAB - Recurring symbols with narrative context */}
              {activeSection === 'symbols' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div 
                    className="glass-card-surface"
                    style={{ padding: '24px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                      <Sparkles size={14} color="#D4A843" />
                      <span style={{ 
                        fontSize: '11px', 
                        fontWeight: 600, 
                        color: '#D4A843', 
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>
                        {lang === 'es' ? 'SÍMBOLOS QUE REGRESAN' : 'SYMBOLS THAT RETURN'}
                      </span>
                    </div>
                    
                    <p style={{ 
                      fontSize: '15px', 
                      color: 'rgba(255, 255, 255, 0.65)', 
                      lineHeight: 1.7,
                      marginBottom: '24px'
                    }}>
                      {lang === 'es'
                        ? 'Los símbolos que aparecen una y otra vez a menudo llevan significado. Estos son los que tu mente ha visitado más de una vez.'
                        : 'Symbols that appear again and again often carry meaning. These are the ones your mind has visited more than once.'}
                    </p>
                    
                    {/* Symbol pills with softer, narrative-friendly design */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {/* Phase D.1: Use actual symbols_with_evidence instead of themes */}
                      {(insights.symbols_with_evidence && insights.symbols_with_evidence.length > 0 
                        ? insights.symbols_with_evidence.slice(0, 6).map((symbolData, idx) => ({
                            name: symbolData.symbol,
                            count: symbolData.count
                          }))
                        : [
                            { name: lang === 'es' ? 'Agua' : 'Water', count: 3 },
                            { name: lang === 'es' ? 'Caminos' : 'Paths', count: 2 },
                            { name: lang === 'es' ? 'Luz' : 'Light', count: 2 }
                          ]
                      ).map((symbol, idx) => (
                        <motion.div 
                          key={idx}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: idx * 0.1 }}
                          style={{
                            padding: '10px 18px',
                            background: 'rgba(255, 255, 255, 0.04)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: '24px',
                            fontSize: '14px',
                            color: 'rgba(255, 255, 255, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <span>{symbol.name}</span>
                          <span style={{ 
                            fontSize: '12px', 
                            color: 'rgba(212, 168, 67, 0.7)',
                            fontWeight: 500
                          }}>
                            {symbol.count}×
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Symbol guidance card removed (Patterns Truth Repair): "The meaning of symbols
                      is deeply personal… Trust your own association." was engineer-written prose
                      in her voice, naming a symbol (water) the member may never have recorded. */}
                </div>
              )}

              {/* P0 Phase 2: EMOTIONS TAB - Emotional landscape narrative */}
              {activeSection === 'emotions' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Emotional narrative card */}
                  <div 
                    className="glass-card-surface"
                    style={{ 
                      padding: '24px',
                      borderLeft: '3px solid rgba(160, 128, 240, 0.5)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                      <Heart size={14} color="#A080F0" />
                      <span style={{ 
                        fontSize: '11px', 
                        fontWeight: 600, 
                        color: '#A080F0', 
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>
                        {lang === 'es' ? 'TONOS QUE REGISTRASTE' : 'TONES YOU RECORDED'}
                      </span>
                    </div>
                    
                    {emotionFact(insights.emotion_trends) && (
                    <p style={{ 
                      fontSize: '16px', 
                      color: 'rgba(255, 255, 255, 0.85)', 
                      lineHeight: 1.8,
                      fontFamily: 'Georgia, serif',
                      marginBottom: '20px'
                    }}>
                      {emotionFact(insights.emotion_trends)}
                    </p>
                    )}
                    
                    {/* Emotion distribution - soft, non-clinical */}
                    {insights.emotion_trends && insights.emotion_trends.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        <p style={{ 
                          fontSize: '12px', 
                          color: 'rgba(255, 255, 255, 0.4)', 
                          marginBottom: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {lang === 'es' ? 'Emociones más frecuentes' : 'Most frequent emotions'}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {insights.emotion_trends.slice(0, 4).map((emotion, idx) => (
                            <div 
                              key={idx}
                              style={{
                                padding: '8px 14px',
                                background: idx === 0 ? 'rgba(160, 128, 240, 0.15)' : 'rgba(255, 255, 255, 0.04)',
                                border: `1px solid ${idx === 0 ? 'rgba(160, 128, 240, 0.25)' : 'rgba(255, 255, 255, 0.08)'}`,
                                borderRadius: '20px',
                                fontSize: '13px',
                                color: idx === 0 ? 'rgba(200, 180, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)'
                              }}
                            >
                              {emotion.emotion || 'Unknown'}
                              <span style={{ 
                                marginLeft: '6px', 
                                fontSize: '11px',
                                opacity: 0.6
                              }}>
                                {emotion.count}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Generic first-person guidance card removed (Patterns Truth Repair):
                      "Emotions are neither good nor bad… Listen gently." was engineer-written
                      prose in her voice and grounded in no evidence for this member. */}
                </div>
              )}

              {/* Disclaimer - softer, more personal */}
              <p style={{ 
                fontSize: '12px', 
                color: 'rgba(255, 255, 255, 0.35)', 
                textAlign: 'center',
                marginTop: '32px',
                lineHeight: 1.6,
                fontStyle: 'italic'
              }} data-testid="insights-disclaimer">
                {lang === 'es'
                  ? 'Estos recuentos son para tu propia reflexión. No son diagnósticos ni consejos profesionales.'
                  : 'These counts are for your own reflection. They are not diagnoses or professional advice.'}
              </p>
            </motion.div>
          )}
        </main>
      </div>
    </AppShellV2>
  );
};

// ============================================================================
// ARVEXIS PRESENCE — The entry point to the companion experience
// ============================================================================
// FOUNDER PRINCIPLE:
// "Conversation is not the destination. Arvexis is the destination.
//  Conversation is simply the medium through which the relationship continues."
//
// The user first encounters Arvexis herself — her sphere, her calm atmosphere,
// the feeling that she is already there. From here, they choose how to communicate.
//
// Primary action: Talk with Arvexis (voice)
// Secondary action: Chat with Arvexis (text)
//
// Both lead to the exact same conversation, the exact same Arvexis.
// ============================================================================
const CompanionScreen = ({ lang, t }) => {
  return <ArvexisPresenceScreen lang={lang} />;
};

// OAuth Callback Handler - detects session_id in URL hash
const OAuthCallbackHandler = ({ children, lang, t }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    // Check URL hash for session_id (Emergent Auth format: #session_id=xxx)
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=') && !hasProcessed.current) {
      hasProcessed.current = true;
      setIsProcessing(true);
      
      // Extract session_id from hash
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      
      if (sessionId) {
        // Process the session_id
        loginWithGoogle(sessionId)
          .then(() => {
            // Clear the hash and navigate to home
            window.history.replaceState(null, '', window.location.pathname);
            navigate('/home', { replace: true });
          })
          .catch((err) => {
            console.error('OAuth callback error:', err);
            navigate('/login', { replace: true, state: { error: 'Authentication failed' } });
          })
          .finally(() => {
            setIsProcessing(false);
          });
      }
    }
  }, []);
  
  if (isProcessing) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4A843] border-t-transparent rounded-full animate-spin mb-6" />
        <p className="text-[#E5E7EB] text-lg">
          {lang === 'es' ? 'Completando inicio de sesión...' : 'Completing sign in...'}
        </p>
      </div>
    );
  }
  
  return children;
};

// ============================================================================
// LANGUAGE AUTHORITY (Phase 6B — ruling 9a / R-8)
// ============================================================================
// The member's explicit language choice lives on their account, so it is the same on every device
// and in their export. This reconciles the two in one direction only: a member who has never made
// an explicit choice but has been using a language on this device has that choice *adopted* into
// their account. Nobody's interface is switched underneath them.
//
// Distinct from the language Arvexis *infers* about a member, which stays a contextual inference
// and never becomes this setting.
const LanguageAuthority = ({ lang, setLang }) => {  const { user, isAuthenticated, refreshUser } = useAuth();
  const settled = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !user || settled.current) return;
    settled.current = true;

    const account = user.preferred_language === 'es' ? 'es' : 'en';
    const explicit = Boolean(user.language_set_at);

    if (explicit) {
      // Their account is the authority. The local hint catches up to it.
      if (account !== lang) setLang(account);
      return;
    }

    if (lang !== account) {
      // One-time migration: adopt what they were already using here.
      axios
        .put(`${API}/auth/profile`, {
          preferred_language: lang,
          language_adopted_from_device: true,
        })
        .then(() => refreshUser && refreshUser())
        .catch(() => {});
    }
  }, [isAuthenticated, user, lang, setLang, refreshUser]);

  return null;
};

// ============================================================================
// TIME FORMAT AUTHORITY (Phase 6 Settings ruling)
// ============================================================================
// The account holds the member's time format, so it is the same on every device and survives
// signing out. This carries the account value onto this device once per session. Nothing is
// written back here: the only writer is the member's own choice in Settings.
const TimeFormatAuthority = ({ timeFormat, setTimeFormat }) => {
  const { user, isAuthenticated } = useAuth();
  const settled = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !user || settled.current) return;
    settled.current = true;
    const account = normaliseTimeFormat(user.time_format);
    if (account !== timeFormat) setTimeFormat(account);
  }, [isAuthenticated, user, timeFormat, setTimeFormat]);

  return null;
};

// Main App
function App() {  // Initialize lang from localStorage for persistence across sessions and navigation
  const [lang, setLangInternal] = useState(() => {
    const saved = localStorage.getItem('arvexis_lang');
    return saved === 'es' ? 'es' : 'en';
  });

  // Phase 6 Settings ruling — the member's time format, held beside their language and entirely
  // independent of it. Presentation only: 12h or 24h, 12h when nothing has been stored.
  const [timeFormat, setTimeFormatInternal] = useState(() => getMemberTimeFormat());

  const setTimeFormat = (next) => {
    setTimeFormatInternal(storeMemberTimeFormat(next));
  };
  
  // Welcome flow state
  const [showWelcome, setShowWelcome] = useState(false);
  
  // Theme is always 'dark' for Launch v1
  // Light and System themes will be revisited in a future release
  const theme = 'dark';
  const setTheme = () => {}; // No-op for now
  
  // Wrapper to show one-time language notification about reflections
  const setLang = (newLang) => {
    const hasSeenLangNotice = localStorage.getItem('arvexis_lang_notice_shown');
    const currentLang = localStorage.getItem('arvexis_lang') || 'en';
    
    // Only show notice if changing language and hasn't seen it before
    if (newLang !== currentLang && !hasSeenLangNotice) {
      localStorage.setItem('arvexis_lang_notice_shown', 'true');
      // Use setTimeout to ensure toast shows after state update
      setTimeout(() => {
        const { toast } = require('sonner');
        const message = newLang === 'es' 
          ? 'Nota: Las reflexiones existentes permanecerán en su idioma original. Las nuevas reflexiones se generarán en español.'
          : 'Note: Existing reflections will remain in their original language. New reflections will be generated in English.';
        toast.info(message, { duration: 6000 });
      }, 100);
    }
    setLangInternal(newLang);
  };
  
  // Persist language changes to localStorage
  useEffect(() => {
    localStorage.setItem('arvexis_lang', lang);
  }, [lang]);
  
  // Apply dark theme to document (Launch v1 - Dark Mode only)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);
  
  const t = translations[lang];

  return (
    <div className="App">
      <AuthProvider>
        <SubscriptionProvider>
          <LanguageAuthority lang={lang} setLang={setLang} />
          <TimeFormatAuthority timeFormat={timeFormat} setTimeFormat={setTimeFormat} />
          <BrowserRouter>
            <OAuthCallbackHandler lang={lang} t={t}>
              <Routes>
              {/* Public Landing Page - The new first impression, for people who have not arrived yet */}
              <Route path="/" element={<LandingGate lang={lang} setLang={setLang} />} />
              
              {/* Auth Routes (public) */}
              <Route path="/login" element={<LoginScreen lang={lang} t={t} setLang={setLang} />} />
              <Route path="/register" element={<RegisterScreen lang={lang} t={t} setLang={setLang} />} />
              <Route path="/forgot-password" element={<ForgotPasswordScreen lang={lang} t={t} />} />
              <Route path="/reset-password" element={<ResetPasswordScreen lang={lang} t={t} />} />
              <Route path="/verify-email" element={<EmailVerificationScreen lang={lang} t={t} />} />
              <Route path="/auth/google/callback" element={<GoogleAuthCallback lang={lang} t={t} />} />
              
              {/* Onboarding V1 — Registration → Verify → Sign in → here → /companion */}
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <OnboardingV1 lang={lang} />
                </ProtectedRoute>
              } />
              
              {/* Legacy /select-language onboarding recovery route is RETIRED. */}
              
              <Route path="/change-password" element={
                <ProtectedRoute>
                  <DreamsShell lang={lang} activePath="/settings">
                    <ChangePasswordScreen lang={lang} t={t} />
                  </DreamsShell>
                </ProtectedRoute>
              } />
              
              {/* Legal Pages (public) */}
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/copyright" element={<CopyrightPolicyPage />} />
              {/* One-click marketing unsubscribe. Public by design: the token is the proof. */}
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/about" element={<AboutArvexisPage />} />
              
              {/* Subscription Payment Routes (protected but outside main flow) */}
              <Route path="/subscription/success" element={
                <ProtectedRoute>
                  <SubscriptionSuccessScreen lang={lang} t={t} />
                </ProtectedRoute>
              } />
              <Route path="/subscription/cancel" element={
                <ProtectedRoute>
                  <SubscriptionCancelScreen lang={lang} t={t} />
                </ProtectedRoute>
              } />
              
              {/* Protected Routes */}
              <Route path="/home" element={
                <ProtectedRoute>
                  <HomeScreen lang={lang} setLang={setLang} t={t} />
                </ProtectedRoute>
              } />
              {/* Retired legacy surface — component preserved, not member-facing */}
              <Route path="/todays-reflection" element={<Navigate to="/home" replace />} />
              <Route path="/log" element={
                <ProtectedRoute>
                  <DreamsShell lang={lang}>
                    <LogDreamScreen lang={lang} t={t} />
                  </DreamsShell>
                </ProtectedRoute>
              } />
              <Route path="/reflection/:id" element={
                <ProtectedRoute>
                  <DreamsShell lang={lang}>
                    <PremiumReflectionScreen lang={lang} t={t} />
                  </DreamsShell>
                </ProtectedRoute>
              } />
              {/* New vocabulary route - interpretation */}
              <Route path="/interpretation/:id" element={
                <ProtectedRoute>
                  <DreamsShell lang={lang}>
                    <PremiumReflectionScreen lang={lang} t={t} />
                  </DreamsShell>
                </ProtectedRoute>
              } />
              {/* Legacy route - journal (redirects to dreams) */}
              <Route path="/journal" element={
                <ProtectedRoute>
                  <AppShellV2 currentPath="/dreams" variant="dark" lang={lang}>
                    <DreamsShell lang={lang}>
                      <DreamJournal lang={lang} />
                    </DreamsShell>
                  </AppShellV2>
                </ProtectedRoute>
              } />
              {/* Primary dreams route */}
              <Route path="/dreams" element={
                <ProtectedRoute>
                  <AppShellV2 currentPath="/dreams" variant="dark" lang={lang}>
                    <DreamsShell lang={lang}>
                      <DreamJournal lang={lang} />
                    </DreamsShell>
                  </AppShellV2>
                </ProtectedRoute>
              } />
              {/* Retired standalone Patterns/Insights dashboard. The longitudinal and
                  pattern intelligence stays private and fully operational; Dream Journal
                  keeps its factual recurrence summary computed from /api/dreams. */}
              <Route path="/insights" element={<Navigate to="/home" replace />} />
              <Route path="/patterns" element={<Navigate to="/home" replace />} />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <DreamsShell lang={lang} activePath="/profile">
                    <YouScreen lang={lang} setLang={setLang} t={t} />
                  </DreamsShell>
                </ProtectedRoute>
              } />
              <Route path="/profile/edit" element={
                <ProtectedRoute>
                  <ProfileEditorWrapper lang={lang} />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <SettingsScreen lang={lang} setLang={setLang} t={t} theme={theme} setTheme={setTheme} timeFormat={timeFormat} setTimeFormat={setTimeFormat} />
                </ProtectedRoute>
              } />
              <Route path="/feedback" element={
                <ProtectedRoute>
                  <FeedbackPage />
                </ProtectedRoute>
              } />
              {/* Intelligence review (clone) — approver identities only; enforced server-side. */}
              <Route path="/intelligence-review" element={
                <ProtectedRoute>
                  <IntelligenceReviewPage />
                </ProtectedRoute>
              } />
              {/* Founder Console V1 — authorization is decided server-side, not by this route. */}
              <Route path="/founder-console" element={
                <ProtectedRoute>
                  <FounderConsole />
                </ProtectedRoute>
              } />
              {/* DEV-only, unlinked, server-Owner-gated S1 exact-byte output diagnostic. */}
              <Route path="/dev/s1-output-ab" element={
                <ProtectedRoute>
                  <S1OutputABDiagnostic />
                </ProtectedRoute>
              } />
              <Route path="/payment/success" element={
                <ProtectedRoute>
                  <PaymentReturn lang={lang} outcome="success" />
                </ProtectedRoute>
              } />
              <Route path="/payment/cancel" element={
                <ProtectedRoute>
                  <PaymentReturn lang={lang} outcome="cancel" />
                </ProtectedRoute>
              } />
              <Route path="/help-center" element={
                <ProtectedRoute>
                  <HelpCenterScreen lang={lang} t={t} />
                </ProtectedRoute>
              } />
              <Route path="/about-arvexis" element={
                <ProtectedRoute>
                  <AboutScreen lang={lang} t={t} />
                </ProtectedRoute>
              } />
              <Route path="/subscription" element={
                <ProtectedRoute>
                  <SubscriptionScreen lang={lang} t={t} />
                </ProtectedRoute>
              } />
              {/* Retired legacy surface — the wellness intelligence, its endpoint, its
                  records, context retrieval, export and member record are all unchanged.
                  Only the standalone multi-step check-in UI is no longer member-facing. */}
              <Route path="/wellness" element={<Navigate to="/home" replace />} />
              {/* Retired legacy surface — component preserved, not member-facing */}
              <Route path="/growth-journey" element={<Navigate to="/home" replace />} />
              <Route path="/companion" element={
                <ProtectedRoute>
                  <CompanionScreen lang={lang} t={t} />
                </ProtectedRoute>
              } />
              
              {/* Catch-all redirect to login */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            </OAuthCallbackHandler>
          </BrowserRouter>
        </SubscriptionProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
;
